import { Client, Room } from "colyseus.js";
import { GlobeScene, type ChargingVisualData, type PlayerVisualData } from "./scene/Globe.js";
import { sounds } from "./sound.js";
import { stageCardResolution, abortCeremony, showDispatch, type CardResolvedPayload } from "./reveal.js";
import { confirmDialog, showConnectingOverlay, hideConnectingOverlay, connectingFailed } from "./dialog.js";
import {
  countriesData,
  eventsData,
  cardsData,
  findCountry,
  axialToPixel,
  parseHexKey,
  hexKey,
  getAdjacent,
  type Country,
} from "@pet-wars/shared";

// Lookup carte par nom (fr) — utilisé par l'enrichissement des logs pour
// transformer "X joue Carte Y" en lien hover-preview.
const CARDS_BY_NAME = new Map<string, (typeof cardsData)[number]>();
for (const c of cardsData as any[]) {
  CARDS_BY_NAME.set(String(c.name), c);
}

// ============================================================ //
// Onboarding / aides au jeu                                      //
// ============================================================ //
//
// Trois composantes contrôlées par un toggle global (#help-toggle) :
//   1. Modales par phase (placement/draft/play/end_round/finished) avec
//      opt-out individuel persisté en localStorage.
//   2. Bandeau contextuel en haut de l'écran ("et maintenant : …") qui
//      s'adapte au state courant.
//   3. Tooltip "pourquoi cette carte est désactivée ?" au survol des cartes
//      grisées dans la main (toujours actif, c'est de l'aide à la demande).
//
// helpEnabled = false → AUCUNE aide visuelle ne se déclenche (modales et
// bandeau). Le tooltip cartes désactivées reste actif car c'est utile.
const HELP_ENABLED_KEY = "sd.helpEnabled";
const HELP_MODAL_DISMISSED_PREFIX = "sd.helpModal."; // + phaseId => "1" = jamais réafficher
const HELP_HINT_DISMISSED_KEY = "sd.helpHintDismissed";

let helpEnabled = true;
let hintDismissedThisSession = false;
let lastShownPhaseModal: string | null = null;

function loadHelpSettings() {
  try {
    const raw = localStorage.getItem(HELP_ENABLED_KEY);
    if (raw === "0") helpEnabled = false;
    if (raw === "1") helpEnabled = true;
  } catch {}
}

function setHelpEnabled(value: boolean) {
  helpEnabled = value;
  try { localStorage.setItem(HELP_ENABLED_KEY, value ? "1" : "0"); } catch {}
  const btn = document.getElementById("help-toggle") as HTMLButtonElement | null;
  if (btn) {
    btn.setAttribute("aria-pressed", value ? "true" : "false");
    btn.classList.toggle("is-off", !value);
    // Le pulse d'attraction (lancé par la modale lobby) n'a plus lieu d'être
    // dès que le joueur a interagi avec le bouton — il sait où il est.
    btn.classList.remove("nav-help-toggle-pulse");
    const label = btn.querySelector(".nav-help-toggle-label");
    if (label) label.textContent = value ? "Aides" : "Aides off";
  }
  if (!value) {
    // Off : referme la modale courante et masque le bandeau.
    hidePhaseModal();
    hideHintBanner();
  } else {
    // On : ressuscite le bandeau si on a un état pertinent.
    refreshHintBanner();
  }
}

/**
 * Contenu des modales par phase. Le ton reste diplomatique/satirique pour
 * coller à l'univers — "note de service", "protocole", "Greffier", etc.
 * Chaque entrée est une fabrique qui peut adapter son contenu au state
 * (ex. lobby change si l'option "pays" est activée ou non).
 */
const PHASE_MODAL_CONTENT: Record<string, () => { eyebrow: string; title: string; body: string }> = {
  lobby: () => {
    const useCountries = Boolean((state.room?.state as any)?.useCountries);
    const countryLine = useCountries
      ? `<li><strong>Choisir votre pays</strong> dans la liste (chacun confère un avantage protocolaire).</li>`
      : ``;
    return {
      eyebrow: "OUVERTURE DE LA SÉANCE",
      title: "Le Greffier convoque les Délégués.",
      body: `
        <p>Bienvenue en séance plénière. Vous êtes ici en qualité de <strong>Délégué accrédité</strong>, dépêché par votre nation pour disputer le grand <em>concours d'influence atmosphérique</em>.</p>
        <p>Avant que le Président ne donne le coup d'envoi, vous devez :</p>
        <ul>
          ${countryLine}
          <li><strong>Déclarer votre prêtitude</strong> via le bouton « Je suis prêt à siéger ».</li>
        </ul>
        <p>Quand tous les Délégués auront signé le registre, la séance s'ouvre.</p>
        <p class="phase-modal-note">📝 <em>Note du Secrétariat</em> — ces conseils protocolaires (cette modale, le bandeau d'astuce, les infobulles) peuvent être désactivés à tout moment via le bouton <strong>💡 Aides</strong> dans le bandeau supérieur. Les Délégués chevronnés peuvent s'en passer.</p>
      `,
    };
  },
  placement: () => ({
    eyebrow: "INSTALLATION DES SIÈGES",
    title: "Vous installez votre siège diplomatique.",
    body: `
      <p>Le Secrétariat vous propose plusieurs sites candidats (cases <em>jaunes</em>) pour l'établissement de votre <strong>capitale</strong>. Cliquez sur celle de votre choix.</p>
      <p><strong>Attention</strong> : cette case devient le cœur de votre influence. Si un Délégué adverse y prend l'avantage en densité, <strong>vous serez vassalisé</strong> — et finirez vos jours à signer des communiqués pour son compte.</p>
      <p>Conseil officieux du Greffier : choisissez un site loin des concurrents agressifs, mais proche de cases neutres pour vous étendre rapidement.</p>
    `,
  }),
  draft: () => ({
    eyebrow: "PHASE DE DRAFT",
    title: "Composition de votre dossier diplomatique.",
    body: `
      <p>Le Secrétariat vous remet un <strong>pool de cartes</strong> à parcourir. À chaque tour, vous en gardez <em>une</em>, et le reste passe à votre voisin (sens indiqué en bas à droite : CW / CCW).</p>
      <p>Les six catégories à arbitrer :</p>
      <ul>
        <li><strong>Densité</strong> — renforce votre présence sur une case.</li>
        <li><strong>Extension</strong> — gagne du terrain sur une nouvelle case.</li>
        <li><strong>Sabotage</strong> — attaque un adversaire.</li>
        <li><strong>Défense</strong> — pare une menace ou lève votre statut Exposé.</li>
        <li><strong>Renseignement</strong> — révèle main ou territoire d'un voisin.</li>
        <li><strong>Risque</strong> — gros effet, parfois raté.</li>
      </ul>
      <p>Un événement mondial est révélé en cours de draft : il peut tout chambouler.</p>
    `,
  }),
  play: () => ({
    eyebrow: "PHASE DE JEU",
    title: "Engagez vos manœuvres, le monde regarde.",
    body: `
      <p>Chaque tour, vous engagez <strong>une seule carte</strong> de votre main. Toutes les cartes engagées par l'ensemble des Délégués se révèlent <em>simultanément</em>, puis se résolvent dans un ordre déterminé par le Secrétariat.</p>
      <p>Quelques règles capitales :</p>
      <ul>
        <li>Une carte <strong>grisée</strong> n'est pas jouable maintenant. Survolez-la pour savoir pourquoi.</li>
        <li>Vous gagnez en <strong>contrôle de case</strong> en y ayant la plus forte densité. En cas d'égalité, votre capitale gagne le tie-break.</li>
        <li>En fin de tour, <strong>Veille atmosphérique</strong> et <strong>Érosion d'isolement</strong> drainent ou affaiblissent certaines cases — surveillez le rapport.</li>
      </ul>
      <p>L'objectif final dépend du mode de victoire affiché dans le panneau « Situation » à droite.</p>
    `,
  }),
  end_round: () => ({
    eyebrow: "FIN DE ROUND",
    title: "Le Secrétariat dresse le bilan.",
    body: `
      <p>Le round s'achève. Le Greffier vous remet un <strong>procès-verbal</strong> récapitulatif. Examinez les évolutions de territoire, les vassalisations éventuelles, et préparez-vous au prochain draft.</p>
      <p>Le sens du draft alterne d'un round à l'autre : préparez votre prochain coup en gardant un œil sur vos voisins.</p>
    `,
  }),
  finished: () => ({
    eyebrow: "FIN DE PARTIE",
    title: "Le verdict est rendu.",
    body: `
      <p>La séance est close. Les résultats sont affichés à l'écran. Le Conseil Mondial vous remercie pour votre coopération exemplaire (ou votre fourberie diplomatique).</p>
      <p>Cliquez sur « Retour au lobby » pour préparer une nouvelle séance.</p>
    `,
  }),
};

function hasDismissedPhaseModal(phaseId: string): boolean {
  try {
    return localStorage.getItem(HELP_MODAL_DISMISSED_PREFIX + phaseId) === "1";
  } catch {
    return false;
  }
}

function markPhaseModalDismissed(phaseId: string) {
  try { localStorage.setItem(HELP_MODAL_DISMISSED_PREFIX + phaseId, "1"); } catch {}
}

function showPhaseModal(phaseId: string) {
  if (!helpEnabled) return;
  if (hasDismissedPhaseModal(phaseId)) return;
  // Pas de guard `lastShownPhaseModal === phaseId` : on veut que la modale
  // revienne à chaque NOUVELLE transition vers ce phase (ex. début du draft
  // au round 2). Le déduplicage se fait via `lastObservedPhase` dans
  // onStateChange (seul un changement de valeur déclenche showPhaseModal).
  const factory = PHASE_MODAL_CONTENT[phaseId];
  const content = factory?.();
  if (!content) return;

  const modal = document.getElementById("phase-modal");
  const eyebrow = document.getElementById("phase-modal-eyebrow");
  const title = document.getElementById("phase-modal-title");
  const body = document.getElementById("phase-modal-body");
  const optout = document.getElementById("phase-modal-optout") as HTMLInputElement | null;
  if (!modal || !eyebrow || !title || !body) return;

  eyebrow.textContent = content.eyebrow;
  title.textContent = content.title;
  body.innerHTML = content.body;
  if (optout) optout.checked = false;
  modal.hidden = false;
  modal.classList.add("phase-modal-show");
  lastShownPhaseModal = phaseId;
  // Mémorise pour le close handler
  modal.dataset.phaseId = phaseId;

  // FX : la PREMIÈRE modale (lobby) mentionne explicitement le bouton 💡 Aides
  // dans son texte. On le fait pulser quelques secondes pour que l'œil le
  // repère sans qu'il ait à chercher dans la navbar.
  if (phaseId === "lobby") {
    const helpBtn = document.getElementById("help-toggle");
    if (helpBtn) {
      helpBtn.classList.add("nav-help-toggle-pulse");
      // Auto-stop au bout de ~8s : si le joueur lit la modale longtemps, on
      // ne veut pas que l'animation tourne indéfiniment et finisse par
      // saouler. Le click sur le bouton retire aussi la classe (cf. setHelpEnabled).
      window.setTimeout(() => helpBtn.classList.remove("nav-help-toggle-pulse"), 8000);
    }
  }
}

function hidePhaseModal() {
  const modal = document.getElementById("phase-modal");
  if (!modal) return;
  modal.classList.remove("phase-modal-show");
  modal.hidden = true;
}

/** Configure les event listeners de la modale (1x au boot). */
function initPhaseModal() {
  const closeBtn = document.getElementById("phase-modal-close");
  const backdrop = document.querySelector(".phase-modal-backdrop");
  const optout = document.getElementById("phase-modal-optout") as HTMLInputElement | null;
  const handler = () => {
    const modal = document.getElementById("phase-modal");
    const phaseId = modal?.dataset.phaseId;
    if (optout?.checked && phaseId) markPhaseModalDismissed(phaseId);
    hidePhaseModal();
  };
  closeBtn?.addEventListener("click", handler);
  backdrop?.addEventListener("click", handler);
  // Esc pour fermer
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape") {
      const modal = document.getElementById("phase-modal");
      if (modal && !modal.hidden) handler();
    }
  });
}

/**
 * Bandeau contextuel "et maintenant : …". S'actualise sur chaque changement
 * de state notable. Texte adapté à la phase + au state local du joueur
 * (ciblage en cours, attente d'un autre joueur, etc.).
 */
function refreshHintBanner() {
  const banner = document.getElementById("hint-banner");
  const textEl = document.getElementById("hint-banner-text");
  if (!banner || !textEl) return;
  if (!helpEnabled || hintDismissedThisSession) {
    banner.hidden = true;
    return;
  }
  const hint = computeContextualHint();
  if (!hint) {
    banner.hidden = true;
    return;
  }
  textEl.innerHTML = hint;
  // Le panneau est en position: fixed (CSS), pas besoin de le repositionner
  // par JS — il reste collé en haut-gauche peu importe le scroll.
  banner.hidden = false;
}

function hideHintBanner() {
  const banner = document.getElementById("hint-banner");
  if (banner) banner.hidden = true;
}

/** Calcule l'astuce du moment en fonction du state Colyseus + UI locale. */
function computeContextualHint(): string | null {
  const room = state.room;
  // Pas de bandeau avant connexion : la home affiche déjà tableau de bord +
  // formulaire de connexion, l'astuce n'apporte rien et pollue l'écran.
  if (!room) return null;
  const phase = String((room.state as any).phase ?? "");
  const me = room.state.players.get(state.sessionId ?? "") as any;

  // Ciblage en cours : prioritaire sur la phase
  if (state.pendingHexSelection) {
    const sel = state.pendingHexSelection;
    const remaining = Math.max(0, sel.needed - sel.selected.length);
    return remaining > 0
      ? `Cliquez sur <strong>${remaining} case(s)</strong> de la carte pour cibler <em>${escapeHtml(sel.card.name)}</em>.`
      : `Cibles complètes. Validez votre engagement dans la colonne de droite.`;
  }

  switch (phase) {
    case "lobby": {
      const useCountries = Boolean((room.state as any).useCountries);
      if (me && !me.ready) {
        return useCountries
          ? `Choisissez votre pays puis cliquez <strong>Je suis prêt à siéger</strong>.`
          : `Cliquez <strong>Je suis prêt à siéger</strong> quand vous êtes prêt.`;
      }
      return `En attente des autres Délégués…`;
    }
    case "placement":
      if (me?.capitalCaseId) return `Capitale établie. En attente des autres Délégués…`;
      return `Cliquez sur une case <strong>jaune</strong> pour y établir votre siège.`;
    case "draft": {
      if (me?.draftedThisRound) return `Carte gardée. En attente du prochain tour de draft…`;
      const remainingTotal = Math.max(0, Number((room.state as any).draftTotalPicks ?? 0) - Number((room.state as any).draftPickNumber ?? 0));
      return `Choisissez <strong>une carte</strong> à conserver. Pick ${Number((room.state as any).draftPickNumber ?? 0) + 1} / ${Number((room.state as any).draftTotalPicks ?? 0)} — il vous reste ${remainingTotal} pick(s).`;
    }
    case "play":
      if (me?.committedAction || me?.draftedThisRound) return `Carte engagée. Résolution dès que tout le monde a joué.`;
      if (me?.status === "expose") return `Vous êtes <strong>Exposé</strong> : seules Sabotage / Défense / Renseignement / Risque sont jouables.`;
      if (me?.status === "vassal") return `Vous êtes <strong>vassalisé</strong> de ${escapeHtml(String(room.state.players.get(me.vassalOf)?.name ?? "—"))}. Vos effets sont divisés par deux.`;
      return `Choisissez une carte de votre main, ciblez si nécessaire, puis cliquez <strong>Engager</strong>.`;
    case "end_round":
      return `Le procès-verbal du round est affiché à l'écran.`;
    case "finished":
      return `Partie terminée. Le verdict est affiché.`;
    default:
      return null;
  }
}

function initHintBanner() {
  const dismiss = document.getElementById("hint-banner-dismiss");
  dismiss?.addEventListener("click", () => {
    hintDismissedThisSession = true;
    hideHintBanner();
  });
}

/**
 * Tooltip "pourquoi cette carte est-elle désactivée ?". Branchée en délégation
 * sur la main du joueur. Utilise les classes que poser sur les cartes grisées :
 * data-disabled-reason="..." (lu depuis renderHand).
 */
function initDisabledCardTooltip() {
  // Élément flottant unique
  let tip = document.getElementById("disabled-card-tooltip") as HTMLDivElement | null;
  if (!tip) {
    tip = document.createElement("div");
    tip.id = "disabled-card-tooltip";
    tip.className = "disabled-card-tooltip";
    tip.hidden = true;
    document.body.appendChild(tip);
  }
  // Source courante du tooltip — permet de vérifier en continu qu'elle est
  // toujours en DOM et toujours survolée. Sans cette mémoire, un re-render
  // de la main (déclenché par un click qui engage une carte) détache l'élément
  // hover et `mouseout` ne s'émet jamais → tooltip orphelin flottant à l'écran.
  let currentSource: HTMLElement | null = null;
  const show = (el: HTMLElement, reason: string) => {
    if (!tip) return;
    currentSource = el;
    tip.innerHTML = `<strong>Carte désactivée</strong><br>${escapeHtml(reason)}`;
    tip.hidden = false;
    const r = el.getBoundingClientRect();
    const tt = tip.getBoundingClientRect();
    let left = r.left + r.width / 2 - tt.width / 2;
    let top = r.top - tt.height - 8;
    if (top < 10) top = r.bottom + 8;
    if (left < 10) left = 10;
    if (left + tt.width > window.innerWidth - 10) left = window.innerWidth - tt.width - 10;
    tip.style.left = `${Math.round(left)}px`;
    tip.style.top = `${Math.round(top)}px`;
  };
  const hide = () => {
    if (tip) tip.hidden = true;
    currentSource = null;
  };

  // Délégation : capture mouseover/mouseout au niveau du document, on filtre
  // sur les cartes ayant data-disabled-reason.
  document.addEventListener("mouseover", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const card = target.closest("[data-disabled-reason]") as HTMLElement | null;
    if (!card) return;
    const reason = card.getAttribute("data-disabled-reason") ?? "";
    if (reason) show(card, reason);
  });
  document.addEventListener("mouseout", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const card = target.closest("[data-disabled-reason]") as HTMLElement | null;
    if (!card) return;
    const related = (e as MouseEvent).relatedTarget as HTMLElement | null;
    if (related && card.contains(related)) return;
    hide();
  });

  // Filets de sécurité contre les "tooltip orphelin" :
  //  - mousemove : si la source mémorisée est sortie du DOM (re-render) ou
  //    si la souris n'est plus sur une carte désactivée, on cache.
  //  - mousedown : tout click cache le tooltip (ex. on click sur une carte
  //    pour l'engager → re-render imminent).
  //  - scroll : un scroll peut décaler la source par rapport au tooltip.
  document.addEventListener("mousemove", (e) => {
    if (!currentSource) return;
    if (!document.body.contains(currentSource)) { hide(); return; }
    const under = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null;
    const card = under?.closest("[data-disabled-reason]") as HTMLElement | null;
    if (!card) { hide(); return; }
    if (card !== currentSource) {
      const reason = card.getAttribute("data-disabled-reason") ?? "";
      if (reason) show(card, reason);
    }
  });
  document.addEventListener("mousedown", () => hide());
  window.addEventListener("scroll", () => hide(), { passive: true });
}

function initHelpToggle() {
  const btn = document.getElementById("help-toggle") as HTMLButtonElement | null;
  if (!btn) return;
  loadHelpSettings();
  setHelpEnabled(helpEnabled); // applique l'état initial sur le DOM
  btn.addEventListener("click", () => setHelpEnabled(!helpEnabled));
}

/**
 * Mode focus : cache la navbar globale pour libérer ~55px de hauteur.
 * Toggle via bouton dans la navbar OU touche F. État persisté en
 * sessionStorage (revient à false entre sessions navigateur — évite de
 * coincer un nouvel utilisateur sans navbar).
 */
function setFocusMode(enabled: boolean) {
  document.body.classList.toggle("focus-mode", enabled);
  const btn = document.getElementById("focus-mode-toggle") as HTMLButtonElement | null;
  if (btn) btn.setAttribute("aria-pressed", enabled ? "true" : "false");
  try {
    sessionStorage.setItem("sd.focus-mode", enabled ? "1" : "0");
  } catch { /* sessionStorage indisponible : ignore */ }
}
function initFocusMode() {
  const btn = document.getElementById("focus-mode-toggle") as HTMLButtonElement | null;
  const handle = document.getElementById("focus-mode-handle") as HTMLButtonElement | null;
  btn?.addEventListener("click", () => setFocusMode(!document.body.classList.contains("focus-mode")));

  // Toggle des effets sonores (bouton navbar + touche M)
  const soundBtn = document.getElementById("sound-toggle");
  const soundIcon = document.getElementById("sound-toggle-icon");
  const refreshSoundToggle = () => {
    if (soundIcon) soundIcon.textContent = sounds.muted ? "🔇" : "🔊";
    soundBtn?.setAttribute("aria-pressed", sounds.muted ? "true" : "false");
  };
  refreshSoundToggle();
  soundBtn?.addEventListener("click", () => {
    sounds.toggleMute();
    refreshSoundToggle();
  });
  window.addEventListener("keydown", (e) => {
    if (e.key.toLowerCase() === "m" && !(e.target instanceof HTMLInputElement) && !(e.target instanceof HTMLTextAreaElement)) {
      sounds.toggleMute();
      refreshSoundToggle();
    }
  });
  handle?.addEventListener("click", () => setFocusMode(false));
  // Touche F pour toggle (hors champs de saisie)
  document.addEventListener("keydown", (e) => {
    if (e.key !== "f" && e.key !== "F") return;
    const target = e.target as HTMLElement | null;
    const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
    if (isTyping) return;
    e.preventDefault();
    setFocusMode(!document.body.classList.contains("focus-mode"));
  });
  // Restaure l'état précédent
  try {
    const saved = sessionStorage.getItem("sd.focus-mode");
    if (saved === "1") setFocusMode(true);
  } catch { /* ignore */ }
}

/**
 * URL du serveur Colyseus.
 * On déduit l'hôte de window.location pour que ça marche aussi bien en local
 * (localhost) qu'en LAN (IP machine) qu'en prod (domaine).
 * On peut surcharger via VITE_SERVER_URL si besoin.
 */
const SERVER_URL = (() => {
  // 1) Variable d'env Vite si définie
  const envUrl = (import.meta as any).env?.VITE_SERVER_URL as string | undefined;
  if (envUrl) return envUrl;

  // 2) Sinon, déduit de l'URL de la page
  const loc = window.location;
  const protocol = loc.protocol === "https:" ? "wss:" : "ws:";
  const host = loc.hostname || "localhost";
  const port = 2567; // port du serveur Colyseus
  return `${protocol}//${host}:${port}`;
})();

/**
 * Cartes Densité avec leur nombre d'hex à choisir.
 * Quand l'utilisateur clique une de ces cartes, il entre en mode sélection d'hex
 * et clique N hex de son propre territoire avant que la carte ne soit jouée.
 */
const CARDS_HEX_SELECT: Record<string, number> = {
  le_chaud_qui_suinte: 1,
  compression_strategique: 3,
  cocotte_minute_diplomatique: 1,
  camembert_centenaire: 1,
  aeration_refusee: 2,
  verrou_securite: 1,
  saturation_regionale: 5,
  renferme_officiel: 2,
};

/** Cartes qui demandent un ciblage manuel via la grille des opposants. */
const CARDS_WITH_TARGET = new Set([
  "ah_cetait_pas_un_pet",
  "bruit_pet_bouche",
  "accusation_solide",
  "vent_tactique",
  "quinte_toux",
  "reconnaissance_aerienne",
  "photo_satellite",
  "espion_cantine",
  "cest_pas_moi_cest_le_chien",
  "reflux",
  "sabotage_olfactif",
  "detournement",
]);

/** Palette de seats (alignée sur le mockup design). */
const SEAT_COLORS = [
  "var(--rose)",
  "var(--mint)",
  "var(--lavande)",
  "var(--jaune)",
  "var(--bleu)",
  "#c490a7",
  "#9bc490",
  "#c4a8e6",
];

/** Icônes pour le log (par catégorie / type d'événement). */
function logIcon(line: string): string {
  if (line.startsWith("→") || line.includes("joue ")) return "🃏";
  if (line.includes("vassal")) return "⛓";
  if (line.includes("Exposé") || line.includes("expose")) return "⚠️";
  if (line.includes("hex")) return "🗺️";
  if (line.includes("Wikileaks") || line.includes("Reveal")) return "📰";
  if (line.includes("Reflux") || line.includes("reflux")) return "🔄";
  if (line.includes("sabote") || line.includes("Sabotage")) return "🕯";
  if (line.includes("indépendance") || line.includes("libéré")) return "🗽";
  if (line.includes("événement") || line.includes("Modificateur")) return "📣";
  if (line.includes("Round") || line.includes("Phase")) return "🎬";
  if (line.includes("rejoint") || line.includes("connecté")) return "👤";
  if (line.includes("éclipsé") || line.includes("Reconnexion")) return "📡";
  if (line.includes("Chargement") || line.includes("📜")) return "📜";
  if (line.includes("🌪")) return "🌪";
  if (line.includes("🏆")) return "🏆";
  return "•";
}

interface ClientCard {
  instanceId: string;
  id: string;
  name: string;
  category: string;
  stats?: { density?: number; spread?: number };
  keywords: unknown[];
  effects?: Array<Record<string, any>>;
  flavor: string;
}

interface IntelEntry {
  /** "territory" : hex détaillés visibles ; "density" : juste la densité max */
  type: "territory" | "density";
  turnsLeft: number;
  data: {
    hexes?: Array<{ density: number }>;
    maxDensity?: number;
  };
}

interface RevealedHand {
  /** Cartes vues dans la main de l'adversaire (snapshot au moment de la révélation). */
  cards: Array<{ name: string; category: string }>;
  /** Tours restants avant péremption de l'info. */
  turnsLeft: number;
  /** Round auquel la révélation a eu lieu (pour étiqueter "snapshot du round X"). */
  revealedAtRound: number;
  /** Source de la révélation : "intel" (carte renseignement) ou "leak" (Wikileaks/event public). */
  source: "intel" | "leak";
}

interface ClientState {
  client: Client;
  room: Room | null;
  sessionId: string | null;
  hand: ClientCard[];
  draftPool: ClientCard[];
  draftPickInfo: { pickNumber: number; totalPicks: number };
  pendingTargetCard: ClientCard | null;
  pendingHexSelection: { card: ClientCard; needed: number; selected: string[] } | null;
  /** Renseignements actifs sur les adversaires : key = playerId */
  intel: Map<string, IntelEntry>;
  /** Mains révélées (orthogonal aux intel territoire/densité). key = playerId */
  revealedHands: Map<string, RevealedHand>;
  /**
   * Buffer des bonus pays reçus du serveur, indexés par cardInstanceId.
   * Quand l'animation de résolution arrive sur cette carte, on affiche le
   * bandeau "Pays — bonus appliqué" puis on le vide.
   */
  pendingCountryBonuses: Map<string, Array<{ playerId: string; countryName: string; label: string; kind: "category_buff" | "play_category_bonus" }>>;
  /** instanceId de la carte actuellement affichée par le bandeau recent-action. */
  currentRecentActionInstanceId: string | null;
  /**
   * instanceId de la carte actuellement "verrouillée" comme sélection dans
   * le pool de draft. Null si rien n'est sélectionné. Pilote l'affichage de
   * la sidebar `.draft-selection-panel` et la classe `.is-selected` dans le
   * carrousel. Effacé automatiquement quand le pool change (nouveau round,
   * pick passé au voisin) ou quand le joueur clique Annuler / Valider.
   */
  selectedDraftCardId: string | null;
  /**
   * Cartes déjà draftées par le joueur dans cette session (committed +
   * pick en cours de la passe actuelle). Alimenté par le message serveur
   * `draftedHandUpdate`. Source de la shelf "Vos cartes draftées" — distinct
   * de `state.hand` qui ne porte que les cartes en main pour le jeu
   * (distribuées en fin de draft).
   */
  draftedCards: ClientCard[];
}

const state: ClientState = {
  client: new Client(SERVER_URL),
  room: null,
  sessionId: null,
  hand: [],
  draftPool: [],
  draftPickInfo: { pickNumber: 0, totalPicks: 0 },
  pendingTargetCard: null,
  pendingHexSelection: null,
  intel: new Map<string, IntelEntry>(),
  revealedHands: new Map<string, RevealedHand>(),
  pendingCountryBonuses: new Map(),
  currentRecentActionInstanceId: null,
  selectedDraftCardId: null,
  draftedCards: [],
};

// ============================================================ //
// Composant carrousel générique — état par container ID         //
// ============================================================ //
// Source de vérité de la position (fractionnaire) et du tween en cours,
// indexée par l'ID du container DOM. Permet d'avoir plusieurs carrousels
// indépendants (main, pool de draft, future zone du plateau) avec exactement
// la même mécanique : molette en continu, clavier/clic via tween, hover-focus.
type CarouselRuntime = { position: number; tweenRaf: number | null };
const carouselRuntimes = new Map<string, CarouselRuntime>();
function getCarouselRuntime(containerId: string): CarouselRuntime {
  let rt = carouselRuntimes.get(containerId);
  if (!rt) {
    rt = { position: 0, tweenRaf: null };
    carouselRuntimes.set(containerId, rt);
  }
  return rt;
}

// ============================================================ //
// Reconnect token persistant (sessionStorage)                    //
// ============================================================ //
// Permet de rejoindre la partie en cours après un reload (HMR Vite, F5,
// onglet rouvert dans la même session). On stocke en sessionStorage et non
// localStorage : la durée de vie est limitée à la session du navigateur,
// donc pas de risque de token zombie qui survit des jours.
const RECONNECT_KEY = "sd.reconnect";
const RECONNECT_NAME_KEY = "sd.lastName";
function saveReconnect(roomId: string, token: string | undefined) {
  if (!roomId || !token) return;
  try {
    sessionStorage.setItem(RECONNECT_KEY, JSON.stringify({ roomId, token, ts: Date.now() }));
    // Sauvegarde aussi le nom courant (s'il est rempli) pour pouvoir le
    // re-pré-remplir au prochain chargement si la reconnexion échoue.
    const nameInput = document.getElementById("player-name") as HTMLInputElement | null;
    if (nameInput?.value) sessionStorage.setItem(RECONNECT_NAME_KEY, nameInput.value);
  } catch {}
}
function readSavedReconnect(): { roomId: string; token: string } | null {
  try {
    const raw = sessionStorage.getItem(RECONNECT_KEY);
    if (!raw) return null;
    const obj = JSON.parse(raw) as { roomId?: string; token?: string; ts?: number };
    if (!obj.roomId || !obj.token) return null;
    // Garde-fou : token vieux de plus de 10 min, on n'essaie même pas (la
    // fenêtre de reconnect server-side est de 60s, mais on laisse de la marge
    // pour le cas où l'utilisateur revient vite après être parti).
    if (obj.ts && Date.now() - obj.ts > 10 * 60 * 1000) return null;
    return { roomId: obj.roomId, token: obj.token };
  } catch {
    return null;
  }
}
function clearSavedReconnect() {
  try { sessionStorage.removeItem(RECONNECT_KEY); } catch {}
}

let globe: GlobeScene | null = null;

function ensureGlobe() {
  if (globe) return globe;
  const canvas = document.getElementById("globe-canvas") as HTMLCanvasElement | null;
  if (!canvas) return null;
  // Vérifie que le canvas a une vraie taille (layout terminé)
  const rect = canvas.getBoundingClientRect();
  if (rect.width === 0 || rect.height === 0) {
    // Pas encore prêt, on réessaie au prochain frame
    requestAnimationFrame(() => ensureGlobe());
    return null;
  }
  globe = new GlobeScene(canvas);
  return globe;
}

// ============================================================ //
// Helpers DOM                                                    //
// ============================================================ //

function $<T extends HTMLElement>(id: string): T {
  const el = document.getElementById(id);
  if (!el) throw new Error(`Élément introuvable : #${id}`);
  return el as T;
}

function $opt<T extends HTMLElement>(id: string): T | null {
  return document.getElementById(id) as T | null;
}

function setStatus(text: string, connected: boolean) {
  const el = $("status-value");
  el.textContent = text;
  el.className = `status-pill ${connected ? "status-connected" : "status-disconnected"}`;
}

function showViewport(phase: string) {
  const map: Record<string, string> = {
    lobby: "viewport-lobby",
    placement: "viewport-placement",
    draft: "viewport-draft",
    play: "viewport-play",
    end_round: "viewport-play", // reste sur play pour voir le résultat
    finished: "viewport-gameend",
  };
  const targetId = map[phase] ?? "viewport-lobby";
  document.querySelectorAll(".viewport").forEach((el) => el.classList.remove("active"));
  const target = $opt(targetId);
  if (target) target.classList.add("active");
}

function seatColor(seatIndex: number): string {
  return SEAT_COLORS[seatIndex % SEAT_COLORS.length]!;
}

/**
 * Retourne le seatIndex « effectif » d'un joueur pour le rendu visuel :
 * si le joueur est vassal d'un autre, on prend le seat du MAÎTRE (ses cases
 * apparaissent en couleur du maître, signifiant l'équipe). Sinon son
 * propre seat. Sert au coloriage des cases sur la grille et aux hachures.
 */
function effectiveSeatIndexOf(playerId: string): number {
  const room = state.room;
  if (!room) return 0;
  const p = room.state.players.get(playerId);
  if (!p) return 0;
  if ((p as any).status === "vassal" && (p as any).vassalOf) {
    const master = room.state.players.get((p as any).vassalOf);
    if (master) return master.seatIndex;
  }
  return p.seatIndex;
}

/**
 * Mémorise toutes les entrées de log dans un buffer structuré pour permettre
 * le re-render (grouping par joueur, filtres, etc.). Les éléments DOM existent
 * en parallèle, mais c'est ce buffer qui est la source de vérité.
 */
interface LogEntry {
  id: number;
  message: string;
  ts: { hh: string; mm: string; ss: string };
  actors: string[];  // ids joueurs détectés comme "acteur" (sujet du verbe)
  targets: string[]; // ids joueurs détectés comme "cible" (objet, mentions)
  cardIds: string[]; // ids cartes mentionnées
  isMine: boolean;   // ligne qui me concerne directement
}
let logEntries: LogEntry[] = [];
let logEntryCounter = 0;
let logGroupByPlayer = false;

function appendLog(message: string) {
  const log = $opt<HTMLElement>("log");
  if (!log) return;
  // Timestamp court HH:MM:SS pour l'ordre visuel.
  const now = new Date();
  const ts = {
    hh: String(now.getHours()).padStart(2, "0"),
    mm: String(now.getMinutes()).padStart(2, "0"),
    ss: String(now.getSeconds()).padStart(2, "0"),
  };
  // Enrichissement : extrait acteurs / cibles / cartes, calcule isMine.
  const meta = parseLogMeta(message);
  const entry: LogEntry = {
    id: ++logEntryCounter,
    message,
    ts,
    actors: meta.actors,
    targets: meta.targets,
    cardIds: meta.cardIds,
    isMine: meta.isMine,
  };
  logEntries.push(entry);
  if (logEntries.length > 200) logEntries.shift();

  // Render : si on est en mode "groupé par joueur" on rebuild tout, sinon on
  // append juste cette ligne (cas le plus fréquent, pour ne pas re-rendre 200
  // entrées à chaque message).
  if (logGroupByPlayer) {
    rerenderLog();
  } else {
    log.appendChild(buildLogEntryNode(entry));
    while (log.children.length > 200) log.firstChild?.remove();
    log.scrollTop = log.scrollHeight;
  }

  // Drawer : bump du badge si fermé
  if (isLogDrawerCollapsed()) {
    logDrawerUnread += 1;
    refreshLogDrawerBadge();
  }
}

/**
 * Analyse une ligne de log brute pour en extraire la metadata utile :
 *  - acteurs / cibles via détection des noms de joueurs (room.state.players)
 *  - cartes via détection des noms (CARDS_BY_NAME, dictionnaire complet)
 *  - isMine si je suis acteur OU cible
 *
 * C'est forcément un peu fragile (parsing de string), mais ça transforme un
 * mur de texte en logs réellement lisibles sans toucher au format serveur.
 */
function parseLogMeta(message: string): {
  actors: string[];
  targets: string[];
  cardIds: string[];
  isMine: boolean;
} {
  const room = state.room;
  const actors: string[] = [];
  const targets: string[] = [];
  const cardIds: string[] = [];
  let isMine = false;
  if (!room) return { actors, targets, cardIds, isMine };

  // 1) Détection des cartes via dictionnaire (essaie tous les noms, gardés
  //    triés par longueur décroissante pour éviter les sous-chaînes piégeuses).
  // Note : on cache le tableau trié pour ne pas le recalculer à chaque ligne.
  if (!parseLogMetaCardNamesCache) {
    parseLogMetaCardNamesCache = Array.from(CARDS_BY_NAME.keys()).sort(
      (a, b) => b.length - a.length
    );
  }
  for (const name of parseLogMetaCardNamesCache) {
    if (message.includes(name)) {
      const c = CARDS_BY_NAME.get(name);
      if (c && !cardIds.includes((c as any).id)) cardIds.push(String((c as any).id));
    }
  }

  // 2) Détection des joueurs. On considère qu'un joueur est ACTEUR s'il
  //    apparaît juste avant un verbe d'action ("joue", "pose", "active",
  //    "revient", "déclenche") ; sinon il est cible.
  const players = Array.from(room.state.players.values()) as any[];
  const myId = state.sessionId ?? "";
  const actionVerbs = /\s(joue|pose|active|déclenche|inspecte|observe|libère|obtient|consomme|relance|pousse|cible|attaque|expose)\b/;
  for (const p of players) {
    const name = String((p as any).name ?? "");
    if (!name || !message.includes(name)) continue;
    // Test : nom suivi (peut-être avec quelques chars) par un verbe d'action ?
    const idx = message.indexOf(name);
    const after = message.slice(idx + name.length, idx + name.length + 30);
    if (actionVerbs.test(after)) {
      actors.push(String((p as any).id));
    } else {
      targets.push(String((p as any).id));
    }
    if ((p as any).id === myId) isMine = true;
  }

  return { actors, targets, cardIds, isMine };
}
let parseLogMetaCardNamesCache: string[] | null = null;

/** Construit l'élément DOM d'une entrée de log avec enrichissement (links carte, highlight). */
function buildLogEntryNode(entry: LogEntry): HTMLElement {
  const node = document.createElement("div");
  node.className = "log-entry";
  if (entry.isMine) node.classList.add("is-mine");

  const icon = document.createElement("div");
  icon.className = "log-icon";
  icon.textContent = logIcon(entry.message);

  const text = document.createElement("div");
  text.className = "log-text";

  // Échappe puis enrichit : on remplace les noms de cartes connus par des
  // <span class="log-card-link" data-card-id="..."> pour activer la preview.
  let safe = entry.message.replace(/</g, "&lt;").replace(/>/g, "&gt;");
  // Itère sur les cards détectées pour wrap leur nom.
  for (const cardId of entry.cardIds) {
    const card = (cardsData as any[]).find((c) => c.id === cardId);
    if (!card) continue;
    // Match le nom exact (échapper les regex specials).
    const escapedName = String(card.name).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const re = new RegExp(`(${escapedName})`, "g");
    safe = safe.replace(
      re,
      `<span class="log-card-link" data-card-id="${cardId}">$1</span>`
    );
  }

  text.innerHTML = `<span class="log-ts">${entry.ts.hh}:${entry.ts.mm}:${entry.ts.ss}</span>${safe}`;
  node.appendChild(icon);
  node.appendChild(text);
  return node;
}

/** Re-render complet du log (utilisé quand on switche le mode groupé). */
function rerenderLog() {
  const log = $opt<HTMLElement>("log");
  if (!log) return;
  log.innerHTML = "";

  if (!logGroupByPlayer) {
    // Mode séquentiel : juste tout dans l'ordre.
    for (const e of logEntries) log.appendChild(buildLogEntryNode(e));
    log.scrollTop = log.scrollHeight;
    return;
  }

  // Mode groupé : on regroupe par "acteur principal" (premier acteur détecté).
  // Les lignes sans acteur connu vont dans une catégorie "Système".
  const room = state.room;
  const groups = new Map<string, LogEntry[]>();
  const groupOrder: string[] = [];
  for (const e of logEntries) {
    const key = e.actors[0] ?? "__system__";
    if (!groups.has(key)) {
      groups.set(key, []);
      groupOrder.push(key);
    }
    groups.get(key)!.push(e);
  }
  for (const key of groupOrder) {
    const header = document.createElement("div");
    header.className = "log-group-header";
    if (key === "__system__") {
      header.textContent = "— Système / phases / événements —";
    } else {
      const player = room?.state.players.get(key) as any;
      const name = player ? String(player.name ?? "—") : "Joueur inconnu";
      const seat = player?.seatIndex ?? 0;
      header.innerHTML = `<span class="log-group-dot" style="background:${seatColor(seat)}"></span>${escapeHtml(name)} <span class="log-group-count">${groups.get(key)!.length} entrée(s)</span>`;
      if (key === state.sessionId) header.classList.add("is-mine");
    }
    log.appendChild(header);
    for (const e of groups.get(key)!) {
      log.appendChild(buildLogEntryNode(e));
    }
  }
  log.scrollTop = log.scrollHeight;
}

/** Toggle du mode groupé. Persist en localStorage pour la session. */
function setLogGroupByPlayer(value: boolean) {
  logGroupByPlayer = value;
  try { window.localStorage.setItem("sd.logGroupByPlayer", value ? "1" : "0"); } catch {}
  const btn = $opt<HTMLButtonElement>("log-drawer-group");
  if (btn) {
    btn.setAttribute("aria-pressed", value ? "true" : "false");
    btn.textContent = value ? "grouper ✓" : "grouper";
  }
  rerenderLog();
}

// ============================================================ //
// Log drawer (procès-verbal repliable)                           //
// ============================================================ //

let logDrawerUnread = 0;
let logDrawerAutoHideTimer: number | null = null;
const LOG_DRAWER_AUTOHIDE_MS = 8000;

function isLogDrawerCollapsed(): boolean {
  const d = $opt<HTMLElement>("log-drawer");
  return !d || d.classList.contains("collapsed");
}

function refreshLogDrawerBadge() {
  const badge = $opt<HTMLElement>("log-drawer-badge");
  if (!badge) return;
  if (logDrawerUnread > 0) {
    badge.hidden = false;
    badge.textContent = logDrawerUnread > 99 ? "99+" : String(logDrawerUnread);
  } else {
    badge.hidden = true;
    badge.textContent = "0";
  }
}

function clearLogDrawerAutoHide() {
  if (logDrawerAutoHideTimer !== null) {
    window.clearTimeout(logDrawerAutoHideTimer);
    logDrawerAutoHideTimer = null;
  }
}

function scheduleLogDrawerAutoHide() {
  clearLogDrawerAutoHide();
  const pinned = ($opt<HTMLInputElement>("log-drawer-pin"))?.checked;
  if (pinned) return;
  logDrawerAutoHideTimer = window.setTimeout(() => {
    closeLogDrawer();
  }, LOG_DRAWER_AUTOHIDE_MS);
}

function openLogDrawer() {
  const d = $opt<HTMLElement>("log-drawer");
  const toggle = $opt<HTMLButtonElement>("log-drawer-toggle");
  if (!d) return;
  d.classList.remove("collapsed");
  if (toggle) toggle.setAttribute("aria-expanded", "true");
  logDrawerUnread = 0;
  refreshLogDrawerBadge();
  // Scrolle au bas pour voir le dernier message
  const log = $opt<HTMLElement>("log");
  if (log) log.scrollTop = log.scrollHeight;
  scheduleLogDrawerAutoHide();
  try { localStorage.setItem("sd:logDrawerOpen", "1"); } catch {}
}

function closeLogDrawer() {
  const d = $opt<HTMLElement>("log-drawer");
  const toggle = $opt<HTMLButtonElement>("log-drawer-toggle");
  if (!d) return;
  d.classList.add("collapsed");
  if (toggle) toggle.setAttribute("aria-expanded", "false");
  clearLogDrawerAutoHide();
  try { localStorage.setItem("sd:logDrawerOpen", "0"); } catch {}
}

function toggleLogDrawer() {
  if (isLogDrawerCollapsed()) openLogDrawer();
  else closeLogDrawer();
}

// ============================================================ //
// Rules drawer — panneau gauche avec les règles du jeu
// ============================================================ //

function isRulesDrawerCollapsed(): boolean {
  return $opt<HTMLElement>("rules-drawer")?.classList.contains("collapsed") ?? true;
}

function openRulesDrawer() {
  const d = $opt<HTMLElement>("rules-drawer");
  const toggle = $opt<HTMLButtonElement>("rules-drawer-toggle");
  if (!d) return;
  d.classList.remove("collapsed");
  if (toggle) toggle.setAttribute("aria-expanded", "true");
}

function closeRulesDrawer() {
  const d = $opt<HTMLElement>("rules-drawer");
  const toggle = $opt<HTMLButtonElement>("rules-drawer-toggle");
  if (!d) return;
  d.classList.add("collapsed");
  if (toggle) toggle.setAttribute("aria-expanded", "false");
}

function toggleRulesDrawer() {
  if (isRulesDrawerCollapsed()) openRulesDrawer();
  else closeRulesDrawer();
}

function initRulesDrawer() {
  const toggle = $opt<HTMLButtonElement>("rules-drawer-toggle");
  const closeBtn = $opt<HTMLButtonElement>("rules-drawer-close");
  toggle?.addEventListener("click", () => toggleRulesDrawer());
  closeBtn?.addEventListener("click", () => closeRulesDrawer());

  // Raccourcis clavier : R pour toggle, Échap pour fermer
  document.addEventListener("keydown", (e) => {
    // Ignore si l'utilisateur tape dans un input/textarea
    const target = e.target as HTMLElement | null;
    const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
    if (isTyping) return;
    if (e.key === "r" || e.key === "R") {
      e.preventDefault();
      toggleRulesDrawer();
    } else if (e.key === "Escape" && !isRulesDrawerCollapsed()) {
      closeRulesDrawer();
    }
  });
}

/** ID du pays sélectionné (si useCountries actif). Vide sinon. */
let selectedCountryId: string = "";

/**
 * Renvoie les IDs des joueurs visibles "gratuitement" pour ME (Fédération Nordique).
 * Vide si l'option useCountries est off ou si mon pays n'a pas see_seat_neighbor.
 */
function getFreeIntelTargetsForMe(): Set<string> {
  const empty = new Set<string>();
  const room = state.room;
  if (!room || !state.sessionId) return empty;
  if (!(room.state as any).useCountries) return empty;
  const me = room.state.players.get(state.sessionId);
  if (!me) return empty;
  const myCountry = findCountry((me as any).countryId);
  if (myCountry?.modifier?.type !== "see_seat_neighbor") return empty;

  const players = playersSnapshot().filter((p) => p.status !== "eliminated");
  if (players.length < 2) return empty;
  const idx = players.findIndex((p) => p.id === state.sessionId);
  if (idx < 0) return empty;
  const left = players[(idx - 1 + players.length) % players.length]!;
  const right = players[(idx + 1) % players.length]!;
  const out = new Set<string>();
  if (left.id !== state.sessionId) out.add(left.id);
  if (right.id !== state.sessionId) out.add(right.id);
  return out;
}

/**
 * Détermine la préposition complète pour annoncer un Délégué :
 * « Délégué de la République », « Délégué du Royaume », « Délégué des Émirats », etc.
 * Renvoie la phrase complète qui suit "Délégué " (avec espace final).
 */
function prefixForName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.startsWith("république") || lower.startsWith("confédération") || lower.startsWith("principauté") || lower.startsWith("fédération") || lower.startsWith("sérénissime")) return "de la ";
  if (lower.startsWith("royaume") || lower.startsWith("commonwealth") || lower.startsWith("khanat")) return "du ";
  if (lower.startsWith("empire")) return "de l'";
  if (lower.startsWith("émirats") || lower.startsWith("états")) return "des ";
  return "de ";
}

/**
 * Click handlers pour les chips de pays dans le briefing du lobby.
 * Mode 1 (useCountries OFF) : cliquer un chip pré-remplit le nom du Délégué.
 * Mode 2 (useCountries ON)  : cliquer un chip sélectionne le pays (exclusif) et le pré-remplit aussi.
 */
function initCountryChips() {
  const container = $opt<HTMLElement>("country-suggestions");
  if (!container) return;

  // Génère les chips depuis countriesData
  container.innerHTML = "";
  for (const c of countriesData) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "country-chip";
    btn.setAttribute("data-country", `Délégué ${prefixForName(c.name)}${c.name}`);
    btn.setAttribute("data-country-id", c.id);
    btn.setAttribute("data-modifier-label", c.modifierLabel);
    btn.setAttribute("data-motto", c.motto);
    btn.textContent = c.name;
    if (c.modifier === null) {
      btn.classList.add("v2");
      btn.title = "Modificateur prévu en v2 — fonctionnel mais sans effet pour l'instant.";
    } else {
      btn.title = c.modifierLabel;
    }
    container.appendChild(btn);
  }

  container.addEventListener("click", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const chip = target.closest(".country-chip") as HTMLElement | null;
    if (!chip) return;
    const value = chip.getAttribute("data-country");
    const id = chip.getAttribute("data-country-id");
    if (!value) return;

    const input = $opt<HTMLInputElement>("player-name");
    if (input) {
      input.value = value;
      input.classList.add("just-filled");
      window.setTimeout(() => input.classList.remove("just-filled"), 500);
    }

    const useCountriesEl = $opt<HTMLInputElement>("setting-use-countries");
    if (useCountriesEl?.checked && id) {
      // Sélection exclusive
      selectedCountryId = id;
      container.querySelectorAll(".country-chip").forEach((c) => c.classList.remove("selected"));
      chip.classList.add("selected");
      renderCountryHint();
    }

    input?.focus();
  });

  // Le toggle "Activer les pays" reste dans les paramètres de séance (form).
  // Le choix de nation se fait dans le salon d'attente après création/join,
  // pas dans le briefing initial. La liste du briefing reste donc indicative :
  // un clic ne fait que pré-remplir le nom du joueur, pas sélectionner.
  renderCountryHint();
}

/**
 * Affiche le modificateur du pays sélectionné sous la liste.
 */
function renderCountryHint() {
  const hint = $opt<HTMLElement>("country-hint");
  const useCountriesEl = $opt<HTMLInputElement>("setting-use-countries");
  if (!hint) return;
  if (!useCountriesEl?.checked) {
    hint.hidden = true;
    return;
  }
  if (!selectedCountryId) {
    hint.hidden = false;
    hint.innerHTML = `<em>Choisissez une nation pour bénéficier de son modificateur passif.</em>`;
    return;
  }
  // import lazy à éviter — on lit le data depuis l'attribut HTML stocké dans le chip
  const container = $opt<HTMLElement>("country-suggestions");
  const chip = container?.querySelector(`.country-chip[data-country-id="${selectedCountryId}"]`) as HTMLElement | null;
  const modLabel = chip?.getAttribute("data-modifier-label") ?? "";
  const motto = chip?.getAttribute("data-motto") ?? "";
  hint.hidden = false;
  hint.innerHTML = `
    <strong>${escapeHtml(chip?.textContent ?? selectedCountryId)}</strong>
    ${motto ? `<span class="country-hint-motto">« ${escapeHtml(motto)} »</span>` : ""}
    <span class="country-hint-mod">${escapeHtml(modLabel)}</span>
  `;
}

function initLogDrawer() {
  const toggle = $opt<HTMLButtonElement>("log-drawer-toggle");
  const closeBtn = $opt<HTMLButtonElement>("log-drawer-close");
  const clearBtn = $opt<HTMLButtonElement>("log-drawer-clear");
  const copyBtn = $opt<HTMLButtonElement>("log-drawer-copy");
  const pin = $opt<HTMLInputElement>("log-drawer-pin");
  const panel = $opt<HTMLElement>("log-drawer-panel");

  toggle?.addEventListener("click", () => toggleLogDrawer());
  closeBtn?.addEventListener("click", () => closeLogDrawer());
  clearBtn?.addEventListener("click", () => {
    const log = $opt<HTMLElement>("log");
    if (log) log.innerHTML = "";
    // Vide aussi le buffer structuré : sinon un re-render (grouper / toggle)
    // ressusciterait les entrées.
    logEntries = [];
  });

  // Bouton "grouper" : bascule l'affichage par joueur acteur
  const groupBtn = $opt<HTMLButtonElement>("log-drawer-group");
  let groupPersisted = false;
  try { groupPersisted = localStorage.getItem("sd.logGroupByPlayer") === "1"; } catch {}
  logGroupByPlayer = groupPersisted;
  if (groupBtn) {
    groupBtn.setAttribute("aria-pressed", logGroupByPlayer ? "true" : "false");
    groupBtn.textContent = logGroupByPlayer ? "grouper ✓" : "grouper";
    groupBtn.addEventListener("click", () => setLogGroupByPlayer(!logGroupByPlayer));
  }

  // Tooltip de carte au survol : on délègue les events sur le drawer pour
  // capter le hover sur les .log-card-link générés dynamiquement.
  initLogCardTooltip();

  // Bouton "copier" : sérialise toutes les entrées du log en texte brut et
  // copie dans le presse-papiers. Utile pour partager les logs en debug
  // (joindre à un rapport de bug, coller dans un message, etc.).
  copyBtn?.addEventListener("click", async () => {
    const log = $opt<HTMLElement>("log");
    if (!log) return;
    // Récupère le texte de chaque entrée, ligne par ligne
    const lines: string[] = [];
    log.querySelectorAll<HTMLElement>(".log-entry").forEach((entry) => {
      // .log-text contient timestamp + message, on prend juste le textContent
      const text = entry.querySelector<HTMLElement>(".log-text")?.innerText ?? entry.innerText;
      lines.push(text.trim());
    });
    const payload = lines.join("\n");
    try {
      await navigator.clipboard.writeText(payload);
      const original = copyBtn.textContent;
      copyBtn.textContent = "copié ✓";
      copyBtn.disabled = true;
      window.setTimeout(() => {
        copyBtn.textContent = original;
        copyBtn.disabled = false;
      }, 1400);
    } catch (err) {
      console.warn("[sd] copie procès-verbal échouée", err);
      copyBtn.textContent = "échec";
      window.setTimeout(() => { copyBtn.textContent = "copier"; }, 1400);
    }
  });

  // Pin : empêche l'auto-hide et persiste le choix
  let pinPersisted = false;
  try { pinPersisted = localStorage.getItem("sd:logDrawerPinned") === "1"; } catch {}
  if (pin) {
    pin.checked = pinPersisted;
    pin.addEventListener("change", () => {
      try { localStorage.setItem("sd:logDrawerPinned", pin.checked ? "1" : "0"); } catch {}
      if (pin.checked) clearLogDrawerAutoHide();
      else scheduleLogDrawerAutoHide();
    });
  }

  // Reset du timer d'auto-hide à chaque interaction avec le panneau
  panel?.addEventListener("mousemove", () => scheduleLogDrawerAutoHide());
  panel?.addEventListener("scroll", () => scheduleLogDrawerAutoHide(), { passive: true });
  panel?.addEventListener("click", () => scheduleLogDrawerAutoHide());

  // Ferme avec Échap
  document.addEventListener("keydown", (e) => {
    if (e.key === "Escape" && !isLogDrawerCollapsed()) closeLogDrawer();
  });

  // État initial : fermé par défaut, sauf si l'utilisateur l'avait laissé ouvert
  try {
    if (localStorage.getItem("sd:logDrawerOpen") === "1") openLogDrawer();
  } catch {}
  refreshLogDrawerBadge();
}

/**
 * Tooltip de carte au survol d'un .log-card-link.
 * Délégation des events depuis le drawer pour capter les nodes créés
 * dynamiquement par buildLogEntryNode().
 */
function initLogCardTooltip() {
  const drawer = $opt<HTMLElement>("log-drawer");
  if (!drawer) return;

  // Conteneur tooltip unique attaché au body — repositionné sur chaque hover.
  let tooltip = document.getElementById("log-card-tooltip") as HTMLDivElement | null;
  if (!tooltip) {
    tooltip = document.createElement("div");
    tooltip.id = "log-card-tooltip";
    tooltip.className = "log-card-tooltip";
    tooltip.hidden = true;
    document.body.appendChild(tooltip);
  }

  let hideTimer: number | null = null;
  const show = (el: HTMLElement, cardId: string) => {
    if (hideTimer != null) { window.clearTimeout(hideTimer); hideTimer = null; }
    const card = (cardsData as any[]).find((c) => c.id === cardId);
    if (!card || !tooltip) return;
    // Construit le contenu : nom + catégorie + stats + effets résumés + flavor
    const stats = card.stats ?? {};
    const statsStr = stats.density !== undefined && stats.spread !== undefined
      ? `D${stats.density} · E${stats.spread}`
      : "";
    const effectsHtml = (card.effects ?? [])
      .map((e: any) => {
        const desc = describeEffect(e, card.category);
        return desc ? `<li>${escapeHtml(desc)}</li>` : "";
      })
      .join("");
    tooltip.innerHTML = `
      <div class="lct-cat cat-${escapeHtml(String(card.category))}">${escapeHtml(categoryLabel(String(card.category)))}</div>
      <div class="lct-name">${escapeHtml(String(card.name))}</div>
      ${statsStr ? `<div class="lct-stats">${statsStr}</div>` : ""}
      ${effectsHtml ? `<ul class="lct-effects">${effectsHtml}</ul>` : ""}
      ${card.flavor ? `<div class="lct-flavor">« ${escapeHtml(String(card.flavor))} »</div>` : ""}
    `;
    tooltip.hidden = false;

    // Positionnement : à gauche du .log-card-link (le drawer est à droite),
    // calé verticalement avec un cran de marge.
    const rect = el.getBoundingClientRect();
    const ttRect = tooltip.getBoundingClientRect();
    const margin = 12;
    let left = rect.left - ttRect.width - margin;
    if (left < 10) left = rect.right + margin; // bascule à droite si pas la place
    let top = rect.top;
    if (top + ttRect.height > window.innerHeight - 10) {
      top = Math.max(10, window.innerHeight - ttRect.height - 10);
    }
    tooltip.style.left = `${Math.round(left)}px`;
    tooltip.style.top = `${Math.round(top)}px`;
  };
  const hide = () => {
    if (!tooltip) return;
    if (hideTimer != null) window.clearTimeout(hideTimer);
    hideTimer = window.setTimeout(() => {
      if (tooltip) tooltip.hidden = true;
    }, 120);
  };

  drawer.addEventListener("mouseover", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const link = target.closest(".log-card-link") as HTMLElement | null;
    if (!link) return;
    const cardId = link.getAttribute("data-card-id");
    if (cardId) show(link, cardId);
  });
  drawer.addEventListener("mouseout", (e) => {
    const target = e.target as HTMLElement | null;
    if (!target) return;
    const link = target.closest(".log-card-link") as HTMLElement | null;
    if (!link) return;
    // Vérifie si la souris quitte vraiment l'élément (pas un enfant)
    const related = (e as MouseEvent).relatedTarget as HTMLElement | null;
    if (related && link.contains(related)) return;
    hide();
  });
}

// ============================================================ //
// Render: Players list (lobby)                                   //
// ============================================================ //

interface PlayerSnap {
  id: string;
  name: string;
  status: string;
  vassalOf: string;
  territorySize: number;
  maxDensity: number;
  handSize: number;
  draftPoolSize: number;
  draftedCount: number;
  draftedThisRound: boolean;
  ready: boolean;
  committedAction: boolean;
  isDisconnected: boolean;
  isBot: boolean;
  seatIndex: number;
  countryId: string;
  countryName: string;
}

function playersSnapshot(): PlayerSnap[] {
  const room = state.room;
  if (!room) return [];
  const players: PlayerSnap[] = [];
  room.state.players.forEach((p: any) => {
    players.push({
      id: p.id,
      name: p.name,
      status: p.status,
      vassalOf: p.vassalOf ?? "",
      territorySize: p.territorySize ?? 0,
      maxDensity: p.maxDensity ?? 0,
      handSize: p.handSize ?? 0,
      draftPoolSize: p.draftPoolSize ?? 0,
      draftedCount: p.draftedCount ?? 0,
      draftedThisRound: Boolean(p.draftedThisRound),
      ready: Boolean(p.ready),
      committedAction: Boolean(p.committedAction),
      isDisconnected: Boolean(p.isDisconnected),
      isBot: Boolean(p.isBot),
      seatIndex: p.seatIndex ?? 0,
      countryId: p.countryId ?? "",
      countryName: p.countryName ?? "",
    });
  });
  players.sort((a, b) => a.seatIndex - b.seatIndex);
  return players;
}

function renderPlayersLobby() {
  const list = $<HTMLUListElement>("players-list");
  const room = state.room;
  list.innerHTML = "";

  if (!room) {
    const li = document.createElement("li");
    li.className = "player-item";
    li.style.opacity = "0.5";
    li.style.fontStyle = "italic";
    li.innerHTML = `<div class="player-dot" style="background: var(--ink-mute)"></div><span class="player-name">En attente de connexion...</span>`;
    list.appendChild(li);
    return;
  }

  const players = playersSnapshot();
  for (const p of players) {
    const li = document.createElement("li");
    li.className = "player-item";
    if (p.status === "expose") li.classList.add("player-expose");
    if (p.status === "vassal") li.classList.add("player-vassal");
    if (p.status === "eliminated") li.classList.add("player-eliminated");
    if (p.isDisconnected) li.classList.add("player-disconnected");

    const dot = document.createElement("div");
    dot.className = "player-dot";
    dot.style.background = seatColor(p.seatIndex);

    const nameBlock = document.createElement("div");
    nameBlock.className = "player-name-block";
    const name = document.createElement("span");
    name.className = "player-name";
    let displayName = p.name;
    if (p.isBot) displayName = `🤖 ${displayName}`;
    if (p.id === state.sessionId) displayName += " (vous)";
    name.textContent = displayName;
    nameBlock.appendChild(name);
    if (p.countryName) {
      const flag = document.createElement("span");
      flag.className = "player-country";
      flag.textContent = `🏛 ${p.countryName}`;
      nameBlock.appendChild(flag);
    }

    const status = document.createElement("span");
    status.className = "player-status";
    if (p.isBot) {
      status.classList.add("ready");
      status.textContent = "Bot prêt";
    } else if (p.ready) {
      status.classList.add("ready");
      status.textContent = "Prêt";
    } else {
      status.textContent = "En attente";
    }

    li.appendChild(dot);
    li.appendChild(nameBlock);
    li.appendChild(status);
    list.appendChild(li);
  }

  renderLobbyCountryPicker();
  updateReadyButtonAvailability();
  updateAddBotButtonAvailability();
}

/** Désactive le bouton "Ajouter un bot" quand on a atteint la limite (8 joueurs). */
function updateAddBotButtonAvailability() {
  const btn = $opt<HTMLButtonElement>("add-bot-btn");
  if (!btn) return;
  const room = state.room;
  if (!room) {
    btn.disabled = true;
    return;
  }
  const playerCount = room.state.players.size ?? 0;
  // MAX_PLAYERS importé depuis @pet-wars/shared. Hardcoded à 8 ici pour
  // éviter d'ajouter un import dans le top du fichier ; si le constant
  // change côté shared, le serveur restera la source de vérité (il rejette
  // les addBot au-delà).
  const MAX = 8;
  if (playerCount >= MAX) {
    btn.disabled = true;
    btn.title = `Limite atteinte : ${MAX} délégués maximum par séance.`;
  } else {
    btn.disabled = false;
    btn.title = "";
  }
}

/**
 * Affiche le panneau de sélection de pays dans le salon d'attente quand
 * useCountries est activé. Les pays "verrouillés" par un joueur READY sont
 * grisés et inutilisables. Premier prêt = premier servi.
 */
function renderLobbyCountryPicker() {
  const section = $opt<HTMLElement>("country-lobby-picker");
  const grid = $opt<HTMLElement>("country-lobby-grid");
  const statusEl = $opt<HTMLElement>("country-lobby-status");
  const room = state.room;
  if (!section || !grid || !statusEl) return;
  if (!room || !(room.state as any).useCountries) {
    section.hidden = true;
    return;
  }
  section.hidden = false;

  const players = playersSnapshot();
  const me = players.find((p) => p.id === state.sessionId);
  const myReady = Boolean(me?.ready);

  // Carte des pays verrouillés (pris par un joueur READY) avec le nom du verrouilleur.
  const lockedBy = new Map<string, string>(); // countryId → playerName
  for (const p of players) {
    if (p.ready && p.countryId) lockedBy.set(p.countryId, p.name);
  }
  // Pays choisis (mais pas encore verrouillés) par chaque joueur non-ready,
  // pour qu'on les indique aussi visuellement (en cours de négociation).
  const pendingBy = new Map<string, string>();
  for (const p of players) {
    if (!p.ready && p.countryId && p.id !== state.sessionId) pendingBy.set(p.countryId, p.name);
  }

  grid.innerHTML = "";
  for (const c of countriesData) {
    const btn = document.createElement("button");
    btn.type = "button";
    btn.className = "country-lobby-chip";
    btn.setAttribute("data-country-id", c.id);
    btn.title = `${c.name} — ${c.modifierLabel}`;
    const isMine = me?.countryId === c.id;
    const lockerName = lockedBy.get(c.id);
    const pendingName = pendingBy.get(c.id);
    const isLocked = Boolean(lockerName);
    if (isMine) btn.classList.add("is-mine");
    if (isLocked) btn.classList.add("is-locked");
    if (pendingName && !isMine && !isLocked) btn.classList.add("is-pending");
    if (myReady && !isMine) btn.disabled = true; // une fois prêt, on ne change plus
    if (isLocked && !isMine) btn.disabled = true;

    let badge = "";
    if (isMine) badge = `<span class="lobby-chip-badge">🏛 votre nation</span>`;
    else if (isLocked) badge = `<span class="lobby-chip-badge locked">🔒 ${escapeHtml(lockerName ?? "")}</span>`;
    else if (pendingName) badge = `<span class="lobby-chip-badge pending">⏳ ${escapeHtml(pendingName)} y pense</span>`;

    btn.innerHTML = `
      <span class="lobby-chip-name">${escapeHtml(c.name)}</span>
      <span class="lobby-chip-mod">${escapeHtml(c.modifierLabel)}</span>
      ${badge}
    `;
    btn.addEventListener("click", () => {
      if (!state.room) return;
      if (myReady) return;
      if (isLocked && !isMine) return;
      // Toggle : reclic sur sa nation = désélection
      const requestedId = isMine ? "" : c.id;
      state.room.send("selectCountry", { countryId: requestedId });
    });
    grid.appendChild(btn);
  }

  // Statut textuel
  if (me?.countryId) {
    const c = countriesData.find((x) => x.id === me.countryId);
    statusEl.innerHTML = `Nation sélectionnée : <strong>${escapeHtml(c?.name ?? "?")}</strong>. <em>${escapeHtml(c?.modifierLabel ?? "")}</em>`;
  } else {
    statusEl.textContent = "Aucune nation sélectionnée. Cliquez sur une nation disponible pour la réserver.";
  }
}

/**
 * Désactive le bouton "Je suis prêt" si le mode pays est activé et que le
 * joueur n'a pas encore sélectionné de nation.
 */
function updateReadyButtonAvailability() {
  const room = state.room;
  const btn = $opt<HTMLButtonElement>("ready-btn");
  if (!btn || !room) return;
  const players = playersSnapshot();
  const me = players.find((p) => p.id === state.sessionId);
  const useCountries = Boolean((room.state as any).useCountries);
  // Si déjà ready : on permet de défaire (laisser cliquable pour "Annuler ready")
  if (me?.ready) {
    btn.disabled = false;
    btn.textContent = "Annuler ma disponibilité";
    return;
  }
  btn.textContent = "Je suis prêt à siéger";
  if (useCountries && !me?.countryId) {
    btn.disabled = true;
    btn.title = "Sélectionnez une nation avant de vous déclarer prêt.";
  } else {
    btn.disabled = false;
    btn.title = "";
  }
}

// ============================================================ //
// Render: Draft viewport                                         //
// ============================================================ //

// Signature du dernier rendu du pool de draft. Permet d'éviter les rebuilds
// inutiles (et donc le flicker des illustrations) quand un bot pick mais que
// le pool du joueur courant n'a pas changé.
let lastDraftPoolSig: string | null = null;

// ============================================================ //
// Draft drawers : cartographie (manuel) + événement (auto-ouvert)
// ============================================================ //

/** Dernier eventId pour lequel le drawer événement s'est auto-ouvert. */
let draftEventDrawerShownForEventId: string | null = null;
/** Timer du auto-close du drawer événement (annulé si l'utilisateur interagit). */
let draftEventDrawerAutoCloseTimer: number | null = null;

function setDraftDrawer(drawer: HTMLElement, btn: HTMLButtonElement | null, open: boolean) {
  drawer.classList.toggle("collapsed", !open);
  if (btn) {
    btn.setAttribute("aria-expanded", open ? "true" : "false");
    // Quand le drawer est ouvert, le handle se déplace pour se coller au bord
    // du panneau — il reste sous la souris et permet de refermer en un clic.
    btn.classList.toggle("is-pushed", open);
  }
}
function isDraftDrawerOpen(drawer: HTMLElement) {
  return !drawer.classList.contains("collapsed");
}

function initDraftDrawers() {
  const mapBtn = $opt<HTMLButtonElement>("draft-map-handle");
  const mapDrawer = $opt<HTMLElement>("draft-map-drawer");
  const mapClose = $opt<HTMLButtonElement>("draft-map-drawer-close");
  const eventBtn = $opt<HTMLButtonElement>("draft-event-handle");
  const eventDrawer = $opt<HTMLElement>("draft-event-drawer");
  const eventClose = $opt<HTMLButtonElement>("draft-event-drawer-close");

  mapBtn?.addEventListener("click", () => {
    if (!mapDrawer) return;
    setDraftDrawer(mapDrawer, mapBtn, !isDraftDrawerOpen(mapDrawer));
  });
  mapClose?.addEventListener("click", () => {
    if (mapDrawer) setDraftDrawer(mapDrawer, mapBtn ?? null, false);
  });

  eventBtn?.addEventListener("click", () => {
    if (!eventDrawer) return;
    // Interaction manuelle : on annule le auto-close et la pulse
    if (draftEventDrawerAutoCloseTimer !== null) {
      window.clearTimeout(draftEventDrawerAutoCloseTimer);
      draftEventDrawerAutoCloseTimer = null;
    }
    eventBtn.classList.remove("has-pulse");
    setDraftDrawer(eventDrawer, eventBtn, !isDraftDrawerOpen(eventDrawer));
  });
  eventClose?.addEventListener("click", () => {
    if (!eventDrawer) return;
    if (draftEventDrawerAutoCloseTimer !== null) {
      window.clearTimeout(draftEventDrawerAutoCloseTimer);
      draftEventDrawerAutoCloseTimer = null;
    }
    setDraftDrawer(eventDrawer, eventBtn ?? null, false);
  });

  // Touche M pour toggle la cartographie ; Échap pour fermer les drawers ouverts
  document.addEventListener("keydown", (e) => {
    const target = e.target as HTMLElement | null;
    const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
    if (isTyping) return;
    if ((e.key === "m" || e.key === "M") && mapDrawer && mapBtn) {
      e.preventDefault();
      setDraftDrawer(mapDrawer, mapBtn, !isDraftDrawerOpen(mapDrawer));
    }
    if (e.key === "Escape") {
      if (mapDrawer && isDraftDrawerOpen(mapDrawer)) {
        setDraftDrawer(mapDrawer, mapBtn ?? null, false);
      }
      if (eventDrawer && isDraftDrawerOpen(eventDrawer)) {
        if (draftEventDrawerAutoCloseTimer !== null) {
          window.clearTimeout(draftEventDrawerAutoCloseTimer);
          draftEventDrawerAutoCloseTimer = null;
        }
        setDraftDrawer(eventDrawer, eventBtn ?? null, false);
      }
    }
  });
}

/**
 * À appeler depuis renderDraft quand un événement est révélé. Si c'est la 1ère
 * fois qu'on voit cet eventId, on rend visible le bouton ⚡ dans le bandeau,
 * on ouvre le drawer automatiquement pour 6 secondes (laisse le temps de lire),
 * puis on le referme. Le bouton garde une pulse subtile encore ~4s pour signaler
 * "il y a quelque chose à relire".
 */
function maybeAutoOpenEventDrawer(currentEventId: string, eventName: string) {
  const eventBtn = $opt<HTMLButtonElement>("draft-event-handle");
  const eventDrawer = $opt<HTMLElement>("draft-event-drawer");
  if (!eventBtn || !eventDrawer) return;

  // Le handle affiche "Événement" générique — ne PAS spoiler le nom de l'event
  // (juste signaler "il y a un événement à consulter").
  eventBtn.hidden = false;
  // Le titre est dans le drawer ; on garde un title="" pour l'accessibility
  eventBtn.setAttribute("title", `Événement du round : ${eventName}`);
  // Pas de toucher au label visible (reste "Événement")
  void eventName;

  // Ne pas auto-réouvrir si on l'a déjà fait pour cet event
  if (draftEventDrawerShownForEventId === currentEventId) return;
  draftEventDrawerShownForEventId = currentEventId;

  setDraftDrawer(eventDrawer, eventBtn, true);
  eventBtn.classList.add("has-pulse");

  if (draftEventDrawerAutoCloseTimer !== null) {
    window.clearTimeout(draftEventDrawerAutoCloseTimer);
  }
  draftEventDrawerAutoCloseTimer = window.setTimeout(() => {
    setDraftDrawer(eventDrawer, eventBtn, false);
    // La pulse reste encore ~4s comme signal "à relire"
    window.setTimeout(() => eventBtn.classList.remove("has-pulse"), 4000);
    draftEventDrawerAutoCloseTimer = null;
  }, 6000);
}

/** Cache complètement le drawer événement et son bouton (fin de round, etc.). */
function hideEventDrawer() {
  const eventBtn = $opt<HTMLButtonElement>("draft-event-handle");
  const eventDrawer = $opt<HTMLElement>("draft-event-drawer");
  if (eventBtn) {
    eventBtn.hidden = true;
    eventBtn.classList.remove("has-pulse");
    eventBtn.setAttribute("aria-expanded", "false");
  }
  if (eventDrawer) eventDrawer.classList.add("collapsed");
  draftEventDrawerShownForEventId = null;
  if (draftEventDrawerAutoCloseTimer !== null) {
    window.clearTimeout(draftEventDrawerAutoCloseTimer);
    draftEventDrawerAutoCloseTimer = null;
  }
}

// ─── PLAY : edge handle + drawer pour l'événement actif ────────────────
// Pattern identique à la draft, dupliqué pour découplage. Variables
// d'état séparées pour éviter qu'un timer draft annule un auto-open play
// et vice-versa.
let playEventDrawerShownForEventId: string | null = null;
let playEventDrawerAutoCloseTimer: number | null = null;

function initPlayEventDrawer() {
  const eventBtn = $opt<HTMLButtonElement>("play-event-handle");
  const eventDrawer = $opt<HTMLElement>("play-event-drawer");
  const eventClose = $opt<HTMLButtonElement>("play-event-drawer-close");
  if (!eventBtn || !eventDrawer) return;

  eventBtn.addEventListener("click", () => {
    if (playEventDrawerAutoCloseTimer !== null) {
      window.clearTimeout(playEventDrawerAutoCloseTimer);
      playEventDrawerAutoCloseTimer = null;
    }
    eventBtn.classList.remove("has-pulse");
    setDraftDrawer(eventDrawer, eventBtn, !isDraftDrawerOpen(eventDrawer));
  });
  eventClose?.addEventListener("click", () => {
    if (playEventDrawerAutoCloseTimer !== null) {
      window.clearTimeout(playEventDrawerAutoCloseTimer);
      playEventDrawerAutoCloseTimer = null;
    }
    setDraftDrawer(eventDrawer, eventBtn, false);
  });

  // Échap pour fermer le drawer si ouvert (ne pas dupliquer le handler clavier
  // M de la draft : ici on est en play, M ne fait rien de spécial).
  document.addEventListener("keydown", (e) => {
    if (e.key !== "Escape") return;
    if (state.room?.state.phase !== "play") return;
    if (isDraftDrawerOpen(eventDrawer)) {
      if (playEventDrawerAutoCloseTimer !== null) {
        window.clearTimeout(playEventDrawerAutoCloseTimer);
        playEventDrawerAutoCloseTimer = null;
      }
      setDraftDrawer(eventDrawer, eventBtn, false);
    }
  });
}

/**
 * À appeler depuis renderPlay quand un événement est actif. Au 1er reveal
 * pour cet eventId on rend visible le handle, on ouvre le drawer 6s,
 * pulse 4s supplémentaires. Aux renders suivants pour le même event,
 * on garde juste le handle visible (pas de re-open).
 */
function maybePlayAutoOpenEventDrawer(currentEventId: string, eventName: string) {
  const eventBtn = $opt<HTMLButtonElement>("play-event-handle");
  const eventDrawer = $opt<HTMLElement>("play-event-drawer");
  if (!eventBtn || !eventDrawer) return;

  eventBtn.hidden = false;
  eventBtn.setAttribute("title", `Événement actif : ${eventName}`);

  if (playEventDrawerShownForEventId === currentEventId) return;
  playEventDrawerShownForEventId = currentEventId;

  setDraftDrawer(eventDrawer, eventBtn, true);
  eventBtn.classList.add("has-pulse");

  if (playEventDrawerAutoCloseTimer !== null) {
    window.clearTimeout(playEventDrawerAutoCloseTimer);
  }
  playEventDrawerAutoCloseTimer = window.setTimeout(() => {
    setDraftDrawer(eventDrawer, eventBtn, false);
    window.setTimeout(() => eventBtn.classList.remove("has-pulse"), 4000);
    playEventDrawerAutoCloseTimer = null;
  }, 6000);
}

/** Cache le drawer play et son bouton (fin de partie, retour lobby). */
function hidePlayEventDrawer() {
  const eventBtn = $opt<HTMLButtonElement>("play-event-handle");
  const eventDrawer = $opt<HTMLElement>("play-event-drawer");
  if (eventBtn) {
    eventBtn.hidden = true;
    eventBtn.classList.remove("has-pulse");
    eventBtn.setAttribute("aria-expanded", "false");
  }
  if (eventDrawer) eventDrawer.classList.add("collapsed");
  playEventDrawerShownForEventId = null;
  if (playEventDrawerAutoCloseTimer !== null) {
    window.clearTimeout(playEventDrawerAutoCloseTimer);
    playEventDrawerAutoCloseTimer = null;
  }
}

function renderDraft() {
  const room = state.room;
  if (!room) return;

  $("draft-round-value").textContent = String(room.state.round || "—");
  $("draft-pick-value").textContent = `${state.draftPickInfo.pickNumber + 1}/${state.draftPickInfo.totalPicks || "—"}`;
  $("draft-direction-value").textContent = (room.state.draftDirection || "—").toUpperCase();

  // Event banner (révélé à mi-draft) — maintenant dans le drawer gauche.
  // Le drawer s'ouvre automatiquement pour 6s à la révélation, puis se range.
  // Le bouton ⚡ dans le bandeau de contexte reste visible pour rouvrir.
  const banner = $<HTMLElement>("draft-event-banner");
  const revealed = Boolean(room.state.eventRevealed);
  if (revealed && room.state.currentEventName) {
    banner.hidden = false;
    $("draft-event-title").textContent = room.state.currentEventName;
    $("draft-event-flavor").textContent = room.state.currentEventFlavor || "";
    // Auto-ouvre le drawer (no-op si déjà fait pour cet event)
    maybeAutoOpenEventDrawer(
      String(room.state.currentEventId ?? ""),
      String(room.state.currentEventName ?? "")
    );

    // Affiche le résumé technique des modificateurs (« cap densité 4 », « risk -10% », etc.)
    const modsEl = $opt<HTMLElement>("draft-event-mods");
    const modsCsv = String((room.state as any).eventModifiersDescription ?? "");
    if (modsEl) {
      if (modsCsv) {
        modsEl.textContent = `✦ Modificateurs actifs : ${modsCsv}`;
        modsEl.hidden = false;
      } else {
        modsEl.hidden = true;
      }
    }

    // Affiche aussi la description lisible des effets de l'événement, comme on le
    // fait pour les cartes. Aide les joueurs à comprendre ce que change l'événement.
    const effectsEl = $opt<HTMLElement>("draft-event-effects");
    if (effectsEl) {
      const evtId = String(room.state.currentEventId ?? "");
      const evt = eventsData.find((e: any) => e.id === evtId);
      effectsEl.innerHTML = "";
      if (evt?.effects) {
        const lines = describeEffects(evt.effects as any[]);
        if (lines.length > 0) {
          const ul = document.createElement("ul");
          ul.className = "draft-event-effects-list";
          for (const line of lines) {
            const li = document.createElement("li");
            li.textContent = line;
            ul.appendChild(li);
          }
          effectsEl.appendChild(ul);
        }
      }
    }
  } else {
    banner.hidden = true;
    hideEventDrawer();
  }

  // Neighbors — version pill compacte horizontale : "↻ CW   ← Probabiliste   Algorithme →"
  const neighbors = $("draft-neighbors-list");
  neighbors.innerHTML = "";
  const players = playersSnapshot();
  const me = players.find((p) => p.id === state.sessionId);
  if (me) {
    const total = players.length;
    const direction = (room.state.draftDirection ?? "cw") as string;
    const offset = direction === "cw" ? 1 : -1;
    const fromIdx = (me.seatIndex - offset + total) % total;
    const toIdx = (me.seatIndex + offset + total) % total;
    const from = players[fromIdx];
    const to = players[toIdx];

    const senseChip = document.createElement("span");
    senseChip.className = "nb-chip nb-chip-sense";
    senseChip.innerHTML = `<span class="nb-glyph">↻</span> ${direction.toUpperCase()}`;
    neighbors.appendChild(senseChip);

    if (from && from.id !== me.id) {
      const chip = document.createElement("span");
      chip.className = "nb-chip";
      const dot = `<span class="nb-dot" style="background:${seatColor(from.seatIndex)}"></span>`;
      chip.innerHTML = `<span class="nb-arrow">←</span>${dot}${escapeHtml(from.name)}`;
      neighbors.appendChild(chip);
    }
    if (to && to.id !== me.id) {
      const chip = document.createElement("span");
      chip.className = "nb-chip";
      const dot = `<span class="nb-dot" style="background:${seatColor(to.seatIndex)}"></span>`;
      chip.innerHTML = `${dot}${escapeHtml(to.name)}<span class="nb-arrow">→</span>`;
      neighbors.appendChild(chip);
    }
  }

  // Statut "qui a pioché / qui on attend" — pills nominatives compactes horizontales
  const progressList = $opt<HTMLElement>("draft-progress-list");
  const progressCounter = $opt<HTMLElement>("draft-progress-counter");
  if (progressList) {
    progressList.innerHTML = "";
    const active = players.filter((p) => p.status !== "eliminated");
    const sorted = [...active].sort((a, b) => a.seatIndex - b.seatIndex);
    let pickedCount = 0;
    for (const p of sorted) {
      const raw = room.state.players.get(p.id) as any;
      const picked = Boolean(raw && raw.draftedThisRound);
      if (picked) pickedCount++;
      const isMe = p.id === state.sessionId;
      const span = document.createElement("span");
      span.className = `dc-player-pill ${picked ? "is-picked" : "is-waiting"} ${isMe ? "is-me" : ""}`;
      const dot = `<span class="dc-pill-dot" style="background:${seatColor(p.seatIndex)}"></span>`;
      const icon = picked ? "✓" : "⏳";
      // On affiche le prénom court (premier mot du nom) pour rester compact ;
      // le nom complet est dans le title (tooltip au survol).
      const shortName = p.name.split(/\s+/).slice(0, 2).join(" ");
      span.innerHTML = `${dot}<span class="dc-pill-name">${escapeHtml(shortName)}${isMe ? " (vous)" : ""}</span><span class="dc-pill-icon">${icon}</span>`;
      span.setAttribute("title", `${p.name} — ${picked ? "a pioché" : "en attente de son pick"}`);
      progressList.appendChild(span);
    }
    if (progressCounter) {
      progressCounter.textContent = `${pickedCount}/${sorted.length}`;
    }
  }

  // Pool de draft : carrousel partagé avec le composant carrousel-main.
  // Mémoïsation par signature pour éviter le rebuild DOM (et le rechargement
  // des <img>) quand un bot pick : la position fractionnaire est appliquée
  // séparément via applyCarouselPositions sans toucher au DOM des cartes.
  const pool = $("draft-pool");
  $("draft-pool-title").textContent = `Votre pool · pick ${state.draftPickInfo.pickNumber + 1}/${state.draftPickInfo.totalPicks || "—"}`;

  const meRaw = state.sessionId ? room.state.players.get(state.sessionId) : null;
  const myDrafted = Boolean(meRaw && (meRaw as any).draftedThisRound);

  const newPoolSig = state.draftPool.map((c) => c.instanceId).join("|") + `#${myDrafted ? "drafted" : "open"}`;
  if (newPoolSig !== lastDraftPoolSig) {
    // Le pool a changé (nouveau round, ou pick passé au voisin). On efface la
    // sélection précédente : elle ne correspond plus aux cartes affichées.
    if (state.selectedDraftCardId !== null) {
      const stillPresent = state.draftPool.some((c) => c.instanceId === state.selectedDraftCardId);
      if (!stillPresent) state.selectedDraftCardId = null;
    }
    lastDraftPoolSig = newPoolSig;
    pool.innerHTML = "";
    pool.classList.add("card-carousel");
    if (state.draftPool.length === 0) {
      pool.innerHTML = '<p class="draft-status">Aucune carte dans votre pool pour l\'instant.</p>';
    } else {
      // Clamp la position si le pool a rétréci (un pick s'est fait).
      const rt = getCarouselRuntime("draft-pool");
      const maxPos = state.draftPool.length - 1;
      rt.position = Math.max(0, Math.min(maxPos, rt.position));

      const track = document.createElement("div");
      track.className = "card-carousel-track";
      state.draftPool.forEach((c, i) => {
        // Click : verrouille la carte comme sélection courante + tween vers
        // elle pour la centrer visuellement. La validation passe par le
        // bouton "Valider la sélection" dans la sidebar.
        const onClick = () => {
          state.selectedDraftCardId = c.instanceId;
          tweenCarouselTo("draft-pool", i, state.draftPool.length - 1);
          // Re-render seulement la classe is-selected sur les cartes (pas de
          // rebuild — applyCarouselPositions ne le gère pas pour is-selected).
          updateDraftPoolSelectionMarkers();
          renderDraftSelectionPanel();
        };
        const cardEl = createCardElement(c, myDrafted, false, onClick);
        cardEl.classList.add("card-carousel-card");
        cardEl.dataset.poolIndex = String(i);
        cardEl.dataset.instanceId = c.instanceId;
        if (c.instanceId === state.selectedDraftCardId) {
          cardEl.classList.add("is-selected");
        }
        track.appendChild(cardEl);
      });
      pool.appendChild(track);
      if (state.draftPool.length > 1) {
        const indicator = document.createElement("div");
        indicator.className = "card-carousel-indicator";
        pool.appendChild(indicator);
      }
      applyCarouselPositions("draft-pool");
    }
  } else {
    // Pool inchangé : on réapplique la position quand même (par sécurité, au
    // cas où le tween était en cours), et les markers de sélection.
    applyCarouselPositions("draft-pool");
    updateDraftPoolSelectionMarkers();
  }

  // Sidebar de carte sélectionnée
  renderDraftSelectionPanel();

  // Shelf des cartes déjà draftées dans cette session (vue compacte)
  renderDraftShelf();

  // Carte visible pendant le draft aussi : position stratégique
  renderTerritory2D();

  // La barre fixe de main est cachée pendant la draft (la shelf prend le relais),
  // mais on appelle quand même renderHand() — il sortira early et l'état reste sain.
  renderHand();
  updateHandFixedBarVisibility();
}

/**
 * Met à jour la classe `is-selected` sur les cartes du pool en fonction de
 * `state.selectedDraftCardId`. Ne touche pas au reste du DOM — appelable
 * à chaque clic/changement de sélection sans rebuild.
 */
function updateDraftPoolSelectionMarkers() {
  const pool = $opt<HTMLElement>("draft-pool");
  if (!pool) return;
  const cards = pool.querySelectorAll<HTMLElement>(".card-carousel-card");
  cards.forEach((cardEl) => {
    const id = cardEl.dataset.instanceId;
    if (id && id === state.selectedDraftCardId) {
      cardEl.classList.add("is-selected");
    } else {
      cardEl.classList.remove("is-selected");
    }
  });
}

/**
 * Affiche ou masque la sidebar de carte sélectionnée selon `state.selectedDraftCardId`.
 * Rend la carte (via createCardElement) dans un wrapper, ajoute la classe
 * `has-selection` au wrap parent pour faire glisser la sidebar.
 */
let lastSelectionSig: string | null = null;
function renderDraftSelectionPanel() {
  const panel = $opt<HTMLElement>("draft-selection-panel");
  const wrap = $opt<HTMLElement>("draft-pool-wrap");
  const cardSlot = $opt<HTMLElement>("draft-selection-card");
  if (!panel || !wrap || !cardSlot) return;

  const selectedId = state.selectedDraftCardId;
  if (!selectedId) {
    panel.hidden = true;
    wrap.classList.remove("has-selection");
    cardSlot.innerHTML = "";
    lastSelectionSig = null;
    return;
  }
  const card = state.draftPool.find((c) => c.instanceId === selectedId);
  if (!card) {
    // Sélection orpheline (ne devrait pas arriver, on a déjà nettoyé en amont) :
    // on clear silencieusement.
    state.selectedDraftCardId = null;
    panel.hidden = true;
    wrap.classList.remove("has-selection");
    cardSlot.innerHTML = "";
    lastSelectionSig = null;
    return;
  }
  panel.hidden = false;
  wrap.classList.add("has-selection");
  // Mémoïsation : ne reconstruit le DOM de la carte que si l'instance change.
  // Sans ça, chaque renderDraft ré-instancierait la carte → flicker.
  if (lastSelectionSig === selectedId) return;
  lastSelectionSig = selectedId;
  cardSlot.innerHTML = "";
  // Carte non-cliquable, juste pour affichage. Le createCardElement attend
  // un onClick handler — on passe un no-op.
  const cardEl = createCardElement(card, false, false, () => {});
  cardSlot.appendChild(cardEl);
}

/**
 * Câble les boutons Valider / Annuler de la sidebar de sélection.
 * Appelé une fois au boot (les boutons existent en permanence dans le DOM).
 */
function initDraftSelectionPanel() {
  const validate = $opt<HTMLButtonElement>("draft-selection-validate");
  const cancel = $opt<HTMLButtonElement>("draft-selection-cancel");
  validate?.addEventListener("click", () => {
    const id = state.selectedDraftCardId;
    if (!id) return;
    // Drafte la carte et clear immédiatement la sélection (le serveur
    // confirmera, mais on évite que l'utilisateur reclique entre temps).
    state.selectedDraftCardId = null;
    draftPick(id);
  });
  cancel?.addEventListener("click", () => {
    state.selectedDraftCardId = null;
    updateDraftPoolSelectionMarkers();
    renderDraftSelectionPanel();
  });
}

// ============================================================ //
// Draft shelf : thumbnails compactes des cartes déjà draftées    //
// ============================================================ //
// Pendant la draft, la barre fixe de main est cachée. Cette shelf prend le
// relais avec un format plus dense : illustration + catégorie + nom. Pas
// d'interaction — c'est de la consultation (rappel de ce qu'on a pris).

let lastShelfSig: string | null = null;

/**
 * Construit un <div.draft-shelf-thumb-img> avec une <img> qui tente la
 * cascade webp → png → jpg pour `cardId`. Si toutes les extensions échouent,
 * un placeholder "CM" s'affiche à la place. Même mécanique que createCardElement
 * pour la cohérence.
 */
function createShelfThumbImage(cardId: string): HTMLElement {
  const wrapper = document.createElement("div");
  wrapper.className = "draft-shelf-thumb-img";
  const img = document.createElement("img");
  img.alt = "";
  img.loading = "lazy";
  const tryNext = (i: number) => {
    const exts = ["webp", "png", "jpg"];
    if (i >= exts.length) {
      img.onload = null;
      img.onerror = null;
      img.style.display = "none";
      const placeholder = document.createElement("span");
      placeholder.className = "draft-shelf-thumb-placeholder";
      placeholder.setAttribute("aria-hidden", "true");
      placeholder.textContent = "CM";
      wrapper.appendChild(placeholder);
      return;
    }
    img.onerror = () => tryNext(i + 1);
    img.src = `/illustrations/${cardId}.${exts[i]}`;
  };
  tryNext(0);
  wrapper.appendChild(img);
  return wrapper;
}

function renderDraftShelf() {
  const list = $opt<HTMLElement>("draft-shelf-list");
  const counter = $opt<HTMLElement>("draft-shelf-counter");
  if (!list || !counter) return;
  // Source : state.draftedCards (alimenté par le message draftedHandUpdate).
  // PAS state.hand qui reste vide pendant la draft — le serveur ne distribue
  // la main de jeu qu'à la fin de la draft.
  const cards = state.draftedCards;
  // Signature mémoïsée : si la liste n'a pas changé, on ne touche pas au DOM
  // (évite le rechargement des <img> et les flashs visuels).
  const sig = cards.map((c) => c.instanceId).join("|");
  if (sig === lastShelfSig) return;
  lastShelfSig = sig;
  counter.textContent = String(cards.length);
  list.innerHTML = "";
  if (cards.length === 0) {
    const empty = document.createElement("p");
    empty.className = "draft-shelf-empty";
    empty.textContent = "Aucune carte draftée pour l'instant.";
    list.appendChild(empty);
    return;
  }
  for (const c of cards) {
    const thumb = document.createElement("div");
    thumb.className = "draft-shelf-thumb";
    thumb.title = c.name;
    thumb.appendChild(createShelfThumbImage(c.id));
    const meta = document.createElement("div");
    meta.className = "draft-shelf-thumb-meta";
    const cat = (c as any).category as string | undefined;
    if (cat) {
      const catEl = document.createElement("span");
      catEl.className = "draft-shelf-thumb-cat";
      catEl.textContent = cat;
      meta.appendChild(catEl);
    }
    const nameEl = document.createElement("span");
    nameEl.className = "draft-shelf-thumb-name";
    nameEl.textContent = c.name;
    meta.appendChild(nameEl);
    thumb.appendChild(meta);
    list.appendChild(thumb);
  }
}

/**
 * Wheel handler du carrousel du pool de draft, attaché une fois sur
 * `#draft-pool`. Actif uniquement en phase draft.
 */
function initDraftPoolCarouselWheel() {
  const pool = $opt<HTMLElement>("draft-pool");
  if (!pool) return;
  attachCarouselWheel(
    pool,
    "draft-pool",
    () => state.draftPool.length - 1,
    () => state.room?.state.phase === "draft" && state.draftPool.length > 1,
  );
}

// ============================================================ //
// Render: Play viewport                                          //
// ============================================================ //

function renderPlay() {
  const room = state.room;
  if (!room) return;

  $("play-round-value").textContent = String(room.state.round || "—");
  $("play-turn-value").textContent = String(room.state.turn || "—");
  $("play-phase-value").textContent = String(room.state.phase || "—");
  $("play-title").textContent = `Phase de jeu — Round ${room.state.round || "—"}`;

  // Settings banner : mode de victoire, plafond densité, grille
  const winMode = String(room.state.winMode ?? "couverture");
  const winThreshold = Number(room.state.winThreshold ?? 20);
  const cap = Number(room.state.currentDensityCap ?? 5);
  const gridRadius = Number(room.state.gridRadius ?? 0);
  const modeEl = $opt<HTMLElement>("play-mode-value");
  if (modeEl) {
    modeEl.textContent = winMode === "survie"
      ? "Survie (dernier non-vassalisé)"
      : `Couverture · ${winThreshold} hex`;
  }
  const capEl = $opt<HTMLElement>("play-cap-value");
  if (capEl) {
    capEl.textContent = `D${cap}${cap < 5 ? " (abaissé)" : ""}`;
    capEl.classList.toggle("alert", cap < 5);
  }
  const gridEl = $opt<HTMLElement>("play-grid-value");
  if (gridEl) {
    gridEl.textContent = gridRadius > 0 ? `Rayon ${gridRadius}` : "Hex indépendants";
  }

  // Event : contenu dans le drawer (#play-event-drawer). Le banner #play-event-banner
  // est désormais à l'intérieur du drawer — on le populate et on appelle
  // maybePlayAutoOpenEventDrawer pour révéler le handle + ouvrir auto au 1er reveal.
  const banner = $<HTMLElement>("play-event-banner");
  if (room.state.currentEventName) {
    banner.hidden = false;
    $("play-event-title").textContent = room.state.currentEventName;
    $("play-event-flavor").textContent = room.state.currentEventFlavor || "";
    const mods = $<HTMLElement>("play-event-mods");
    if (room.state.eventModifiersDescription) {
      mods.textContent = `✦ Modificateurs actifs : ${room.state.eventModifiersDescription}`;
      mods.hidden = false;
    } else {
      // Event sans effet mécanique (ex: Greta a 30 Ans) : on assume et on
      // l'affiche avec un texte qui colle au ton du jeu, plutôt que de laisser
      // un trou. Le joueur sait que rien ne change cette fois-ci.
      mods.textContent = `✦ Effet : néant. Le Conseil Mondial note la séance et passe à autre chose.`;
      mods.hidden = false;
    }
    const evtId = String(room.state.currentEventId ?? room.state.currentEventName);
    maybePlayAutoOpenEventDrawer(evtId, room.state.currentEventName);
  } else {
    banner.hidden = true;
    hidePlayEventDrawer();
  }

  renderMyStatus();
  renderWarnings();
  renderHand();
  // Synchronise la visibilité de la barre fixe : on arrive en play depuis la
  // draft où elle était masquée. Sans cet appel, elle reste cachée et le
  // joueur ne voit pas sa main.
  updateHandFixedBarVisibility();
  renderOpponents();
  // renderCharging() désactivé : la "Veille atmosphérique" (renderWarnings)
  // affichait déjà les cartes adverses en chargement, et le bloc dédié
  // dupliquait l'info. Si on veut afficher SES PROPRES cartes en chargement
  // un jour, on les ajoutera dans computeWarnings.
  // renderCharging();
  renderTerritory2D();
  renderGlobeUpdate();
  // Toasts de gratification : compare la taille du territoire à la précédente
  checkTerritoryChange();
}

/**
 * Spirale axiale : pour l'index N, retourne (q, r) en coordonnées axiales
 * d'un hex à distance D du centre. Index 0 = centre, indices 1-6 = anneau 1, etc.
 */
function spiralAxial(index: number): [number, number] {
  if (index === 0) return [0, 0];
  let ring = 1;
  let prevTotal = 1;
  while (prevTotal + 6 * ring <= index) {
    prevTotal += 6 * ring;
    ring++;
  }
  const localIdx = index - prevTotal;
  const side = Math.floor(localIdx / ring);
  const step = localIdx % ring;

  // Coins de l'anneau `ring` en coords axiales, ordre horaire
  const corners: Array<[number, number]> = [
    [ring, 0],
    [0, ring],
    [-ring, ring],
    [-ring, 0],
    [0, -ring],
    [ring, -ring],
  ];
  const startCorner = corners[side]!;
  const nextCorner = corners[(side + 1) % 6]!;
  const dx = (nextCorner[0] - startCorner[0]) / ring;
  const dy = (nextCorner[1] - startCorner[1]) / ring;
  return [startCorner[0] + dx * step, startCorner[1] + dy * step];
}

function drawHexagon(
  svg: SVGSVGElement,
  cx: number,
  cy: number,
  size: number,
  color: string,
  density: number,
  isMine: boolean,
  hexId?: string,
  isSelectable?: boolean,
  isSelected?: boolean
) {
  const ns = "http://www.w3.org/2000/svg";
  const points: string[] = [];
  for (let i = 0; i < 6; i++) {
    const angle = (Math.PI / 3) * i - Math.PI / 2;
    const x = cx + size * Math.cos(angle);
    const y = cy + size * Math.sin(angle);
    points.push(`${x.toFixed(1)},${y.toFixed(1)}`);
  }
  const polygon = document.createElementNS(ns, "polygon");
  polygon.setAttribute("points", points.join(" "));
  polygon.setAttribute("fill", color);
  const strokeColor = isSelected ? "#d4b96f" : (isMine ? "#2d2837" : "white");
  const strokeWidth = isSelected ? 3 : (isMine ? 1.5 : 1);
  polygon.setAttribute("stroke", strokeColor);
  polygon.setAttribute("stroke-width", String(strokeWidth));
  const opacity = 0.5 + (density / 5) * 0.5;
  polygon.setAttribute("opacity", String(opacity));
  polygon.style.transition = "opacity 200ms";

  // Marqueur pour event delegation au niveau du SVG
  if (hexId) {
    polygon.setAttribute("data-hex-id", hexId);
  }
  if (isSelectable && hexId) {
    polygon.style.cursor = "pointer";
    polygon.setAttribute("data-selectable", "1");
    polygon.style.pointerEvents = "all";
  }

  svg.appendChild(polygon);

  // Zone de clic invisible plus large pour faciliter la sélection (16 → 22)
  if (isSelectable && hexId) {
    const hit = document.createElementNS(ns, "polygon");
    const hitPoints: string[] = [];
    const hitSize = size + 4;
    for (let i = 0; i < 6; i++) {
      const angle = (Math.PI / 3) * i - Math.PI / 2;
      const x = cx + hitSize * Math.cos(angle);
      const y = cy + hitSize * Math.sin(angle);
      hitPoints.push(`${x.toFixed(1)},${y.toFixed(1)}`);
    }
    hit.setAttribute("points", hitPoints.join(" "));
    hit.setAttribute("fill", "transparent");
    hit.setAttribute("stroke", "none");
    hit.setAttribute("data-hex-id", hexId);
    hit.setAttribute("data-selectable", "1");
    hit.style.cursor = "pointer";
    hit.style.pointerEvents = "all";
    svg.appendChild(hit);
  }

  // Halo doré pour les hex sélectionnables
  if (isSelectable && hexId && !isSelected) {
    const halo = document.createElementNS(ns, "polygon");
    halo.setAttribute("points", points.join(" "));
    halo.setAttribute("fill", "none");
    halo.setAttribute("stroke", "#d4b96f");
    halo.setAttribute("stroke-width", "2");
    halo.setAttribute("stroke-dasharray", "3 2");
    halo.setAttribute("opacity", "0.8");
    halo.style.pointerEvents = "none";
    svg.appendChild(halo);
  }

  // Densité en chiffre au centre
  const text = document.createElementNS(ns, "text");
  text.setAttribute("x", String(cx));
  text.setAttribute("y", String(cy + 4));
  text.setAttribute("text-anchor", "middle");
  text.setAttribute("font-size", "11");
  text.setAttribute("font-weight", "700");
  text.setAttribute("font-family", "IBM Plex Mono, monospace");
  text.setAttribute("fill", "white");
  text.setAttribute("style", "filter: drop-shadow(0 1px 1.5px rgba(0,0,0,0.5));");
  text.textContent = String(density);
  text.style.pointerEvents = "none";
  svg.appendChild(text);
}

/**
 * Dessine un nuage de brouillard pour représenter un territoire adverse non révélé.
 * Affiche le nombre d'hex contrôlés + le D max si on a un intel "density" actif.
 */
function drawFogCluster(
  svg: SVGSVGElement,
  cx: number,
  cy: number,
  hexCount: number,
  baseColor: string,
  intelEntry: IntelEntry | null
) {
  const ns = "http://www.w3.org/2000/svg";

  // Nuage : ellipse opaque-clair avec une bordure pointillée
  const cloud = document.createElementNS(ns, "ellipse");
  cloud.setAttribute("cx", String(cx));
  cloud.setAttribute("cy", String(cy + 4));
  cloud.setAttribute("rx", String(22 + Math.min(hexCount, 12) * 1.6));
  cloud.setAttribute("ry", String(15 + Math.min(hexCount, 12) * 0.8));
  cloud.setAttribute("fill", baseColor);
  cloud.setAttribute("opacity", "0.18");
  svg.appendChild(cloud);

  const cloudRing = document.createElementNS(ns, "ellipse");
  cloudRing.setAttribute("cx", String(cx));
  cloudRing.setAttribute("cy", String(cy + 4));
  cloudRing.setAttribute("rx", String(22 + Math.min(hexCount, 12) * 1.6));
  cloudRing.setAttribute("ry", String(15 + Math.min(hexCount, 12) * 0.8));
  cloudRing.setAttribute("fill", "none");
  cloudRing.setAttribute("stroke", baseColor);
  cloudRing.setAttribute("stroke-width", "1.5");
  cloudRing.setAttribute("stroke-dasharray", "3 4");
  cloudRing.setAttribute("opacity", "0.55");
  svg.appendChild(cloudRing);

  // Label : "? × N" au centre, D max si intel partiel
  const label = document.createElementNS(ns, "text");
  label.setAttribute("x", String(cx));
  label.setAttribute("y", String(cy + 4));
  label.setAttribute("text-anchor", "middle");
  label.setAttribute("font-size", "12");
  label.setAttribute("font-weight", "700");
  label.setAttribute("font-family", "IBM Plex Mono, monospace");
  label.setAttribute("fill", "#5a4f3a");
  label.setAttribute("style", "filter: drop-shadow(0 1px 2px rgba(255,255,255,0.7));");
  label.style.pointerEvents = "none";

  if (intelEntry?.type === "density" && intelEntry.data.maxDensity !== undefined) {
    label.textContent = `? × ${hexCount}`;
    svg.appendChild(label);

    const dLabel = document.createElementNS(ns, "text");
    dLabel.setAttribute("x", String(cx));
    dLabel.setAttribute("y", String(cy + 18));
    dLabel.setAttribute("text-anchor", "middle");
    dLabel.setAttribute("font-size", "9");
    dLabel.setAttribute("font-weight", "600");
    dLabel.setAttribute("font-family", "IBM Plex Mono, monospace");
    dLabel.setAttribute("fill", "#8a3a3a");
    dLabel.textContent = `D≤${intelEntry.data.maxDensity}`;
    dLabel.style.pointerEvents = "none";
    svg.appendChild(dLabel);
  } else {
    label.textContent = `? × ${hexCount}`;
    svg.appendChild(label);
  }
}

function onHexClick(hexId: string) {
  const sel = state.pendingHexSelection;
  if (!sel) return;
  // Toggle : si déjà sélectionné, on le retire
  const idx = sel.selected.indexOf(hexId);
  if (idx >= 0) {
    sel.selected.splice(idx, 1);
  } else {
    sel.selected.push(hexId);
    // Si on atteint la cible, on commit
    if (sel.selected.length >= sel.needed) {
      playCardWithHexes(sel.card.instanceId, sel.selected);
      state.pendingHexSelection = null;
    }
  }
  renderPlay();
}

// ============================================================ //
// Grille hexagonale (nouveau modèle "cases disputées")           //
// ============================================================ //

interface CaseInfo {
  id: string;
  q: number;
  r: number;
  densities: Map<string, number>;
  capitalOf: string;
}

/** Collecte les cases depuis le state Colyseus dans une structure JS plate. */
function snapshotCases(): CaseInfo[] {
  const room = state.room;
  if (!room) return [];
  const out: CaseInfo[] = [];
  (room.state as any).cases?.forEach((c: any) => {
    const dens = new Map<string, number>();
    c.densities?.forEach?.((d: number, pid: string) => dens.set(pid, d));
    out.push({
      id: c.id,
      q: c.q,
      r: c.r,
      densities: dens,
      capitalOf: c.capitalOf ?? "",
    });
  });
  return out;
}

// ============================================================ //
// FX layer : petits nuages verts (pets) sur changements grille
// ============================================================ //

/**
 * Snapshot des densités d'une case, utilisé pour détecter les diffs entre 2 renders.
 * key = caseId, valeur = Map<playerId, density>
 */
type CaseDensitiesSnapshot = Map<string, Map<string, number>>;
let prevCaseSnapshot: CaseDensitiesSnapshot | null = null;

interface CaseFxDiff {
  caseId: string;
  q: number;
  r: number;
  /** Évolution par joueur : delta net de densité sur cette case. */
  byPlayer: Map<string, number>;
}

/** Calcule les diffs (delta de densité par joueur par case) entre snapshot précédent et actuel. */
function computeCaseDiffs(cases: CaseInfo[]): CaseFxDiff[] {
  if (!prevCaseSnapshot) return [];
  const diffs: CaseFxDiff[] = [];
  for (const c of cases) {
    const prev = prevCaseSnapshot.get(c.id);
    const byPlayer = new Map<string, number>();
    // Densités actuelles vs précédentes
    c.densities.forEach((d, pid) => {
      const old = prev?.get(pid) ?? 0;
      if (d !== old) byPlayer.set(pid, d - old);
    });
    // Densités présentes avant mais retirées maintenant
    prev?.forEach((d, pid) => {
      if (!c.densities.has(pid) && d > 0) {
        byPlayer.set(pid, -d);
      }
    });
    if (byPlayer.size > 0) diffs.push({ caseId: c.id, q: c.q, r: c.r, byPlayer });
  }
  return diffs;
}

function saveCaseSnapshot(cases: CaseInfo[]) {
  const snap: CaseDensitiesSnapshot = new Map();
  for (const c of cases) {
    snap.set(c.id, new Map(c.densities));
  }
  prevCaseSnapshot = snap;
}

// Suivi du territoire du joueur courant pour déclencher des toasts de gratification
let prevMyTerritorySize: number | null = null;
function checkTerritoryChange() {
  const room = state.room;
  if (!room || !state.sessionId) return;
  const me = room.state.players.get(state.sessionId) as any;
  if (!me) return;
  const current = Number(me.territorySize ?? 0);
  if (prevMyTerritorySize === null) {
    prevMyTerritorySize = current;
    return;
  }
  const delta = current - prevMyTerritorySize;
  if (delta !== 0) {
    if (delta > 0) {
      showGameToast(`+${delta} hex !`, "win");
    } else {
      showGameToast(`${delta} hex`, "loss");
    }
  }
  prevMyTerritorySize = current;
}

interface GameToast { id: number; text: string; kind: "win" | "loss" | "info"; expires: number; }
const gameToasts: GameToast[] = [];
let gameToastIdSeq = 0;
const GAME_TOAST_LIFETIME_MS = 2200;

function showGameToast(text: string, kind: "win" | "loss" | "info") {
  gameToasts.push({
    id: ++gameToastIdSeq,
    text,
    kind,
    expires: Date.now() + GAME_TOAST_LIFETIME_MS,
  });
  renderGameToasts();
  window.setTimeout(renderGameToasts, GAME_TOAST_LIFETIME_MS + 100);
}

function renderGameToasts() {
  let container = document.getElementById("game-toasts");
  if (!container) {
    container = document.createElement("div");
    container.id = "game-toasts";
    container.className = "game-toasts";
    document.body.appendChild(container);
  }
  const now = Date.now();
  for (let i = gameToasts.length - 1; i >= 0; i--) {
    if (gameToasts[i]!.expires <= now) gameToasts.splice(i, 1);
  }
  container.innerHTML = gameToasts
    .map((t) => `<div class="game-toast game-toast-${t.kind}">${escapeHtml(t.text)}</div>`)
    .join("");
}

/**
 * Injecte un nuage de pet en HTML/CSS dans la couche FX (un <div> dans le wrapper).
 * On passe par du HTML plutôt que SVG car les CSS transforms sur SVG circles sont
 * historiquement capricieuses (transform-box fragile selon navigateurs).
 *
 * pctX / pctY : position du centre de l'hex en pourcentage du wrapper (0–100),
 * pré-calculée par renderTerritory2D depuis les coords SVG via le viewBox.
 */
function injectFxCloud(
  fxLayer: HTMLDivElement,
  pctX: number,
  pctY: number,
  variant: "puff" | "fade" | "burst",
  tint: string
) {
  const wrap = document.createElement("div");
  wrap.className = `fx-pet fx-pet-${variant}`;
  wrap.style.left = `${pctX.toFixed(2)}%`;
  wrap.style.top = `${pctY.toFixed(2)}%`;

  // 5-7 petites bulles décalées qui montent et se dispersent
  const bubbleCount = variant === "burst" ? 7 : 5;
  for (let i = 0; i < bubbleCount; i++) {
    const bubble = document.createElement("span");
    bubble.className = "fx-pet-bubble";
    const dx = (Math.random() - 0.5) * 30; // pixels
    const dy = (Math.random() - 0.5) * 14 - 4;
    const size = 12 + Math.random() * 14;
    bubble.style.background = tint;
    bubble.style.width = `${size}px`;
    bubble.style.height = `${size}px`;
    bubble.style.marginLeft = `${dx}px`;
    bubble.style.marginTop = `${dy}px`;
    bubble.style.animationDelay = `${i * 70}ms`;
    wrap.appendChild(bubble);
  }

  // Anneau d'odeur qui se propage
  const ring = document.createElement("span");
  ring.className = "fx-smell-ring";
  // Borde dérivée du tint (mêmes coords HSL/RGB, opacité réduite)
  ring.style.borderColor = tint;
  wrap.appendChild(ring);

  fxLayer.appendChild(wrap);
  console.log("[sd] FX injected", { pctX: pctX.toFixed(1), pctY: pctY.toFixed(1), variant, tint });

  // Auto-cleanup après la durée maximale d'animation (1.8s pour être large)
  window.setTimeout(() => {
    if (wrap.parentNode === fxLayer) fxLayer.removeChild(wrap);
  }, 1800);
}

/**
 * Pour chaque case affectée par une phase (attrition / érosion), dépose
 * des bulles "-1" en couleur du joueur touché sur la couche FX du SVG
 * play. Une bulle par joueur affecté, empilée verticalement.
 */
function spawnPhaseDamageNumbers(
  cases: Array<{ caseId: string; affectedPlayerIds: string[] }>,
  kind: "attrition" | "erosion"
) {
  const room = state.room;
  if (!room) return;
  const gridRadius = (room.state as any).gridRadius ?? 0;
  if (gridRadius <= 0) return;
  // Trouve la couche FX du container VISIBLE. Auparavant on figeait via un
  // sélecteur CSS qui pouvait taper sur la carte de draft restée en
  // display:none (donc une fxLayer cachée) → damage numbers invisibles.
  const allFx = Array.from(document.querySelectorAll<HTMLDivElement>(".territory-2d .grid-wrapper .fx-layer"));
  const fxLayer = allFx.find((el) => (el as HTMLElement).offsetParent !== null) ?? null;
  if (!fxLayer) return;
  // Recalcule les dimensions du viewBox (idem renderTerritory2D)
  const hexSize = Math.max(12, Math.min(28, 200 / gridRadius));
  const padding = hexSize * 2;
  const W = Math.ceil((gridRadius * 2 + 1) * hexSize * Math.sqrt(3) + padding * 2);
  const H = Math.ceil((gridRadius * 2 + 1) * hexSize * 1.5 + padding * 2);
  const cxView = W / 2;
  const cyView = H / 2;

  for (const c of cases) {
    const parts = c.caseId.split(",");
    const q = Number(parts[0]);
    const r = Number(parts[1]);
    if (!Number.isFinite(q) || !Number.isFinite(r)) continue;
    const { x: ox, y: oy } = axialToPixel(q, r, hexSize);
    const pctX = ((cxView + ox) / W) * 100;
    const pctY = ((cyView + oy) / H) * 100;
    c.affectedPlayerIds.forEach((pid, idx) => {
      const seat = effectiveSeatIndexOf(pid);
      const color = seatColor(seat);
      injectDamageNumber(fxLayer, pctX, pctY, color, "−1", idx, kind);
    });
  }
}

/**
 * Damage number type JRPG : un "-1" coloré qui apparaît au-dessus d'une
 * case et monte légèrement en fade-out. Une bulle par joueur affecté,
 * stackées en escalier vertical pour éviter le chevauchement.
 * Utilisé par les phases Attrition (rouge orangé) et Érosion (gris vent).
 */
function injectDamageNumber(
  fxLayer: HTMLDivElement,
  pctX: number,
  pctY: number,
  color: string,
  label: string,
  stackOffset: number = 0,
  kind: "attrition" | "erosion" = "attrition"
) {
  const el = document.createElement("div");
  el.className = `fx-damage fx-damage-${kind}`;
  el.style.left = `${pctX.toFixed(2)}%`;
  el.style.top = `calc(${pctY.toFixed(2)}% - ${stackOffset * 14}px)`;
  el.style.color = color;
  el.textContent = label;
  fxLayer.appendChild(el);
  window.setTimeout(() => {
    if (el.parentNode === fxLayer) fxLayer.removeChild(el);
  }, 2200);
}

/**
 * Renvoie le contrôleur d'une case : playerId avec la densité strictement max,
 * ou "" si égalité / case vide.
 */
function controllerOf(c: CaseInfo): string {
  let max = 0;
  let controller = "";
  let tied = false;
  c.densities.forEach((d, pid) => {
    if (d > max) {
      max = d;
      controller = pid;
      tied = false;
    } else if (d === max && d > 0) {
      tied = true;
    }
  });
  return tied ? "" : controller;
}

/**
 * Calcule les cases éligibles pour une carte en cours de sélection (mode grille).
 * Selon la catégorie : pour Extension, cases adjacentes à mes positions ; pour Densité,
 * mes cases existantes ; pour Risque/Sabotage, cases adverses.
 */
/**
 * Nombre de cases que le joueur peut RÉELLEMENT sélectionner pour la carte en
 * cours, compte tenu de l'état du board. Peut être inférieur au spread demandé :
 *  - buff de pays (+1 étendue) alors qu'on n'a qu'une case (début de partie) ;
 *  - carte d'attaque alors qu'aucun adversaire n'est à portée ;
 *  - MOVE_CLOUDS dont la source choisie n'a aucun voisin valide.
 * Sans ce calcul, le bouton Valider restait grisé à jamais et la carte était
 * injouable (blocage dur si c'était la dernière carte en main).
 */
function maxSelectableCasesFor(sel: { card: ClientCard; needed: number; selected: string[] }): number {
  const eligible = computeEligibleCasesFor(sel.card).size;
  const isMoveClouds = (sel.card.effects ?? []).some(
    (e: any) => String(e?.type ?? "") === "MOVE_CLOUDS"
  );
  // MOVE_CLOUDS est séquentiel : `eligible` ne décrit que l'étape courante
  // (sources, puis voisins de la source) et n'inclut pas les cases déjà
  // choisies — on les additionne. Sinon `eligible` est l'ensemble complet.
  return isMoveClouds
    ? Math.min(sel.needed, sel.selected.length + eligible)
    : Math.min(sel.needed, Math.max(eligible, sel.selected.length));
}

function computeEligibleCasesFor(card: ClientCard): Set<string> {
  const out = new Set<string>();
  const room = state.room;
  if (!room || !state.sessionId) return out;
  const myId = state.sessionId;
  const cases = snapshotCases();
  // Mes cases où je suis présent (densité > 0 ou capitale). Sert de base
  // d'extension et d'attaque : le tunnel d'hex isolés est désormais
  // permis, mais sera régulé par la phase d'érosion d'isolement en fin
  // de tour (cases présentes sans soutien d'un contrôle perdent 1 densité).
  const myCaseCoords = new Set<string>();
  for (const c of cases) {
    if ((c.densities.get(myId) ?? 0) > 0 || c.capitalOf === myId) {
      myCaseCoords.add(c.id);
    }
  }

  // On scanne les effets de la carte (pas la catégorie) — une Défense comme
  // Mea Culpa Diplomatique a un ADD_HEX en plus de EXPOSE_PLAYER, donc il faut
  // détecter le type d'effet spatial pour proposer les bonnes cases.
  const effects = (card.effects ?? []) as Array<Record<string, any>>;
  const addCasesAdjacent = () => {
    for (const c of cases) {
      if (myCaseCoords.has(c.id)) {
        out.add(c.id);
        continue;
      }
      for (const a of getAdjacent(c.q, c.r)) {
        if (myCaseCoords.has(`${a.q},${a.r}`)) {
          out.add(c.id);
          break;
        }
      }
    }
  };
  const addOwnCases = () => {
    // Renforcement (MODIFY_DENSITY target=own_hex) : mes cases présentes
    for (const c of cases) {
      if ((c.densities.get(myId) ?? 0) > 0) out.add(c.id);
    }
  };
  const addOwnCasesAndAdjacent = () => {
    // MODIFY_DENSITY target=own_hex_adjacent : mes cases + voisines libres
    // (cartes Saturation Régionale, Compression Stratégique, etc.)
    for (const c of cases) {
      if ((c.densities.get(myId) ?? 0) > 0 || c.capitalOf === myId) {
        out.add(c.id);
      }
    }
    for (const c of cases) {
      if (out.has(c.id)) continue;
      for (const a of getAdjacent(c.q, c.r)) {
        const adjId = `${a.q},${a.r}`;
        const adjCase = cases.find((x) => x.id === adjId);
        if (!adjCase) continue;
        if ((adjCase.densities.get(myId) ?? 0) > 0 || adjCase.capitalOf === myId) {
          out.add(c.id);
          break;
        }
      }
    }
  };
  const addEnemyInRange = () => {
    // Attaque : la cible doit être adjacente à une case où j'ai au moins 1
    // de densité (présence, contrôle ou pas). Tunnels permis ; l'érosion
    // de fin de tour régule la durée des avancées non-soutenues.
    for (const c of cases) {
      let hasOther = false;
      c.densities.forEach((d, pid) => {
        if (pid !== myId && d > 0) hasOther = true;
      });
      if (!hasOther) continue;
      let inRange = false;
      for (const a of getAdjacent(c.q, c.r)) {
        if (myCaseCoords.has(`${a.q},${a.r}`)) {
          inRange = true;
          break;
        }
      }
      if (inRange) out.add(c.id);
    }
  };

  for (const eff of effects) {
    if (!isUserSelectableSpatialEffect(eff)) continue;
    const t = String(eff.type ?? "");
    const tgt = String(eff.target ?? "");
    if (t === "ADD_HEX") addCasesAdjacent();
    else if (t === "MODIFY_DENSITY") {
      if (tgt === "own_hex_adjacent") addOwnCasesAndAdjacent();
      else addOwnCases();
    }
    else if (t === "LOSE_HEX") addEnemyInRange();
    else if (t === "MOVE_CLOUDS") {
      // Vent Tactique : sélection séquentielle en 2 étapes.
      // - Étape 1 (source pas encore choisie) : cases adverses attaquables
      // - Étape 2 (source choisie) : voisins valides de la source (destination)
      const sel = state.pendingHexSelection;
      const sourceId = sel && sel.card.instanceId === card.instanceId && sel.selected.length >= 1
        ? sel.selected[0]
        : null;
      if (sourceId) {
        // Étape 2 : on n'autorise que les voisins valides de la source
        const src = cases.find((c) => c.id === sourceId);
        if (src) {
          for (const a of getAdjacent(src.q, src.r)) {
            const adjId = `${a.q},${a.r}`;
            const adjCase = cases.find((c) => c.id === adjId);
            if (!adjCase) continue;
            // Filtre du serveur : pas capitale d'un autre que la victime.
            // On ne connaît pas la victime côté client à 100% (le serveur la
            // déduit du explicitTargetId ou random), mais on protège a minima
            // les capitales du joueur courant et de tous les autres sauf la cible.
            // → on autorise toute case non-capitale (le serveur validera).
            if (adjCase.capitalOf) continue;
            out.add(adjCase.id);
          }
        }
      } else {
        // Étape 1 : cases adverses attaquables (= candidates pour source)
        addEnemyInRange();
      }
    }
  }

  return out;
}

/**
 * Initialise le click delegation au niveau des conteneurs stables (.territory-2d et #placement-grid).
 * À appeler une seule fois au boot. Le handler dispatche selon data-case-id + data-clickable.
 *
 * Pourquoi pas sur le SVG ? Parce qu'il est recréé à chaque render et perd son listener
 * entre le moment où la souris survole une case et le moment où on clique.
 */
let gridClickDelegationAttached = false;
function initGridClickDelegation() {
  if (gridClickDelegationAttached) return;
  gridClickDelegationAttached = true;

  const handler = (e: Event) => {
    let el = e.target as Element | null;
    let walk = 0;
    while (el && walk < 12) {
      walk++;
      const caseId = el.getAttribute?.("data-case-id");
      const clickable = el.getAttribute?.("data-clickable");
      if (caseId && clickable === "1") {
        console.log("[sd] delegation dispatch →", { caseId, walk });
        // Détermine le contexte : si on a une pendingHexSelection, on est en mode play ;
        // sinon on est en mode placement.
        if (state.pendingHexSelection) {
          onGridCaseClick(caseId);
        } else if (state.room?.state.phase === "placement") {
          state.room.send("placeCapital", { caseId });
        }
        return;
      }
      if (el.classList?.contains("territory-2d") || el.id === "placement-grid") break;
      el = el.parentElement;
    }
    console.log("[sd] delegation : aucun élément cliquable trouvé", { walkSteps: walk });
  };

  // Attache sur les conteneurs stables (présents dans le HTML, jamais détruits)
  document.querySelectorAll(".territory-2d").forEach((c) => {
    c.addEventListener("click", handler);
    c.addEventListener("mousemove", handleTooltipMove as EventListener);
    c.addEventListener("mouseleave", hideHexTooltip);
    console.log("[sd] delegation attachée à", c);
  });
  const placement = document.getElementById("placement-grid");
  if (placement && !placement.classList.contains("territory-2d")) {
    placement.addEventListener("click", handler);
    console.log("[sd] delegation attachée à #placement-grid");
  }
}

// ============================================================ //
// Zoom + pan sur la carte hex (phase play)
// ============================================================ //
// Le joueur peut zoomer pour mieux voir une portion de la carte (utile
// quand la barre de main couvre le bas) et panner en glissant. Le drag
// utilise un seuil pour ne pas conflit avec le clic d'hex (delegation).
// État stocké dans des variables module-local — pas dans state global :
// ça reste un état purement visuel, pas de besoin de persister.
let mapZoom = 1;
let mapPanX = 0;
let mapPanY = 0;
const MAP_ZOOM_MIN = 0.6;
const MAP_ZOOM_MAX = 3;
const MAP_PAN_THRESHOLD = 5; // pixels — au-delà on traite comme drag, pas comme click

function applyMapTransform() {
  const containers = document.querySelectorAll<HTMLElement>(".territory-2d.territory-2d-large");
  containers.forEach((c) => {
    c.style.setProperty("--map-zoom", String(mapZoom));
    c.style.setProperty("--map-pan-x", `${mapPanX}px`);
    c.style.setProperty("--map-pan-y", `${mapPanY}px`);
  });
  // Met à jour l'état disabled des boutons zoom (atteinte des bornes)
  const inBtn = $opt<HTMLButtonElement>("play-map-zoom-in");
  const outBtn = $opt<HTMLButtonElement>("play-map-zoom-out");
  const resetBtn = $opt<HTMLButtonElement>("play-map-zoom-reset");
  if (inBtn) inBtn.disabled = mapZoom >= MAP_ZOOM_MAX - 0.01;
  if (outBtn) outBtn.disabled = mapZoom <= MAP_ZOOM_MIN + 0.01;
  if (resetBtn) resetBtn.disabled = mapZoom === 1 && mapPanX === 0 && mapPanY === 0;
}
function setMapZoom(z: number) {
  const newZoom = Math.max(MAP_ZOOM_MIN, Math.min(MAP_ZOOM_MAX, z));
  if (newZoom === mapZoom) return;
  // Si on revient à zoom 1, on recale aussi le pan (sinon contenu off-center)
  if (newZoom === 1) { mapPanX = 0; mapPanY = 0; }
  mapZoom = newZoom;
  applyMapTransform();
}
function resetMapView() {
  mapZoom = 1;
  mapPanX = 0;
  mapPanY = 0;
  applyMapTransform();
}

function initMapZoomPan() {
  const inBtn = $opt<HTMLButtonElement>("play-map-zoom-in");
  const outBtn = $opt<HTMLButtonElement>("play-map-zoom-out");
  const resetBtn = $opt<HTMLButtonElement>("play-map-zoom-reset");
  inBtn?.addEventListener("click", () => setMapZoom(mapZoom * 1.25));
  outBtn?.addEventListener("click", () => setMapZoom(mapZoom * 0.8));
  resetBtn?.addEventListener("click", () => resetMapView());

  // Drag-to-pan : on enregistre la position initiale au pointerdown SANS
  // setPointerCapture (qui intercepterait le click et empêcherait la
  // delegation hex de fonctionner). pointermove/pointerup sont attachés
  // au document pour suivre la souris même si elle sort de la carte.
  // Le seuil de 5px différencie un clic d'un drag.
  const map = document.querySelector<HTMLElement>("#viewport-play .territory-2d.territory-2d-large");
  if (!map) return;
  type DragState = { startX: number; startY: number; basePanX: number; basePanY: number; dragging: boolean };
  let drag: DragState | null = null;

  map.addEventListener("pointerdown", (e: PointerEvent) => {
    // N'intercepte que le clic gauche
    if (e.button !== 0) return;
    // Ne pas démarrer un drag depuis les boutons zoom
    if ((e.target as HTMLElement).closest(".play-map-controls")) return;
    drag = {
      startX: e.clientX,
      startY: e.clientY,
      basePanX: mapPanX,
      basePanY: mapPanY,
      dragging: false,
    };
    // PAS de setPointerCapture ici : ça redirigerait le click qui suit vers
    // la map au lieu du polygon SVG, et la delegation ne pourrait pas
    // détecter la case cliquée. On capture seulement si un vrai drag démarre.
  });

  document.addEventListener("pointermove", (e: PointerEvent) => {
    if (!drag) return;
    const dx = e.clientX - drag.startX;
    const dy = e.clientY - drag.startY;
    if (!drag.dragging && Math.hypot(dx, dy) > MAP_PAN_THRESHOLD) {
      drag.dragging = true;
      map.classList.add("is-panning");
      // Maintenant qu'on est sûr d'être en drag, on capture le pointer pour
      // que pointermove continue à fonctionner même si la souris sort de
      // la map (utile pour ne pas perdre le drag).
      try { map.setPointerCapture(e.pointerId); } catch { /* ignore */ }
    }
    if (drag.dragging) {
      mapPanX = drag.basePanX + dx;
      mapPanY = drag.basePanY + dy;
      applyMapTransform();
    }
  });

  const endDrag = (e: PointerEvent) => {
    if (!drag) return;
    const wasDragging = drag.dragging;
    drag = null;
    if (wasDragging) {
      map.classList.remove("is-panning");
      try { map.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
      // Bloquer le click synthétique qui suit le drag (pour ne pas drafter
      // une case par erreur). Installé sur window en capture pour intercepter
      // avant que la delegation ne reçoive l'event.
      const blocker = (ev: Event) => {
        ev.stopPropagation();
        ev.preventDefault();
        window.removeEventListener("click", blocker, true);
      };
      window.addEventListener("click", blocker, true);
      // Sécurité : si pas de click ne suit (rare), on retire le blocker au
      // prochain tick pour ne pas bloquer un click manuel futur.
      setTimeout(() => window.removeEventListener("click", blocker, true), 100);
    }
    // Sinon (pas de drag), le click suit normalement et la delegation peut
    // traiter le polygon cliqué.
  };
  document.addEventListener("pointerup", endDrag);
  document.addEventListener("pointercancel", endDrag);

  // Hotkeys clavier : + / - / 0
  document.addEventListener("keydown", (e) => {
    if (state.room?.state.phase !== "play") return;
    const target = e.target as HTMLElement | null;
    const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
    if (isTyping) return;
    if (e.key === "+" || e.key === "=") {
      e.preventDefault();
      setMapZoom(mapZoom * 1.25);
    } else if (e.key === "-" || e.key === "_") {
      e.preventDefault();
      setMapZoom(mapZoom * 0.8);
    } else if (e.key === "0") {
      e.preventDefault();
      resetMapView();
    }
  });

  applyMapTransform(); // init des boutons disabled
}

// ============================================================ //
// Tooltip case : densités triées par taille au survol
// ============================================================ //

let hexTooltipEl: HTMLDivElement | null = null;
function getOrCreateHexTooltip(): HTMLDivElement {
  if (hexTooltipEl) return hexTooltipEl;
  const el = document.createElement("div");
  el.id = "hex-tooltip";
  el.className = "hex-tooltip";
  el.hidden = true;
  document.body.appendChild(el);
  hexTooltipEl = el;
  return el;
}

let lastTooltipCaseId: string | null = null;
function handleTooltipMove(e: MouseEvent) {
  let el = e.target as Element | null;
  let walk = 0;
  let caseId: string | null = null;
  while (el && walk < 8) {
    walk++;
    const cid = el.getAttribute?.("data-case-id");
    if (cid) { caseId = cid; break; }
    if (el.classList?.contains("territory-2d")) break;
    el = el.parentElement;
  }
  if (!caseId) {
    hideHexTooltip();
    return;
  }
  // Évite de re-render le contenu si on est toujours sur la même case
  if (caseId !== lastTooltipCaseId) {
    lastTooltipCaseId = caseId;
    const html = buildHexTooltipHtml(caseId);
    if (!html) {
      hideHexTooltip();
      return;
    }
    const tip = getOrCreateHexTooltip();
    tip.innerHTML = html;
    tip.hidden = false;
  }
  // Positionne près du curseur (avec offset pour ne pas overlap)
  const tip = getOrCreateHexTooltip();
  if (tip.hidden) return;
  const offset = 14;
  let x = e.clientX + offset;
  let y = e.clientY + offset;
  // Évite de sortir à droite/bas de la fenêtre
  const rect = tip.getBoundingClientRect();
  if (x + rect.width > window.innerWidth - 8) x = e.clientX - rect.width - offset;
  if (y + rect.height > window.innerHeight - 8) y = e.clientY - rect.height - offset;
  tip.style.left = `${x}px`;
  tip.style.top = `${y}px`;
}

function hideHexTooltip() {
  lastTooltipCaseId = null;
  if (hexTooltipEl) hexTooltipEl.hidden = true;
}

function buildHexTooltipHtml(caseId: string): string | null {
  const room = state.room;
  if (!room) return null;
  const cases = snapshotCases();
  const cas = cases.find((c) => c.id === caseId);
  if (!cas) return null;

  // Respect du fog of war : on n'affiche les densités précises que sur les cases
  // détaillées (les miennes + adjacentes). Sur les cases avec présence brouillée,
  // on dit juste "présence adverse". Hors fog : pas de tooltip du tout.
  const { detailed, presence } = getVisibleCases();
  const myId = state.sessionId ?? "";
  const myDensity = cas.densities.get(myId) ?? 0;
  const isDetailed = detailed.has(cas.id);
  const hasPresence = presence.has(cas.id);
  if (!isDetailed && !hasPresence && myDensity === 0) return null;

  // Header : coords + capitale visible si elle est détaillée
  const coords = `${cas.q}, ${cas.r}`;
  let head = `<div class="ht-head"><span class="ht-coords">${escapeHtml(coords)}</span>`;
  if (cas.capitalOf && isDetailed) {
    const capOwner = room.state.players.get(cas.capitalOf) as any;
    if (capOwner) head += `<span class="ht-capital">🏛 capitale de ${escapeHtml(String(capOwner.name ?? "—"))}</span>`;
  }
  head += `</div>`;

  // Cas brouillé : on indique juste "présence adverse" sans dévoiler les densités
  if (!isDetailed && hasPresence && myDensity === 0) {
    return head + `<div class="ht-empty">Présence adverse détectée<br/><span style="font-size:10px;color:var(--ink-mute)">(rapprochez-vous pour voir les détails)</span></div>`;
  }

  // Cas détaillé : on liste les densités triées par taille
  type Row = { id: string; name: string; seat: number; density: number; isMe: boolean; isCapital: boolean };
  const rows: Row[] = [];
  cas.densities.forEach((d, pid) => {
    if (d <= 0) return;
    const p = room.state.players.get(pid) as any;
    if (!p) return;
    rows.push({
      id: pid,
      name: String(p.name ?? "—"),
      seat: Number(p.seatIndex ?? 0),
      density: d,
      isMe: pid === state.sessionId,
      isCapital: cas.capitalOf === pid,
    });
  });
  rows.sort((a, b) => b.density - a.density || a.seat - b.seat);

  if (rows.length === 0) {
    return head + `<div class="ht-empty">Case vide</div>`;
  }

  const rowsHtml = rows
    .map((r) => {
      const seatBg = seatColor(r.seat);
      const meTag = r.isMe ? `<span class="ht-me">vous</span>` : "";
      const capTag = r.isCapital ? `<span class="ht-cap">🏛</span>` : "";
      return `<div class="ht-row${r.isMe ? " ht-row-me" : ""}">
        <span class="ht-dot" style="background:${seatBg}"></span>
        <span class="ht-name">${escapeHtml(r.name)}${meTag}${capTag}</span>
        <span class="ht-density">D ${r.density}</span>
      </div>`;
    })
    .join("");

  return head + `<div class="ht-rows">${rowsHtml}</div>`;
}

/** Click sur une case en mode grille : ajoute/retire à la sélection. */
function onGridCaseClick(caseId: string) {
  console.log("[sd] onGridCaseClick", { caseId, pendingHexSelection: state.pendingHexSelection });
  const sel = state.pendingHexSelection;
  if (!sel) {
    console.warn("[sd] click ignoré : aucune pendingHexSelection (clique d'abord une carte de ta main)");
    return;
  }
  const idx = sel.selected.indexOf(caseId);
  if (idx >= 0) {
    // Re-clic sur une case déjà cochée → on la retire
    sel.selected.splice(idx, 1);
    console.log("[sd] case retirée de la sélection", { selected: [...sel.selected], needed: sel.needed });
  } else {
    // Bloque l'ajout si on est déjà à la limite. Empêche le compteur d'afficher
    // 2/1 et le serveur de slicer silencieusement la deuxième sélection.
    if (sel.selected.length >= sel.needed) {
      console.warn(
        "[sd] sélection refusée : limite atteinte",
        { selected: sel.selected.length, needed: sel.needed }
      );
      flashTargetingPanel();
      return;
    }
    sel.selected.push(caseId);
    console.log("[sd] case ajoutée à la sélection", { selected: [...sel.selected], needed: sel.needed });
  }
  renderPlay();
}

/**
 * Petit feedback visuel sur le panneau de targeting quand on essaie de
 * sélectionner plus de cases que la carte ne permet : ajoute une classe
 * "flash-deny" qui joue une animation CSS courte, puis l'enlève.
 */
function flashTargetingPanel() {
  const panel = $opt<HTMLElement>("targeting-panel");
  if (!panel) return;
  panel.classList.remove("flash-deny");
  // Force un reflow pour redémarrer l'animation
  void panel.offsetWidth;
  panel.classList.add("flash-deny");
  window.setTimeout(() => panel.classList.remove("flash-deny"), 350);
}

/**
 * Renvoie l'ensemble des cases "visibles" pour le joueur courant :
 * - Mes cases (densité > 0) → visibles
 * - Mes capitales → visibles (et adjacentes)
 * - Cases adjacentes à mes positions → visibles (densités précises)
 * - Cases d'un joueur sur lequel j'ai un intel "territory" actif → détaillées
 *   (cartes Reconnaissance Aérienne, Communiqué Météo) ; le rendu lira les
 *   densités en LIVE — l'intel = autorisation de voir, pas un snapshot figé.
 * - Sinon → présence brouillée (on voit qu'il y a quelqu'un mais pas combien)
 */
function getVisibleCases(): { detailed: Set<string>; presence: Set<string> } {
  const detailed = new Set<string>();
  const presence = new Set<string>();
  const myId = state.sessionId;
  if (!myId) return { detailed, presence };
  const cases = snapshotCases();
  // Étape 1 : mes cases + adjacentes à moi → détaillées
  for (const c of cases) {
    if ((c.densities.get(myId) ?? 0) > 0 || c.capitalOf === myId) {
      detailed.add(c.id);
      // Voisinage
      for (const a of getAdjacent(c.q, c.r)) {
        detailed.add(`${a.q},${a.r}`);
      }
    }
  }
  // Étape 1bis : joueurs avec intel territory actif → leurs cases sont détaillées.
  // C'est l'effet attendu par les cartes Reconnaissance Aérienne et Communiqué Météo.
  // Sans ce branchement, le state.intel était stocké mais ignoré par le fog of war
  // de la grille principale (silencieusement no-op pour le joueur humain).
  const revealedPlayerIds = new Set<string>();
  for (const [pid, entry] of state.intel) {
    if (entry.type === "territory") revealedPlayerIds.add(pid);
  }
  if (revealedPlayerIds.size > 0) {
    for (const c of cases) {
      for (const pid of revealedPlayerIds) {
        if ((c.densities.get(pid) ?? 0) > 0 || c.capitalOf === pid) {
          detailed.add(c.id);
          break;
        }
      }
    }
  }
  // Étape 2 : autres cases avec présence adverse → mode présence
  for (const c of cases) {
    if (detailed.has(c.id)) continue;
    let hasAny = false;
    c.densities.forEach((d) => {
      if (d > 0) hasAny = true;
    });
    if (hasAny || c.capitalOf) presence.add(c.id);
  }
  return { detailed, presence };
}

/**
 * S'assure qu'un <pattern> SVG existe (dans <defs>) pour le set de couleurs
 * donné. Sert à dessiner les hachures "intrus" sur les cases où je suis
 * présent et d'autres joueurs aussi (signal visuel de pression imminente).
 *
 * Le pattern est tuile 10×10 (avant rotation -45°), avec N bandes horizontales
 * de 2px chacune empilées au sommet (= 1 par couleur, total 2*N px), puis le
 * reste de la tuile transparent (= l'espacement entre deux groupes de bandes
 * adjacents). Conséquence : plus il y a d'intrus, plus la bande est épaisse,
 * mais l'écart entre les "rayures" reste constant — comme demandé.
 */
function ensureHatchPattern(
  svg: SVGSVGElement,
  ns: string,
  id: string,
  colors: string[]
): void {
  if (svg.querySelector(`#${id}`)) return;
  let defs = svg.querySelector("defs") as SVGDefsElement | null;
  if (!defs) {
    defs = document.createElementNS(ns, "defs") as SVGDefsElement;
    svg.insertBefore(defs, svg.firstChild);
  }
  const pattern = document.createElementNS(ns, "pattern");
  pattern.setAttribute("id", id);
  pattern.setAttribute("patternUnits", "userSpaceOnUse");
  const STRIPE_W = 3;          // 3px par couleur
  const SEP = 1;               // 1px de séparateur clair entre rayures
                                //   colorées + cadre blanc d'encadrement
                                //   haut/bas pour démarcation daltonien
  const GAP = 6;               // espacement constant entre groupes de rayures
  // Largeur totale du groupe incluant le cadre blanc haut+bas
  const groupWithBorder =
    STRIPE_W * colors.length + SEP * (colors.length - 1) + SEP * 2;
  const tileSize = groupWithBorder + GAP;
  pattern.setAttribute("width", String(tileSize));
  pattern.setAttribute("height", String(tileSize));
  pattern.setAttribute("patternTransform", "rotate(-45)");

  // Fond blanc qui couvre toute la zone du groupe : crée un cadre blanc
  // de 1px en haut + 1px en bas + 1px entre chaque couleur. Améliore
  // significativement la visibilité pour daltonisme rouge/vert sur fond
  // violet : on perçoit le motif même quand les couleurs se confondent.
  const bg = document.createElementNS(ns, "rect");
  bg.setAttribute("x", "0");
  bg.setAttribute("y", "0");
  bg.setAttribute("width", String(tileSize));
  bg.setAttribute("height", String(groupWithBorder));
  bg.setAttribute("fill", "#ffffff");
  bg.setAttribute("opacity", "0.9");
  pattern.appendChild(bg);

  // Une bande horizontale par couleur, décalée de SEP (cadre blanc haut)
  for (let i = 0; i < colors.length; i++) {
    const rect = document.createElementNS(ns, "rect");
    rect.setAttribute("x", "0");
    rect.setAttribute("y", String(SEP + i * (STRIPE_W + SEP)));
    rect.setAttribute("width", String(tileSize));
    rect.setAttribute("height", String(STRIPE_W));
    rect.setAttribute("fill", colors[i]!);
    rect.setAttribute("opacity", "1");
    pattern.appendChild(rect);
  }
  defs.appendChild(pattern);
}

/**
 * Dessine la grille hexagonale en SVG. Retourne l'élément SVG.
 *
 * @param mode : "play" pour le rendu normal, "placement" pour la phase placement
 * @param selectedCases : ids des cases déjà sélectionnées par le joueur (highlight)
 * @param eligibleCases : ids des cases sélectionnables (highlight cliquable)
 * @param onClick : callback déclenché au click sur une case éligible
 */
/**
 * Compteur module-level pour générer un nonce unique par appel à buildGridSvg.
 * Sert à donner des IDs de pattern uniques entre les différents SVG rendus
 * dans le DOM (placement-grid, draft, play). Sans ça, le navigateur résolvait
 * `url(#hatch-minor-0)` vers le premier match du document — souvent le SVG
 * d'une phase invisible avec un userSpace décalé → hachures invisibles.
 */
let buildGridSvgNonceCounter = 0;

function buildGridSvg(
  gridRadius: number,
  cases: CaseInfo[],
  options: {
    mode: "play" | "placement";
    selectedCases?: Set<string>;
    eligibleCases?: Set<string>;
    candidateCases?: Set<string>;
    detailed?: Set<string>;
    presence?: Set<string>;
    onClick?: (caseId: string) => void;
    /** Pour la prévisualisation sur cases sélectionnées (carte en cours). */
    projection?: { mode: "add" | "lose" | "none"; perCase: number };
  }
): SVGSVGElement {
  const ns = "http://www.w3.org/2000/svg";
  // Nonce propre à ce SVG, suffixé à tous les IDs de pattern qu'on crée
  const svgNonce = `g${++buildGridSvgNonceCounter}`;
  const hexSize = Math.max(12, Math.min(28, 200 / gridRadius));
  const padding = hexSize * 2;
  const W = Math.ceil((gridRadius * 2 + 1) * hexSize * Math.sqrt(3) + padding * 2);
  const H = Math.ceil((gridRadius * 2 + 1) * hexSize * 1.5 + padding * 2);
  const cx = W / 2;
  const cy = H / 2;

  const svg = document.createElementNS(ns, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  svg.setAttribute("style", "width: 100%; height: auto; display: block; max-height: 640px;");

  // NOTE : le click handler est attaché au conteneur stable via initGridClickDelegation().
  // Le SVG peut être recréé à chaque render Colyseus, mais le conteneur .territory-2d
  // (ou #placement-grid) persiste, ce qui rend le delegation robuste. Les polygones
  // portent seulement data-case-id et data-clickable pour identification.
  // L'option onClick est ignorée — voir initGridClickDelegation.
  void options.onClick;

  // Fond
  const bg = document.createElementNS(ns, "rect");
  bg.setAttribute("width", String(W));
  bg.setAttribute("height", String(H));
  bg.setAttribute("fill", "#fbf7ee");
  svg.appendChild(bg);

  const selected = options.selectedCases ?? new Set<string>();
  const eligible = options.eligibleCases ?? new Set<string>();
  const candidates = options.candidateCases ?? new Set<string>();
  const detailed = options.detailed ?? new Set<string>();
  const presence = options.presence ?? new Set<string>();

  // ===== Pré-calcul "érosion fin de tour" pour le joueur courant =====
  // Une de mes cases présentes va perdre 1 densité fin de tour si je ne la
  // contrôle pas ET qu'aucune voisine n'est contrôlée par moi. Sert à
  // afficher un badge "💨" sur ces cases (avancée non-soutenue).
  const myId = state.sessionId ?? "";
  const myControlledCoords = new Set<string>();
  if (options.mode === "play" && myId) {
    for (const c of cases) {
      const mine = c.densities.get(myId) ?? 0;
      const isMyCapital = c.capitalOf === myId;
      if (mine === 0 && !isMyCapital) continue;
      let topOther = 0;
      c.densities.forEach((d, pid) => {
        if (pid === myId) return;
        if (d > topOther) topOther = d;
      });
      const controls = isMyCapital ? mine >= topOther : mine > topOther;
      if (controls) myControlledCoords.add(c.id);
    }
  }
  const willErodeCoords = new Set<string>();
  if (options.mode === "play" && myId) {
    for (const c of cases) {
      const mine = c.densities.get(myId) ?? 0;
      if (mine === 0) continue;
      if (myControlledCoords.has(c.id)) continue; // contrôle stable
      let touchesControl = false;
      for (const a of getAdjacent(c.q, c.r)) {
        if (myControlledCoords.has(`${a.q},${a.r}`)) {
          touchesControl = true;
          break;
        }
      }
      if (!touchesControl) willErodeCoords.add(c.id);
    }
  }

  for (const c of cases) {
    const { x: ox, y: oy } = axialToPixel(c.q, c.r, hexSize);
    const px = cx + ox;
    const py = cy + oy;

    // Polygone (hex pointy-top)
    const points: string[] = [];
    for (let i = 0; i < 6; i++) {
      const ang = (Math.PI / 3) * i - Math.PI / 2;
      points.push(`${(px + hexSize * Math.cos(ang)).toFixed(1)},${(py + hexSize * Math.sin(ang)).toFixed(1)}`);
    }
    const poly = document.createElementNS(ns, "polygon");
    poly.setAttribute("points", points.join(" "));

    // Détermine le rendu selon le mode
    const ctrl = controllerOf(c);
    let fill = "#ffffff";
    let stroke = "#d9cfbb";
    let strokeWidth = "1";
    let textContent = "";
    let textColor = "#9e9485";

    if (options.mode === "placement") {
      // Candidates highlightées
      if (candidates.has(c.id)) {
        fill = "rgba(212, 185, 111, 0.25)";
        stroke = "#b89b54";
        strokeWidth = "2";
      }
      if (c.capitalOf) {
        // Déjà choisie par un joueur
        const ownerSeat = state.room?.state.players.get(c.capitalOf)?.seatIndex ?? 0;
        fill = seatColor(ownerSeat);
        stroke = "#2d2837";
        strokeWidth = "2";
        textContent = "🏛";
        textColor = "white";
      }
    } else {
      // Mode play
      const isDetailed = detailed.has(c.id);
      const hasPresence = presence.has(c.id);
      if (isDetailed) {
        // Cases nettes : colorée selon contrôleur (ou neutre).
        // Si le contrôleur est un vassal, on prend la couleur du maître :
        // les cases du vassal apparaissent fondues dans l'équipe du maître.
        if (ctrl) {
          const seat = effectiveSeatIndexOf(ctrl);
          fill = seatColor(seat);
          const myD = c.densities.get(state.sessionId ?? "") ?? 0;
          const ctrlD = c.densities.get(ctrl) ?? 0;
          textContent = String(ctrlD);
          textColor = "white";
          // Si je suis le contrôleur, ma case est plus claire
          void myD;
        } else {
          // Pas de contrôleur (vide ou disputée)
          const myD = c.densities.get(state.sessionId ?? "") ?? 0;
          if (myD > 0) {
            // Cases disputées : on voit notre densité (couleur effective si vassal)
            const seat = effectiveSeatIndexOf(state.sessionId ?? "");
            fill = seatColor(seat);
            fill = fill.replace("rgb(", "rgba(").replace(")", ", 0.5)");
            textContent = `${myD}?`;
            textColor = "white";
          }
        }
        if (c.capitalOf === state.sessionId) {
          stroke = "#2d2837";
          strokeWidth = "3";
        }
      } else if (hasPresence) {
        // Présence adverse brouillée : fond gris, texte flou
        fill = "rgba(155, 123, 196, 0.15)";
        textContent = "?";
        textColor = "#9080a0";
      }
      // Highlight sélection (carte en cours)
      if (selected.has(c.id)) {
        stroke = "#d4b96f";
        strokeWidth = "3";
      } else if (eligible.has(c.id)) {
        stroke = "#7ba89b";
        strokeWidth = "2";
      }
    }

    poly.setAttribute("fill", fill);
    poly.setAttribute("stroke", stroke);
    poly.setAttribute("stroke-width", strokeWidth);

    const clickable =
      (options.mode === "placement" && candidates.has(c.id) && !c.capitalOf) ||
      (options.mode === "play" && eligible.has(c.id));
    // Marqueurs pour event delegation au niveau du SVG
    poly.setAttribute("data-case-id", c.id);
    if (clickable) {
      poly.setAttribute("data-clickable", "1");
      poly.style.cursor = "pointer";
      poly.style.pointerEvents = "all";
    }
    svg.appendChild(poly);

    // Hachures "minoritaires" : sur chaque case détaillée avec ≥ 2 joueurs
    // présents, on superpose des hachures aux couleurs de tous ceux qui NE
    // SONT PAS le contrôleur (= les minoritaires, peu importe que ce soit moi
    // ou un adversaire). Signale visuellement la contestation : sur ma case
    // dominée → je vois les couleurs de ceux qui montent ; sur une case que
    // je ne contrôle pas mais où je suis présent → je vois MA couleur en
    // hachures sur le fond du contrôleur, donc je sais où je suis minoritaire.
    if (options.mode === "play" && detailed.has(c.id)) {
      // Intrus = présents non-contrôleurs, ET appartenant à une équipe
      // différente de celle du contrôleur. Si un vassal est sur la case
      // contrôlée par son maître (même équipe), pas de hachure : c'est
      // visuellement "la même couleur". Dédup par seat effectif (équipe).
      const ctrlEffSeat = ctrl ? effectiveSeatIndexOf(ctrl) : -1;
      const seenSeats = new Set<number>();
      const intruders: Array<{ seat: number; color: string }> = [];
      c.densities.forEach((d, pid) => {
        if (d <= 0) return;
        if (pid === ctrl) return;
        const seat = effectiveSeatIndexOf(pid);
        if (ctrl && seat === ctrlEffSeat) return; // même équipe que le contrôleur
        if (seenSeats.has(seat)) return;
        seenSeats.add(seat);
        intruders.push({ seat, color: seatColor(seat) });
      });
      if (intruders.length > 0) {
        intruders.sort((a, b) => a.seat - b.seat);
        const seats = intruders.map((i) => i.seat);
        const colors = intruders.map((i) => i.color);
        // Préfixe svgNonce pour éviter la collision d'IDs entre les
        // <pattern> des SVG vivant simultanément dans le DOM (placement,
        // draft, play). Sans ça, url(#hatch-minor-0) résolvait vers le
        // pattern d'un SVG invisible au userSpace décalé.
        const patternId = `${svgNonce}-hatch-minor-${seats.join("-")}`;
        ensureHatchPattern(svg, ns, patternId, colors);

        const hatchPoly = document.createElementNS(ns, "polygon");
        hatchPoly.setAttribute("points", points.join(" "));
        hatchPoly.setAttribute("fill", `url(#${patternId})`);
        // Recopie le stroke du polygon de base sinon les bordures spéciales
        // (capitale = stroke épais foncé, sélection = stroke jaune, etc.)
        // disparaissent visuellement sous l'overlay. Avec ce stroke, on
        // garde le marqueur capitale même quand des intrus s'y trouvent.
        hatchPoly.setAttribute("stroke", stroke);
        hatchPoly.setAttribute("stroke-width", strokeWidth);
        hatchPoly.setAttribute("fill-rule", "evenodd");
        hatchPoly.style.pointerEvents = "none";
        svg.appendChild(hatchPoly);
      }
    }

    // Texte
    if (textContent) {
      const txt = document.createElementNS(ns, "text");
      txt.setAttribute("x", String(px));
      txt.setAttribute("y", String(py + 4));
      txt.setAttribute("text-anchor", "middle");
      txt.setAttribute("font-size", String(Math.max(10, hexSize * 0.5)));
      txt.setAttribute("font-weight", "700");
      txt.setAttribute("font-family", "IBM Plex Mono, monospace");
      txt.setAttribute("fill", textColor);
      txt.style.pointerEvents = "none";
      txt.textContent = textContent;
      svg.appendChild(txt);
    }

    // Marqueur "💨" pour cases en érosion fin de tour : signale que ma
    // densité non-soutenue va perdre 1 si je ne la consolide pas.
    if (options.mode === "play" && willErodeCoords.has(c.id)) {
      const dxOff = hexSize * 0.55;
      const dyOff = -hexSize * 0.55;
      const er = document.createElementNS(ns, "text");
      er.setAttribute("x", String(px + dxOff));
      er.setAttribute("y", String(py + dyOff + 4));
      er.setAttribute("text-anchor", "middle");
      er.setAttribute("font-size", String(Math.max(8, hexSize * 0.45)));
      er.setAttribute("fill", "#7a6a4a");
      er.style.pointerEvents = "none";
      er.textContent = "💨";
      const erTitle = document.createElementNS(ns, "title");
      erTitle.textContent = "Avancée isolée — perd 1 densité en fin de tour si vous ne consolidez pas.";
      er.appendChild(erTitle);
      svg.appendChild(er);
    }

    // Prévisualisation : badge "+X" ou "-X" sur les cases sélectionnées
    if (options.mode === "play" && selected.has(c.id) && options.projection && options.projection.mode !== "none") {
      const proj = options.projection;
      const myD = c.densities.get(state.sessionId ?? "") ?? 0;
      const projDelta = proj.mode === "add" ? proj.perCase : -proj.perCase;
      const projected = Math.max(0, Math.min(5, myD + projDelta));
      const badgeText = proj.mode === "add"
        ? `${myD}→${projected}`
        : `${myD}→${projected}`;
      const badgeColor = proj.mode === "add" ? "#2da77a" : "#c2575a";
      // Petit bandeau juste sous le centre de l'hex
      const bg = document.createElementNS(ns, "rect");
      const bw = hexSize * 1.2;
      const bh = hexSize * 0.45;
      bg.setAttribute("x", String(px - bw / 2));
      bg.setAttribute("y", String(py + hexSize * 0.35));
      bg.setAttribute("width", String(bw));
      bg.setAttribute("height", String(bh));
      bg.setAttribute("rx", "3");
      bg.setAttribute("fill", badgeColor);
      bg.setAttribute("opacity", "0.92");
      bg.style.pointerEvents = "none";
      svg.appendChild(bg);
      const ptxt = document.createElementNS(ns, "text");
      ptxt.setAttribute("x", String(px));
      ptxt.setAttribute("y", String(py + hexSize * 0.65));
      ptxt.setAttribute("text-anchor", "middle");
      ptxt.setAttribute("font-size", String(Math.max(8, hexSize * 0.32)));
      ptxt.setAttribute("font-weight", "700");
      ptxt.setAttribute("font-family", "IBM Plex Mono, monospace");
      ptxt.setAttribute("fill", "white");
      ptxt.style.pointerEvents = "none";
      ptxt.textContent = badgeText;
      svg.appendChild(ptxt);
    }
  }

  return svg;
}

/** Rend le viewport "placement" : grille avec candidats highlightés. */
// Signature du dernier rendu placement — évite le rebuild SVG complet quand
// rien de pertinent n'a changé pour cette phase.
let lastPlacementSig: string | null = null;

function renderPlacement() {
  const room = state.room;
  if (!room) return;
  const gridRadius = (room.state as any).gridRadius ?? 0;
  if (gridRadius <= 0) return;

  const cases = snapshotCases();
  const candidatesCsv = (room.state as any).placementCandidatesCsv ?? "";
  // Séparateur ";" car les ids contiennent déjà des "," (ex: "0,-4")
  const candidates = new Set<string>(candidatesCsv.split(";").filter(Boolean));

  // Signature : qui a placé, sur quelle case, et liste des candidats. Le
  // statut courant change quand un bot place — sans memo, le SVG était
  // rebuild à chaque placement bot → flicker bref de la grille.
  const playerSig: string[] = [];
  room.state.players.forEach((p: any) => {
    playerSig.push(`${p.id}:${p.hasPlacedCapital ? p.capitalCaseId : "_"}`);
  });
  const sig = `${candidatesCsv}#${playerSig.sort().join("|")}`;
  if (sig === lastPlacementSig) return;
  lastPlacementSig = sig;

  const container = $opt<HTMLElement>("placement-grid");
  if (container) {
    container.innerHTML = "";
    const svg = buildGridSvg(gridRadius, cases, {
      mode: "placement",
      candidateCases: candidates,
      onClick: (caseId) => {
        if (!state.room) return;
        state.room.send("placeCapital", { caseId });
      },
    });
    container.appendChild(svg);
  }

  // Liste de statut
  const list = $opt<HTMLElement>("placement-players-list");
  if (list) {
    list.innerHTML = "";
    room.state.players.forEach((p: any) => {
      const li = document.createElement("li");
      const dot = `<span class="player-dot" style="background: ${seatColor(p.seatIndex ?? 0)}; display: inline-block; width: 10px; height: 10px; border-radius: 50%; margin-right: 8px;"></span>`;
      li.innerHTML = `${dot}${p.isBot ? "🤖 " : ""}${escapeHtml(p.name)}${p.hasPlacedCapital ? " — ✅ placé" : " — en attente..."}`;
      li.style.padding = "6px 0";
      list.appendChild(li);
    });
  }

  const status = $opt<HTMLElement>("placement-status");
  if (status) {
    const me = state.sessionId ? room.state.players.get(state.sessionId) : null;
    if (me && (me as any).hasPlacedCapital) {
      status.textContent = "✅ Vous avez choisi votre capitale. En attente des autres délégués...";
    } else {
      status.textContent = "Cliquez sur l'un des sites candidats (jaunes) pour y établir votre siège diplomatique.";
    }
  }
}

function renderTerritory2D() {
  const room = state.room;
  if (!room) return;
  const containers = document.querySelectorAll<HTMLElement>(".territory-2d");
  if (containers.length === 0) return;

  // Mode grille : on rend la nouvelle grille de cases disputées
  const gridRadius = (room.state as any).gridRadius ?? 0;
  if (gridRadius > 0) {
    const cases = snapshotCases();
    const { detailed, presence } = getVisibleCases();
    const sel = state.pendingHexSelection;
    const selectedCases = sel ? new Set(sel.selected) : new Set<string>();
    const eligibleCases = sel ? computeEligibleCasesFor(sel.card) : new Set<string>();
    const projection = sel ? computeCardProjection(sel.card) : undefined;
    if (sel) {
      console.log("[sd] renderTerritory2D mode grille avec sélection en cours", {
        cardId: sel.card.id,
        selected: sel.selected,
        needed: sel.needed,
        eligibleCount: eligibleCases.size,
        detailedCount: detailed.size,
        casesCount: cases.length,
      });
    }
    // Diff entre les densités précédentes et actuelles pour déclencher les FX
    const diffs = computeCaseDiffs(cases);

    if (diffs.length > 0) {
      console.log("[sd] case diffs détectés", { count: diffs.length, diffs: diffs.map(d => ({ id: d.caseId, byPlayer: Array.from(d.byPlayer.entries()) })) });
    }

    // Dimensions du viewBox SVG, utilisées pour convertir coords hex → pourcentage
    const hexSize = Math.max(12, Math.min(28, 200 / gridRadius));
    const padding = hexSize * 2;
    const W = Math.ceil((gridRadius * 2 + 1) * hexSize * Math.sqrt(3) + padding * 2);
    const H = Math.ceil((gridRadius * 2 + 1) * hexSize * 1.5 + padding * 2);
    const cxView = W / 2;
    const cyView = H / 2;

    containers.forEach((c, idx) => {
      // Wrapper persistant : on garde le même <div class="grid-wrapper"> entre les
      // renders, et on ne remplace que le SVG de la grille à l'intérieur. La
      // couche FX (div HTML superposée) est conservée pour ne pas couper les animations.
      let wrapper = c.querySelector(":scope > .grid-wrapper") as HTMLDivElement | null;
      let fxLayer = c.querySelector(":scope > .grid-wrapper > .fx-layer") as HTMLDivElement | null;
      if (!wrapper) {
        c.innerHTML = "";
        wrapper = document.createElement("div");
        wrapper.className = "grid-wrapper";
        c.appendChild(wrapper);
        fxLayer = document.createElement("div");
        fxLayer.classList.add("fx-layer");
        wrapper.appendChild(fxLayer);
      }
      // (Le wrapper ne porte plus d'aspect-ratio inline : la correction du
      // letterbox SVG → fx-layer se fait plus bas en pixels mesurés via
      // getBoundingClientRect.)

      const svg = buildGridSvg(gridRadius, cases, {
        mode: "play",
        selectedCases,
        eligibleCases,
        detailed,
        presence,
        onClick: (caseId) => onGridCaseClick(caseId),
        projection,
      });
      svg.classList.add("grid-main");

      // Remplace l'ancien grid SVG mais garde la couche FX
      const oldGrid = wrapper.querySelector(":scope > .grid-main");
      if (oldGrid) wrapper.removeChild(oldGrid);
      wrapper.insertBefore(svg, fxLayer);

      // Injecte les FX dans le container VISIBLE qui a des diffs à montrer.
      // Auparavant on filtrait sur `idx === 0`, mais c'était fragile : en play
      // phase la carte de draft (.territory-2d-draft) reste dans le DOM en
      // display:none — selon l'ordre, idx=0 pouvait pointer sur la carte cachée
      // et la carte compacte (idx=1) visible ne recevait jamais ses nuages.
      // `offsetParent !== null` détecte qu'aucun ancêtre n'est en display:none.
      const isVisible = (c as HTMLElement).offsetParent !== null;
      if (isVisible && fxLayer && diffs.length > 0) {
        // Compense le letterbox du SVG dans le wrapper. Le SVG a viewBox W×H,
        // mais le wrapper (qui couvre la fx-layer) peut être plus large que
        // le SVG visible (preserveAspectRatio=meet centre + bandes vides).
        // On mesure les dims réelles pour mapper viewBox → fx-layer correctement.
        const wrapRect = wrapper.getBoundingClientRect();
        const wrapW = wrapRect.width || 1;
        const wrapH = wrapRect.height || 1;
        const contentRatio = W / H;
        const wrapRatio = wrapW / wrapH;
        // Si wrapper plus large que ratio → letterbox horizontal (bandes G/D)
        // Si wrapper plus haut que ratio → letterbox vertical (bandes haut/bas)
        let visibleW: number, visibleH: number, offsetX: number, offsetY: number;
        if (wrapRatio > contentRatio) {
          visibleH = wrapH;
          visibleW = wrapH * contentRatio;
          offsetX = (wrapW - visibleW) / 2;
          offsetY = 0;
        } else {
          visibleW = wrapW;
          visibleH = wrapW / contentRatio;
          offsetX = 0;
          offsetY = (wrapH - visibleH) / 2;
        }
        const myId = state.sessionId ?? "";
        for (const diff of diffs) {
          const { x: ox, y: oy } = axialToPixel(diff.q, diff.r, hexSize);
          // Position viewBox de la case
          const vbX = cxView + ox;
          const vbY = cyView + oy;
          // → pixels dans la zone visible du SVG → pixels dans le wrapper → %
          const pxX = offsetX + (vbX / W) * visibleW;
          const pxY = offsetY + (vbY / H) * visibleH;
          const pctX = (pxX / wrapW) * 100;
          const pctY = (pxY / wrapH) * 100;
          // Teinte selon contexte : vert si gain perso, rouge si perte, jaune si affrontement
          let netForMe = diff.byPlayer.get(myId) ?? 0;
          let totalPositive = 0;
          let totalNegative = 0;
          diff.byPlayer.forEach((delta) => {
            if (delta > 0) totalPositive += delta;
            else totalNegative += delta;
          });
          let tint = "rgba(120, 180, 90, 0.7)";
          let variant: "puff" | "fade" | "burst" = "puff";
          if (netForMe > 0) {
            tint = "rgba(120, 180, 90, 0.75)"; // vert : mes pets
            variant = "puff";
          } else if (netForMe < 0) {
            tint = "rgba(190, 80, 80, 0.7)"; // rouge : je perds
            variant = "fade";
          } else if (totalPositive > 0 && totalNegative < 0) {
            tint = "rgba(212, 160, 30, 0.75)"; // jaune : affrontement
            variant = "burst";
          } else if (totalPositive > 0) {
            tint = "rgba(155, 123, 196, 0.7)"; // lavande : pets adverses
            variant = "puff";
          } else {
            tint = "rgba(110, 110, 110, 0.6)"; // gris : dissipation
            variant = "fade";
          }
          injectFxCloud(fxLayer, pctX, pctY, variant, tint);
        }
      }
    });

    // Sauvegarde le snapshot pour le prochain diff
    saveCaseSnapshot(cases);
    return;
  }
  // Sinon : ancien rendu (joueurs en cercle)

  const hexesByOwner = new Map<string, Array<{ id: string; density: number }>>();
  room.state.hexes.forEach((h: any) => {
    const list = hexesByOwner.get(h.ownerId) ?? [];
    list.push({ id: h.id, density: h.density });
    hexesByOwner.set(h.ownerId, list);
  });

  // Cartes en chargement groupées par owner
  const chargesByOwner = new Map<string, Array<{ cardName: string; turnsRemaining: number }>>();
  room.state.chargingCards?.forEach((c: any) => {
    const list = chargesByOwner.get(c.ownerId) ?? [];
    list.push({ cardName: c.cardName, turnsRemaining: c.turnsRemaining });
    chargesByOwner.set(c.ownerId, list);
  });

  const isTargeting = Boolean(state.pendingTargetCard);

  // Voisins de siège visibles en permanence (Fédération Nordique)
  const freeIntelTargets = getFreeIntelTargetsForMe();

  const players = playersSnapshot();
  if (players.length === 0) {
    containers.forEach((c) => {
      c.innerHTML = '<p class="hint" style="padding: 20px;">Aucun joueur en jeu.</p>';
    });
    return;
  }

  const ns = "http://www.w3.org/2000/svg";
  // ViewBox plus serré : on ne perd plus 30% d'espace horizontal
  const W = 620;
  const H = 540;
  const cx = W / 2;
  const cy = H / 2;
  const orbitRadius = 170;
  const hexSize = 16;
  const slots = 8;

  const svg = document.createElementNS(ns, "svg") as SVGSVGElement;
  svg.setAttribute("viewBox", `0 0 ${W} ${H}`);
  // Largeur fluide ; la hauteur max est imposée par CSS via la variante .territory-2d / .territory-2d-compact
  svg.setAttribute("style", "width: 100%; height: auto; display: block;");

  // Event delegation : un seul listener pour tous les hex cliquables
  svg.addEventListener("click", (e) => {
    const target = e.target as Element | null;
    if (!target) return;
    // Remonte la hiérarchie pour trouver un élément avec data-hex-id
    let el: Element | null = target;
    while (el && el !== svg) {
      const hexId = el.getAttribute?.("data-hex-id");
      const selectable = el.getAttribute?.("data-selectable");
      if (hexId && selectable === "1") {
        onHexClick(hexId);
        return;
      }
      el = el.parentElement;
    }
  });

  // Defs : gradient pour le fond "globe"
  const defs = document.createElementNS(ns, "defs");
  const grad = document.createElementNS(ns, "radialGradient");
  grad.setAttribute("id", "globe-grad");
  grad.setAttribute("cx", "50%");
  grad.setAttribute("cy", "50%");
  const stop1 = document.createElementNS(ns, "stop");
  stop1.setAttribute("offset", "0%");
  stop1.setAttribute("stop-color", "#dfeaf3");
  const stop2 = document.createElementNS(ns, "stop");
  stop2.setAttribute("offset", "100%");
  stop2.setAttribute("stop-color", "#f5ecdc");
  grad.appendChild(stop1);
  grad.appendChild(stop2);
  defs.appendChild(grad);
  svg.appendChild(defs);

  // Fond circulaire (faux-globe)
  const bg = document.createElementNS(ns, "circle");
  bg.setAttribute("cx", String(cx));
  bg.setAttribute("cy", String(cy));
  bg.setAttribute("r", "260");
  bg.setAttribute("fill", "url(#globe-grad)");
  svg.appendChild(bg);

  // Léger anneau d'orbite (lignes pointillées)
  const orbit = document.createElementNS(ns, "circle");
  orbit.setAttribute("cx", String(cx));
  orbit.setAttribute("cy", String(cy));
  orbit.setAttribute("r", String(orbitRadius));
  orbit.setAttribute("fill", "none");
  orbit.setAttribute("stroke", "#d9cfbb");
  orbit.setAttribute("stroke-width", "1");
  orbit.setAttribute("stroke-dasharray", "3 5");
  orbit.setAttribute("opacity", "0.5");
  svg.appendChild(orbit);

  // Pour chaque joueur : son secteur + ses hex
  for (const p of players) {
    const seatAngle = (p.seatIndex / slots) * Math.PI * 2 - Math.PI / 2;
    const homeX = cx + orbitRadius * Math.cos(seatAngle);
    const homeY = cy + orbitRadius * Math.sin(seatAngle);
    const color = seatColor(p.seatIndex);
    const isMe = p.id === state.sessionId;

    const playerHexes = hexesByOwner.get(p.id) ?? [];
    // Tri par densité décroissante : les hex saturés au centre (visibilité)
    playerHexes.sort((a, b) => b.density - a.density);

    // Mes hex sont-ils sélectionnables (mode pendingHexSelection actif et je suis le joueur courant) ?
    const hexSel = state.pendingHexSelection;
    const isSelectionMode = Boolean(hexSel && isMe);

    // Fog of war : pour les adversaires sans intel "territory" actif, on n'affiche
    // qu'un voile flou avec le nombre d'hex. Sinon (moi, ou intel actif, ou voisin de siège Nordique), on affiche normalement.
    const intelEntry = isMe ? null : state.intel.get(p.id) ?? null;
    const hasTerritoryIntel = intelEntry?.type === "territory";
    const freeIntel = freeIntelTargets.has(p.id);
    const showDetailedHexes = isMe || hasTerritoryIntel || freeIntel;

    if (showDetailedHexes) {
      // Rendu détaillé : chaque hex avec sa densité
      for (let i = 0; i < playerHexes.length; i++) {
        const [q, r] = spiralAxial(i);
        const hx = homeX + hexSize * Math.sqrt(3) * (q + r / 2);
        const hy = homeY + hexSize * 1.5 * r;
        const ph = playerHexes[i]!;
        const isSelected = isSelectionMode && hexSel!.selected.includes(ph.id);
        drawHexagon(svg, hx, hy, hexSize, color, ph.density, isMe, ph.id, isSelectionMode, isSelected);
      }
    } else if (playerHexes.length > 0) {
      // Fog : un seul "nuage" flou + nombre d'hex
      drawFogCluster(svg, homeX, homeY, playerHexes.length, color, intelEntry);
    }

    // Aura "exposé" autour du home
    if (p.status === "expose") {
      const aura = document.createElementNS(ns, "circle");
      aura.setAttribute("cx", String(homeX));
      aura.setAttribute("cy", String(homeY));
      aura.setAttribute("r", "18");
      aura.setAttribute("fill", "none");
      aura.setAttribute("stroke", "#c54e3c");
      aura.setAttribute("stroke-width", "2.5");
      aura.setAttribute("stroke-dasharray", "4 3");
      aura.setAttribute("opacity", "0.85");
      svg.appendChild(aura);
    }

    // Halo de cible si le joueur est ciblable (carte en attente de cible)
    const isTargetable = isTargeting && !isMe && p.status !== "eliminated";
    if (isTargetable) {
      const halo = document.createElementNS(ns, "circle");
      halo.setAttribute("cx", String(homeX));
      halo.setAttribute("cy", String(homeY));
      halo.setAttribute("r", "20");
      halo.setAttribute("fill", "none");
      halo.setAttribute("stroke", "#d4b96f");
      halo.setAttribute("stroke-width", "3");
      halo.setAttribute("stroke-dasharray", "5 3");
      halo.setAttribute("opacity", "0.9");
      svg.appendChild(halo);
    }

    // Home marker (par-dessus)
    const homeCircle = document.createElementNS(ns, "circle");
    homeCircle.setAttribute("cx", String(homeX));
    homeCircle.setAttribute("cy", String(homeY));
    homeCircle.setAttribute("r", isMe ? "11" : "9");
    homeCircle.setAttribute("fill", p.status === "eliminated" ? "#999" : color);
    homeCircle.setAttribute("stroke", isMe ? "#2d2837" : (isTargetable ? "#d4b96f" : "white"));
    homeCircle.setAttribute("stroke-width", isMe || isTargetable ? "2.5" : "2");
    if (p.status === "vassal") {
      homeCircle.setAttribute("opacity", "0.5");
    }
    if (isTargetable) {
      homeCircle.style.cursor = "pointer";
    }

    // Click handler pour ciblage (toujours actif, ne fait rien si pas en mode targeting)
    const targetId = p.id;
    const isMyHome = isMe;
    homeCircle.addEventListener("click", () => {
      if (state.pendingTargetCard && !isMyHome && p.status !== "eliminated") {
        playCard(state.pendingTargetCard.instanceId, targetId);
        state.pendingTargetCard = null;
        renderPlay();
      }
    });

    // En mode sélection d'hex, le home ne doit pas masquer les clics du premier hex en dessous
    if (state.pendingHexSelection && isMe) {
      homeCircle.style.pointerEvents = "none";
    }

    svg.appendChild(homeCircle);

    // Indicateur de carte(s) en chargement au-dessus du home
    const playerCharges = chargesByOwner.get(p.id) ?? [];
    if (playerCharges.length > 0) {
      const minTurns = Math.min(...playerCharges.map((c) => c.turnsRemaining));
      // Fond du badge
      const badge = document.createElementNS(ns, "rect");
      const badgeY = homeY - 30;
      const badgeText = `📜 ${minTurns}t`;
      const badgeWidth = playerCharges.length > 1 ? 50 : 38;
      badge.setAttribute("x", String(homeX - badgeWidth / 2));
      badge.setAttribute("y", String(badgeY - 11));
      badge.setAttribute("width", String(badgeWidth));
      badge.setAttribute("height", "20");
      badge.setAttribute("rx", "10");
      badge.setAttribute("fill", color);
      badge.setAttribute("stroke", "white");
      badge.setAttribute("stroke-width", "1.5");
      svg.appendChild(badge);

      // Texte du badge
      const badgeLabel = document.createElementNS(ns, "text");
      badgeLabel.setAttribute("x", String(homeX));
      badgeLabel.setAttribute("y", String(badgeY + 4));
      badgeLabel.setAttribute("text-anchor", "middle");
      badgeLabel.setAttribute("font-size", "11");
      badgeLabel.setAttribute("font-weight", "700");
      badgeLabel.setAttribute("font-family", "IBM Plex Sans, sans-serif");
      badgeLabel.setAttribute("fill", "white");
      badgeLabel.textContent = playerCharges.length > 1 ? `📜×${playerCharges.length} ${minTurns}t` : badgeText;
      svg.appendChild(badgeLabel);

      // Tooltip via <title>
      const titleNode = document.createElementNS(ns, "title");
      titleNode.textContent = playerCharges.map((c) => `${c.cardName} (${c.turnsRemaining} tour${c.turnsRemaining > 1 ? "s" : ""})`).join("\n");
      badge.appendChild(titleNode);
    }

    // Label : nom du joueur
    // Position : à l'extérieur du home, dans la direction radiale
    const labelOffset = 28;
    const labelX = homeX + (Math.cos(seatAngle) * labelOffset);
    const labelY = homeY + (Math.sin(seatAngle) * labelOffset) + 4;

    const nameText = document.createElementNS(ns, "text");
    nameText.setAttribute("x", String(labelX));
    nameText.setAttribute("y", String(labelY));
    nameText.setAttribute("text-anchor", "middle");
    nameText.setAttribute("font-size", "12");
    nameText.setAttribute("font-weight", "700");
    nameText.setAttribute("font-family", "IBM Plex Sans, sans-serif");
    nameText.setAttribute("fill", "#2d2837");
    let label = p.name;
    if (p.isBot) label = "🤖 " + label;
    if (isMe) label += " (vous)";
    if (p.status === "vassal") label += " ⛓";
    if (p.status === "eliminated") label += " ✗";
    nameText.textContent = label;
    svg.appendChild(nameText);

    // Stats sous le nom : on masque D max si fog (sans intel)
    const statsText = document.createElementNS(ns, "text");
    statsText.setAttribute("x", String(labelX));
    statsText.setAttribute("y", String(labelY + 14));
    statsText.setAttribute("text-anchor", "middle");
    statsText.setAttribute("font-size", "10");
    statsText.setAttribute("font-family", "IBM Plex Mono, monospace");
    statsText.setAttribute("fill", "#5a5468");
    let densityLabel = "";
    if (showDetailedHexes) {
      densityLabel = `D${p.maxDensity}`;
    } else if (intelEntry?.type === "density" && intelEntry.data.maxDensity !== undefined) {
      densityLabel = `D≤${intelEntry.data.maxDensity}`;
    } else {
      densityLabel = "D?";
    }
    statsText.textContent = `${p.territorySize} hex · ${densityLabel}`;
    svg.appendChild(statsText);

    // Badge "intel actif" : petit indicateur près du nom si on a une intel sur ce joueur
    if ((intelEntry || freeIntel) && !isMe) {
      const intelBadge = document.createElementNS(ns, "text");
      intelBadge.setAttribute("x", String(labelX));
      intelBadge.setAttribute("y", String(labelY + 26));
      intelBadge.setAttribute("text-anchor", "middle");
      intelBadge.setAttribute("font-size", "9");
      intelBadge.setAttribute("font-weight", "600");
      intelBadge.setAttribute("font-family", "IBM Plex Mono, monospace");
      intelBadge.setAttribute("fill", "#7a4ba0");
      intelBadge.textContent = freeIntel && !intelEntry ? `🏛 voisin` : `📡 ${intelEntry!.turnsLeft}t`;
      svg.appendChild(intelBadge);
    }
  }

  // Insère le même SVG (cloné) dans tous les containers (draft + play)
  containers.forEach((c, idx) => {
    c.innerHTML = "";
    c.appendChild(idx === 0 ? svg : (svg.cloneNode(true) as SVGSVGElement));
  });
}

function renderMyStatus() {
  const room = state.room;
  if (!room || !state.sessionId) return;
  const me = room.state.players.get(state.sessionId);
  if (!me) return;

  const box = $("my-status");
  const winThreshold = room.state.winThreshold ?? 20;
  const winMode = room.state.winMode ?? "couverture";
  const territory = (me as any).territorySize ?? 0;
  const maxD = (me as any).maxDensity ?? 0;
  const handSize = (me as any).handSize ?? 0;
  const status = (me as any).status ?? "feutre";
  const isBot = Boolean((me as any).isBot);
  const seat = (me as any).seatIndex ?? 0;

  let badgeClass = "";
  let badgeText = "Feutré";
  if (status === "expose") { badgeClass = "badge-expose"; badgeText = "Exposé"; }
  else if (status === "vassal") { badgeClass = "badge-vassal"; badgeText = "Vassal"; }
  else if (status === "eliminated") { badgeClass = "badge-eliminated"; badgeText = "Éliminé"; }

  const progressPct = winMode === "couverture"
    ? Math.min(100, Math.round((territory / winThreshold) * 100))
    : 0;
  const targetText = winMode === "couverture"
    ? `<span class="target">/ ${winThreshold}</span>`
    : `<span class="target">(survie)</span>`;

  // Pays et modificateur de pays
  const countryId = (me as any).countryId ?? "";
  const country = countryId ? findCountry(countryId) : undefined;
  let countryBlock = "";
  if (country) {
    const counters: string[] = [];
    const roundShield = Number((me as any).roundShieldLeft ?? 0);
    const exposeSave = Number((me as any).exposeSaveLeft ?? 0);
    if (roundShield > 0) counters.push(`🌊 bouclier ×${roundShield}`);
    if (exposeSave > 0) counters.push(`🛡 save expose ×${exposeSave}`);
    countryBlock = `
      <div class="my-country">
        <div class="my-country-head">
          <span class="my-country-flag">🏛</span>
          <strong>${escapeHtml(country.name)}</strong>
          ${counters.length > 0 ? `<span class="my-country-counters">${counters.join(" · ")}</span>` : ""}
        </div>
        <div class="my-country-mod">${escapeHtml(country.modifierLabel)}</div>
        <div class="my-country-motto">« ${escapeHtml(country.motto)} »</div>
      </div>
    `;
  }

  // Modificateurs personnels actifs
  const disabledCsv = (me as any).disabledCategoriesCsv ?? "";
  const myDisabledCats = disabledCsv ? disabledCsv.split(",").filter(Boolean) : [];
  const immunityTurns = Number((me as any).immunityTurnsLeft ?? 0);
  const shieldTurns = Number((me as any).attackShieldTurnsLeft ?? 0);
  const pendingD = Number((me as any).pendingBuffDensity ?? 0);
  const pendingS = Number((me as any).pendingBuffSpread ?? 0);

  const modBadges: string[] = [];
  if (status === "expose") modBadges.push(`<span class="modbadge modbadge-expose">🔴 Exposé — vos cartes Densité et Extension sont neutralisées tant que vous l'êtes</span>`);
  if (status === "vassal") modBadges.push(`<span class="modbadge modbadge-vassal">⛓ Vassal — vos cartes ont leurs effets divisés par 2</span>`);
  if (immunityTurns > 0) modBadges.push(`<span class="modbadge modbadge-immunity">🫁 Apnée — immunisé ${immunityTurns}t</span>`);
  if (shieldTurns > 0) modBadges.push(`<span class="modbadge modbadge-shield">🛡 Démenti — bouclier ${shieldTurns}t</span>`);
  if (myDisabledCats.length > 0) {
    modBadges.push(`<span class="modbadge modbadge-confined">🔒 Confiné — bloqué sur : ${myDisabledCats.map((c: string) => categoryLabel(c)).join(", ")}</span>`);
  }
  if (pendingD !== 0 || pendingS !== 0) {
    modBadges.push(`<span class="modbadge modbadge-buff">✨ Pari Confiant — prochaine carte +${pendingD}D / +${pendingS}E</span>`);
  }

  const modBlock = modBadges.length > 0
    ? `<div class="my-modifiers">${modBadges.join("")}</div>`
    : "";

  box.innerHTML = `
    <div class="my-status-header">
      <div class="my-color-dot" style="background: ${seatColor(seat)}"></div>
      <div class="my-name">${isBot ? "🤖 " : ""}${escapeHtml(me.name)} (vous)</div>
      <div class="my-badge ${badgeClass}">${badgeText}</div>
    </div>
    ${countryBlock}
    <div class="my-stats">
      <div class="my-stat">
        <div class="my-stat-label">Territoire</div>
        <div class="my-stat-value">${territory} ${targetText}</div>
        <div class="my-progress-bar"><div class="my-progress-fill" style="width: ${progressPct}%"></div></div>
      </div>
      <div class="my-stat">
        <div class="my-stat-label">Densité max</div>
        <div class="my-stat-value">D ${maxD}</div>
      </div>
      <div class="my-stat">
        <div class="my-stat-label">Main</div>
        <div class="my-stat-value">${handSize}</div>
      </div>
    </div>
    ${modBlock}
  `;
}

// ============================================================ //
// Veille atmosphérique — avertissements proactifs
// ============================================================ //

interface Warning {
  severity: "info" | "warn" | "danger";
  icon: string;
  title: string;
  detail: string;
}

/**
 * Calcule les avertissements pertinents pour le joueur courant
 * en se basant sur l'état actuel de la partie (événement, charges,
 * progression des adversaires, effets temporaires sur soi).
 */
function computeWarnings(): Warning[] {
  const out: Warning[] = [];
  const room = state.room;
  if (!room || !state.sessionId) return out;
  if (room.state.phase !== "play") return out;
  const me = room.state.players.get(state.sessionId) as any;
  if (!me || me.status === "eliminated") return out;

  const threshold = Number(room.state.winThreshold ?? 20);
  const winMode = String(room.state.winMode ?? "couverture");
  const players = playersSnapshot().filter((p) => p.status !== "eliminated");

  // ---- 1. Win threshold imminent (couverture uniquement) ----
  if (winMode === "couverture") {
    const opps = players.filter((p) => p.id !== state.sessionId);
    for (const op of opps) {
      const remaining = threshold - op.territorySize;
      if (remaining <= 2 && remaining > 0) {
        out.push({
          severity: remaining === 1 ? "danger" : "warn",
          icon: "🏆",
          title: `${op.name} sur le point de gagner`,
          detail: `${op.territorySize}/${threshold} hex — il lui manque ${remaining} hex pour la victoire`,
        });
      }
    }
    const myRemaining = threshold - (me.territorySize ?? 0);
    if (myRemaining <= 2 && myRemaining > 0) {
      out.push({
        severity: "info",
        icon: "🥇",
        title: "Victoire à portée",
        detail: `${me.territorySize}/${threshold} hex — encore ${myRemaining} pour gagner`,
      });
    }
  }

  // ---- 2. Charging cards adverses ----
  const charges = (room.state.chargingCards ?? []) as any;
  charges.forEach((c: any) => {
    if (c.ownerId === state.sessionId) return;
    const owner = room.state.players.get(c.ownerId) as any;
    const turnsLeft = Number(c.turnsRemaining ?? 0);
    const isPetOriginel = c.cardId === "pet_originel";
    if (turnsLeft <= 1) {
      out.push({
        severity: "danger",
        icon: "📜",
        title: `${owner?.name ?? "?"} active "${c.cardName}" au prochain tour`,
        detail: isPetOriginel
          ? "Le Pet Originel : +5 hex en D5 partout (effet massif)"
          : "Effet de chargement imminent",
      });
    } else if (turnsLeft === 2) {
      out.push({
        severity: "warn",
        icon: "📜",
        title: `${owner?.name ?? "?"} charge "${c.cardName}"`,
        detail: `2 tours avant déclenchement${isPetOriginel ? " — Pet Originel, prépare-toi" : ""}`,
      });
    }
  });

  // ---- 3. Événement avec triggers per-turn dangereux pour moi ----
  const evtId = String(room.state.currentEventId ?? "");
  if (evtId && room.state.eventRevealed) {
    const evt = (eventsData as any[]).find((e) => e.id === evtId);
    if (evt) {
      // a) EXPOSE_PLAYER top_density frequency=each_turn
      const exposeTop = (evt.effects ?? []).find(
        (e: any) => e.type === "EXPOSE_PLAYER" && e.target === "top_density" && (e.frequency || e.trigger)
      );
      if (exposeTop && me.status !== "expose") {
        const sorted = [...players].sort((a, b) => b.maxDensity - a.maxDensity);
        const topD = sorted[0]?.maxDensity ?? 0;
        if (topD > 0 && (me.maxDensity ?? 0) === topD) {
          out.push({
            severity: "danger",
            icon: "🚨",
            title: "Vous risquez d'être exposé au prochain tour",
            detail: `${evt.name} expose le plus dense chaque tour. Vous êtes #1 (D${topD}).`,
          });
        }
      }

      // b) GLOBAL_DENSITY delta négatif (per-turn)
      const globalDNeg = (evt.effects ?? []).find(
        (e: any) => e.type === "GLOBAL_DENSITY" && Number(e.delta ?? 0) < 0
      );
      if (globalDNeg) {
        out.push({
          severity: "info",
          icon: "🌬",
          title: "Densité globale en baisse",
          detail: `${evt.name} : ${globalDNeg.delta} densité sur tous les hex chaque tour`,
        });
      }

      // c) REVEAL_HEX random per-turn (Détecteur Méthane)
      const revealRandom = (evt.effects ?? []).find(
        (e: any) => e.type === "REVEAL_HEX" && (e.target === "random" || e.target === "all") && e.frequency
      );
      if (revealRandom) {
        out.push({
          severity: "info",
          icon: "🛰",
          title: "Surveillance atmosphérique active",
          detail: `${evt.name} révèle un hex aléatoire chaque tour, son occupant est exposé`,
        });
      }
    }
  }

  // ---- 4. Mes buffs/debuffs qui expirent au prochain tour ----
  const immunityTurns = Number(me.immunityTurnsLeft ?? 0);
  if (immunityTurns === 1) {
    out.push({
      severity: "info",
      icon: "🫁",
      title: "Apnée expire après ce tour",
      detail: "Vous redeviendrez ciblable",
    });
  }
  const shieldTurns = Number(me.attackShieldTurnsLeft ?? 0);
  if (shieldTurns === 1) {
    out.push({
      severity: "info",
      icon: "🛡",
      title: "Démenti officiel expire après ce tour",
      detail: "Votre bouclier anti-attaque tombe",
    });
  }
  const roundShield = Number(me.roundShieldLeft ?? 0);
  if (roundShield > 0) {
    out.push({
      severity: "info",
      icon: "🌊",
      title: `Brise diplomatique disponible (×${roundShield})`,
      detail: "La prochaine attaque entrante sera annulée",
    });
  }

  // ---- 5. Mes catégories désactivées qui expirent ----
  const disabledCats = String(me.disabledCategoriesCsv ?? "")
    .split(",")
    .filter(Boolean);
  if (disabledCats.length > 0) {
    out.push({
      severity: "info",
      icon: "🔒",
      title: "Confinement expire à la fin du tour",
      detail: `Vos cartes ${disabledCats.map((c) => categoryLabel(c)).join(", ")} redeviendront jouables`,
    });
  }

  // ---- 6. Statut Exposé : rappel critique ----
  if (me.status === "expose") {
    out.push({
      severity: "warn",
      icon: "🔴",
      title: "Statut Exposé — neutralisation partielle",
      detail: "Vos cartes Densité et Extension sont neutralisées. Pose une Défense (Oups Désolé, Mea Culpa) pour t'auto-clarifier.",
    });
  }

  // Tri par sévérité : danger > warn > info
  const severityOrder: Record<Warning["severity"], number> = { danger: 0, warn: 1, info: 2 };
  out.sort((a, b) => severityOrder[a.severity] - severityOrder[b.severity]);

  return out;
}

function renderWarnings() {
  const panel = $opt<HTMLElement>("warnings-panel");
  const list = $opt<HTMLElement>("warnings-list");
  if (!panel || !list) return;

  const warnings = computeWarnings();
  if (warnings.length === 0) {
    panel.hidden = true;
    list.innerHTML = "";
    return;
  }

  panel.hidden = false;
  list.innerHTML = warnings
    .map(
      (w) => `
      <div class="warning warning-${w.severity}">
        <span class="warning-icon">${w.icon}</span>
        <div class="warning-body">
          <strong>${escapeHtml(w.title)}</strong>
          <span>${escapeHtml(w.detail)}</span>
        </div>
      </div>
    `
    )
    .join("");
}

// Signature du dernier rendu de la main du joueur — évite les rebuilds DOM
// inutiles (et donc le flicker des illustrations) quand un autre joueur agit
// mais que ma main personnelle n'a pas changé.
let lastHandSig: string | null = null;

function renderHand() {
  const meRaw = state.sessionId ? state.room?.state.players.get(state.sessionId) : null;
  const container = $("hand-container");

  const myCommitted = Boolean(meRaw && (meRaw as any).committedAction);
  const phase = state.room?.state.phase as string | undefined;
  const disabled = myCommitted || phase !== "play";

  // Signature : ids de cartes, état committed, phase, carte pending ciblage,
  // case pending sélection. Si rien de tout ça ne change, le DOM peut rester
  // identique. Sans memo, chaque onStateChange (déclenché par un bot qui joue,
  // un autre joueur qui draft, etc.) faisait rebuild complet de la main → les
  // <img> d'illustration se rechargeaient et tout clignotait.
  const pendingId = state.pendingTargetCard?.instanceId
    ?? state.pendingHexSelection?.card.instanceId
    ?? "";
  const handIds = state.hand.map((c) => c.instanceId).join("|");
  const sig = `${handIds}#${disabled ? "lock" : "open"}#${phase ?? ""}#p:${pendingId}`;

  // La position du carrousel ne fait PAS partie de la signature : elle évolue
  // en continu (molette, tween) et est appliquée par applyCarouselPositions
  // qui ne touche qu'aux styles inline et aux classes — pas de rebuild DOM.
  if (sig === lastHandSig) {
    // Rien de structurel n'a changé → on rafraîchit juste le ciblage et on
    // ré-applique la position (au cas où le tween était en cours d'écriture).
    renderTargetingPanel();
    applyCarouselPositions("hand-container");
    return;
  }
  lastHandSig = sig;

  container.innerHTML = "";

  if (state.hand.length === 0) {
    container.innerHTML = '<p class="draft-status">Vous n\'avez plus de cartes en main pour ce round.</p>';
    renderTargetingPanel();
    return;
  }

  // ── Carrousel "main de cartes" : composant générique partagé avec le pool
  // de draft. Position fractionnaire, molette continue, tween sur actions
  // discrètes (clavier, clic sur voisine).
  container.classList.add("card-carousel");

  // Clamp la position si la main a rétréci (carte jouée).
  const rt = getCarouselRuntime("hand-container");
  const maxPos = state.hand.length - 1;
  rt.position = Math.max(0, Math.min(maxPos, rt.position));

  const track = document.createElement("div");
  track.className = "card-carousel-track";

  state.hand.forEach((c, i) => {
    const isPending =
      state.pendingTargetCard?.instanceId === c.instanceId ||
      state.pendingHexSelection?.card.instanceId === c.instanceId;
    // Click handler : si on clique sur la carte la plus proche du centre,
    // on la joue ; sinon, on tween vers elle.
    const onClick = () => {
      const active = Math.round(rt.position);
      if (i === active) {
        onCardClick(c);
      } else {
        tweenCarouselTo("hand-container", i, state.hand.length - 1);
      }
    };
    const cardEl = createCardElement(c, disabled, isPending, onClick);
    cardEl.classList.add("card-carousel-card");
    cardEl.dataset.handIndex = String(i);
    track.appendChild(cardEl);
  });

  container.appendChild(track);

  if (state.hand.length > 1) {
    const indicator = document.createElement("div");
    indicator.className = "card-carousel-indicator";
    container.appendChild(indicator);
  }

  applyCarouselPositions("hand-container");

  renderTargetingPanel();
}

/**
 * Met à jour les transformations CSS de chaque carte du carrousel + la classe
 * `is-center` + l'indicateur, en fonction de la position fractionnaire stockée
 * dans `carouselRuntimes[containerId]`. Ne reconstruit jamais le DOM —
 * appelable à chaque frame d'animation sans rejouer les listeners ni
 * recharger les <img>.
 *
 * Composant générique : un même container DOM peut héberger n'importe quel
 * carrousel (main, pool draft, futur plateau) tant que les cartes ont la
 * classe `.card-carousel-card`.
 */
function applyCarouselPositions(containerId: string) {
  const container = $opt<HTMLElement>(containerId);
  if (!container) return;
  const rt = getCarouselRuntime(containerId);
  const cards = Array.from(container.querySelectorAll<HTMLElement>(".card-carousel-card"));
  if (cards.length === 0) return;
  const pos = rt.position;
  const active = Math.max(0, Math.min(cards.length - 1, Math.round(pos)));

  cards.forEach((cardEl, i) => {
    const dist = i - pos;
    const abs = Math.abs(dist);
    // Courbe translateX : linéaire 0..1, puis pente plus douce (110 px/cran)
    // au-delà. Aux points entiers : |d|=0 → 0, |d|=1 → 220, |d|=2 → 330,
    // |d|=3 → 440. Continue partout (pas de discontinuité à |d|=1).
    let tx: number;
    if (abs <= 1) {
      tx = dist * 220;
    } else {
      tx = (dist >= 0 ? 1 : -1) * (220 + (abs - 1) * 110);
    }
    const scale = Math.max(0.4, 1 - 0.22 * abs);
    const opacity = Math.max(0, 1 - 0.32 * abs);
    cardEl.style.setProperty("--tx", `${tx}px`);
    cardEl.style.setProperty("--scl", String(scale));
    cardEl.style.setProperty("--opa", String(opacity));
    cardEl.style.zIndex = String(100 - Math.floor(abs));
    cardEl.style.pointerEvents = opacity < 0.05 ? "none" : "auto";
    if (i === active) cardEl.classList.add("is-center");
    else cardEl.classList.remove("is-center");
  });

  const indicator = container.querySelector(".card-carousel-indicator") as HTMLElement | null;
  if (indicator) indicator.textContent = `${active + 1} / ${cards.length}`;
}

/**
 * Anime la position d'un carrousel depuis sa valeur courante vers `target`
 * (qui doit être un entier dans [0, maxIndex]) en ~220ms avec ease-out cubic.
 * Annule tout tween en cours sur ce carrousel.
 */
function tweenCarouselTo(containerId: string, target: number, maxIndex: number) {
  const rt = getCarouselRuntime(containerId);
  const start = rt.position;
  const end = Math.max(0, Math.min(maxIndex, target));
  if (Math.abs(start - end) < 0.001) return;
  if (rt.tweenRaf !== null) cancelAnimationFrame(rt.tweenRaf);
  const t0 = performance.now();
  const duration = 220;
  const step = (now: number) => {
    const t = Math.min(1, (now - t0) / duration);
    const e = 1 - Math.pow(1 - t, 3); // ease-out cubic
    rt.position = start + (end - start) * e;
    applyCarouselPositions(containerId);
    if (t < 1) {
      rt.tweenRaf = requestAnimationFrame(step);
    } else {
      rt.tweenRaf = null;
    }
  };
  rt.tweenRaf = requestAnimationFrame(step);
}

/**
 * Attache un handler de molette à `targetEl` qui pilote la position du
 * carrousel `containerId`. La sensibilité est adaptée au deltaMode du
 * navigateur. Annule un tween éventuellement en cours.
 *
 * `getMaxIndex` et `phaseGuard` sont des callbacks pour ne pas capturer
 * la longueur au moment de l'attache (les listes changent dans le temps).
 */
function attachCarouselWheel(
  targetEl: HTMLElement,
  containerId: string,
  getMaxIndex: () => number,
  phaseGuard: () => boolean,
) {
  targetEl.addEventListener("wheel", (e: WheelEvent) => {
    if (!phaseGuard()) return;
    const maxIdx = getMaxIndex();
    if (maxIdx < 1) return;
    const dy = e.deltaY;
    const dx = e.deltaX;
    const raw = Math.abs(dx) > Math.abs(dy) ? dx : dy;
    if (Math.abs(raw) < 0.5) return;
    e.preventDefault();
    const rt = getCarouselRuntime(containerId);
    if (rt.tweenRaf !== null) {
      cancelAnimationFrame(rt.tweenRaf);
      rt.tweenRaf = null;
    }
    let sensitivity = 0.006;
    if (e.deltaMode === 1) sensitivity = 0.12;
    else if (e.deltaMode === 2) sensitivity = 0.8;
    rt.position = Math.max(0, Math.min(maxIdx, rt.position + raw * sensitivity));
    applyCarouselPositions(containerId);
  }, { passive: false });
}

/**
 * Initialise le wheel handler du carrousel de main (attaché à la barre fixe
 * pour qu'il existe en permanence — le container `#hand-container` est
 * recréé à chaque renderHand).
 * Pendant la draft, la barre est masquée et le pool de draft prend le relais.
 */
function initHandCarouselWheel() {
  const bar = $opt<HTMLElement>("hand-fixed-bar");
  if (!bar) return;
  attachCarouselWheel(
    bar,
    "hand-container",
    () => state.hand.length - 1,
    () => state.room?.state.phase === "play" && state.hand.length > 1,
  );
}

/**
 * Affiche ou masque la barre fixe de main selon la phase et le contenu.
 * Met aussi à jour le compteur "8 cartes" du label minimisé.
 */
function updateHandFixedBarVisibility() {
  const bar = $opt<HTMLElement>("hand-fixed-bar");
  if (!bar) return;
  const phase = state.room?.state.phase;
  // Uniquement en phase play : pendant la draft, la shelf dédiée prend le relais
  // pour afficher les cartes déjà draftées. Avoir aussi la barre fixe créait
  // une redondance et empilait visuellement les zones de cartes.
  const shouldShow = phase === "play" && state.hand.length > 0;
  bar.hidden = !shouldShow;
  const counter = $opt<HTMLElement>("hand-bar-counter");
  if (counter) {
    counter.textContent = `${state.hand.length} carte${state.hand.length > 1 ? "s" : ""}`;
  }
}

/**
 * Toggle minimize/maximize de la barre fixe. Mémorisé dans sessionStorage
 * pour persister entre les renders (mais pas entre les sessions, pour
 * que chaque partie commence avec la main dépliée).
 */
function setHandBarMinimized(minimized: boolean) {
  const bar = $opt<HTMLElement>("hand-fixed-bar");
  if (!bar) return;
  bar.classList.toggle("minimized", minimized);
  document.body.classList.toggle("hand-bar-minimized", minimized);
  try {
    sessionStorage.setItem("sd.hand-bar-min", minimized ? "1" : "0");
  } catch { /* sessionStorage indisponible : on ignore */ }
}

function initHandFixedBar() {
  const toggle = $opt<HTMLButtonElement>("hand-bar-toggle");
  toggle?.addEventListener("click", () => {
    const bar = $opt<HTMLElement>("hand-fixed-bar");
    if (!bar) return;
    setHandBarMinimized(!bar.classList.contains("minimized"));
  });
  // Touche H : toggle minimize
  document.addEventListener("keydown", (e) => {
    if (e.key !== "h" && e.key !== "H") return;
    const target = e.target as HTMLElement | null;
    const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
    if (isTyping) return;
    const bar = $opt<HTMLElement>("hand-fixed-bar");
    if (!bar || bar.hidden) return;
    e.preventDefault();
    setHandBarMinimized(!bar.classList.contains("minimized"));
  });
  // Restaure l'état précédent
  try {
    const saved = sessionStorage.getItem("sd.hand-bar-min");
    if (saved === "1") setHandBarMinimized(true);
  } catch { /* ignore */ }
  // Active le scroll molette horizontal : c'est lié à la barre fixe (qui
  // existe dès le boot), pas au container du carrousel (recréé à chaque render).
  initHandCarouselWheel();
}

/**
 * Raccourci clavier pour le carrousel de main : ←/→ pour naviguer entre les
 * cartes quand on n'est pas en train de taper dans un champ.
 */
/**
 * Raccourcis clavier ←/→ qui naviguent dans le carrousel actif selon la phase :
 * - draft → pool draft
 * - play → main du joueur
 * (Si demain plusieurs carrousels coexistent en play, on routera via le focus DOM.)
 */
function initCarouselKeyboardNav() {
  document.addEventListener("keydown", (e) => {
    if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
    const target = e.target as HTMLElement | null;
    const isTyping = target?.tagName === "INPUT" || target?.tagName === "TEXTAREA" || target?.isContentEditable;
    if (isTyping) return;
    const phase = state.room?.state.phase;
    let containerId: string | null = null;
    let cards: any[] = [];
    if (phase === "play" && state.hand.length > 1) {
      containerId = "hand-container";
      cards = state.hand;
    } else if (phase === "draft" && state.draftPool.length > 1) {
      containerId = "draft-pool";
      cards = state.draftPool;
    }
    if (!containerId) return;
    e.preventDefault();
    const rt = getCarouselRuntime(containerId);
    const cur = Math.round(rt.position);
    const next = e.key === "ArrowLeft"
      ? Math.max(0, cur - 1)
      : Math.min(cards.length - 1, cur + 1);
    tweenCarouselTo(containerId, next, cards.length - 1);
  });
}

/**
 * Rend le panneau de ciblage dans la colonne droite (sticky), évitant le scroll.
 * Garde aussi l'ancien `#targeting-hint` caché (legacy).
 */
function renderTargetingPanel() {
  // Compatibilité ascendante : on garde #targeting-panel hidden (référencé
  // ailleurs dans le DOM, mais plus utilisé visuellement).
  const legacyPanel = $opt<HTMLElement>("targeting-panel");
  if (legacyPanel) legacyPanel.hidden = true;
  const legacyHint = $opt<HTMLElement>("targeting-hint");
  if (legacyHint) legacyHint.hidden = true;

  const sidebar = $opt<HTMLElement>("play-selection-panel");
  const cardSlot = $opt<HTMLElement>("play-selection-card");
  const targetingSlot = $opt<HTMLElement>("play-selection-targeting");
  const validateBtn = $opt<HTMLButtonElement>("play-selection-validate");
  if (!sidebar || !cardSlot || !targetingSlot || !validateBtn) return;

  const pendingCard = state.pendingHexSelection?.card ?? state.pendingTargetCard ?? null;
  if (!pendingCard) {
    sidebar.hidden = true;
    cardSlot.innerHTML = "";
    targetingSlot.hidden = true;
    targetingSlot.innerHTML = "";
    return;
  }

  // ── Affichage de la carte sélectionnée (1.3x via classe .draft-selection-card) ──
  sidebar.hidden = false;
  // Mémoïsation simple : on ne reconstruit la carte que si l'instance change,
  // sinon les <img> rechargeraient à chaque renderPlay (déclenché par chaque tick bot).
  const instanceId = pendingCard.instanceId;
  if (cardSlot.dataset.instanceId !== instanceId) {
    cardSlot.innerHTML = "";
    cardSlot.dataset.instanceId = instanceId;
    const cardEl = createCardElement(pendingCard, false, false, () => {});
    cardSlot.appendChild(cardEl);
  }

  // ── Slot ciblage : instructions selon le mode (hex / joueur) ──
  if (state.pendingHexSelection) {
    const sel = state.pendingHexSelection;
    targetingSlot.hidden = false;
    targetingSlot.innerHTML = "";

    // Instructions textuelles (spéciales pour Vent Tactique)
    const isMoveClouds = (sel.card.effects ?? []).some(
      (e: any) => String(e?.type ?? "") === "MOVE_CLOUDS"
    );
    const text = document.createElement("div");
    if (isMoveClouds) {
      if (sel.selected.length === 0) {
        text.innerHTML = `<strong>Étape 1/2 — Source :</strong> cliquez la case adverse <em>d'où</em> faire partir le nuage.`;
      } else if (sel.selected.length === 1) {
        text.innerHTML = `<strong>Étape 2/2 — Destination :</strong> cliquez la case voisine cible (re-cliquez la source pour la changer).`;
      } else {
        text.innerHTML = `Source et destination choisies. Validez ou re-cliquez une case pour modifier.`;
      }
    } else {
      text.textContent = `Cliquez ${sel.needed} case${sel.needed > 1 ? "s" : ""} éligible${sel.needed > 1 ? "s" : ""} sur la carte (re-cliquez pour retirer).`;
    }
    targetingSlot.appendChild(text);

    // Cases réellement sélectionnables : si le joueur en a MOINS que le spread
    // demandé (ex. 1 seule case en round 1 avec un buff pays +1 étendue), on ne
    // peut pas exiger l'impossible — le serveur répartit le surplus
    // automatiquement (auto-complétion) ou le signale comme perdu.
    const maxSelectable = maxSelectableCasesFor(sel);
    const partial = sel.selected.length > 0 && sel.selected.length < sel.needed;
    if (maxSelectable === 0 || partial || maxSelectable < sel.needed) {
      const note = document.createElement("div");
      note.className = "targeting-note";
      if (maxSelectable === 0) {
        note.textContent = isMoveClouds
          ? `Aucune cible à portée — cette carte n'aura aucun effet ce tour.`
          : `Aucune case éligible — la carte se placera automatiquement.`;
      } else if (isMoveClouds) {
        note.textContent = partial
          ? `Vous pouvez valider avec la seule source : la destination sera choisie automatiquement.`
          : `Aucune destination libre — le nuage ira sur une case voisine choisie automatiquement.`;
      } else {
        // Jouer "en portion" : on peut toujours valider ce qu'on a choisi,
        // le serveur répartit le reliquat sur les cases éligibles (ou le
        // concentre si le territoire est trop petit).
        const reste = sel.needed - sel.selected.length;
        note.textContent = `Vous pouvez valider dès maintenant : ${reste} point${reste > 1 ? "s" : ""} restant${reste > 1 ? "s" : ""} ${reste > 1 ? "seront placés" : "sera placé"} automatiquement sur vos cases.`;
      }
      targetingSlot.appendChild(note);
    }

    // Compteur N/M + progress dots
    const counter = document.createElement("div");
    counter.className = "targeting-counter";
    counter.textContent = `Sélectionnées : ${sel.selected.length}/${maxSelectable}`;
    targetingSlot.appendChild(counter);

    const progress = document.createElement("div");
    progress.className = "targeting-progress";
    for (let i = 0; i < maxSelectable; i++) {
      const dot = document.createElement("span");
      dot.className = `targeting-dot${i < sel.selected.length ? " filled" : ""}`;
      dot.textContent = String(i + 1);
      progress.appendChild(dot);
    }
    targetingSlot.appendChild(progress);

    // Activation du Valider : dès qu'AU MOINS une case est choisie, le joueur
    // peut jouer sa carte "en portion" — le serveur complète le reliquat.
    // (Et si rien n'est sélectionnable du tout, on débloque quand même.)
    const ready = sel.selected.length > 0 || maxSelectable === 0;
    validateBtn.disabled = !ready;
    validateBtn.textContent =
      sel.selected.length >= sel.needed || maxSelectable === 0
        ? "Valider"
        : `Valider (${sel.selected.length}/${sel.needed})`;
  } else if (state.pendingTargetCard) {
    targetingSlot.hidden = false;
    targetingSlot.innerHTML = "";
    const text = document.createElement("div");
    text.textContent = "Cliquez un adversaire dans la liste à gauche, ou validez pour cible automatique.";
    targetingSlot.appendChild(text);
    validateBtn.disabled = false;
    validateBtn.textContent = "Valider (cible auto)";
  } else {
    targetingSlot.hidden = true;
    targetingSlot.innerHTML = "";
    validateBtn.disabled = false;
    validateBtn.textContent = "Valider";
  }
}

/**
 * Wire-up des boutons Valider/Annuler de la sidebar de sélection en play.
 * À appeler une fois au boot (les boutons existent en permanence dans le DOM).
 */
function initPlaySelectionPanel() {
  const validateBtn = $opt<HTMLButtonElement>("play-selection-validate");
  const cancelBtn = $opt<HTMLButtonElement>("play-selection-cancel");
  validateBtn?.addEventListener("click", () => {
    if (state.pendingHexSelection) {
      const sel = state.pendingHexSelection;
      // Validation "en portion" : une seule case suffit (le serveur répartit
      // le reliquat). Rien n'est exigé si aucune case n'est sélectionnable.
      const maxSelectable = maxSelectableCasesFor(sel);
      if (sel.selected.length === 0 && maxSelectable > 0) return; // bouton disabled normalement
      playCardWithHexes(sel.card.instanceId, sel.selected);
      state.pendingHexSelection = null;
      renderPlay();
    } else if (state.pendingTargetCard) {
      playCard(state.pendingTargetCard.instanceId);
      state.pendingTargetCard = null;
      renderPlay();
    }
  });
  cancelBtn?.addEventListener("click", () => {
    if (state.pendingHexSelection) state.pendingHexSelection = null;
    if (state.pendingTargetCard) state.pendingTargetCard = null;
    renderPlay();
  });
}

// Signature du dernier rendu des opposants — mêmes raisons que renderHand.
let lastOpponentsSig: string | null = null;

function renderOpponents() {
  const room = state.room;
  if (!room) return;
  const grid = $("opponents-grid");
  // On filtre soi-même : le dashboard "Vous" en haut de colonne droite affiche
  // déjà les infos détaillées du joueur courant.
  const players = playersSnapshot().filter((p) => p.id !== state.sessionId);

  // Signature : empreinte de toutes les infos affichées + état targeting + intel.
  // Si rien ne change → pas de rebuild → pas de flicker quand un bot agit.
  const targeting = Boolean(state.pendingTargetCard);
  const intelKeys = Array.from(state.intel.keys()).sort().join(",");
  const sigParts = players.map((p) =>
    `${p.id}:${p.status}:${p.territorySize}:${p.maxDensity}:${p.handSize}:${p.committedAction ? 1 : 0}:${p.isDisconnected ? 1 : 0}:${p.isBot ? 1 : 0}`
  );
  const sig = sigParts.join("|") + `#t:${targeting ? 1 : 0}#i:${intelKeys}`;
  if (sig === lastOpponentsSig) return;
  lastOpponentsSig = sig;

  grid.innerHTML = "";
  const freeIntelTargets = getFreeIntelTargetsForMe();

  // Compteur d'engagements (committed) — affiché dans le compteur du context-bar.
  const totalActive = players.filter((p) => p.status !== "eliminated").length;
  const committedCount = players.filter((p) => p.committedAction && p.status !== "eliminated").length;
  const counterEl = $opt<HTMLElement>("play-committed-counter");
  if (counterEl) counterEl.textContent = `${committedCount}/${totalActive}`;

  for (const p of players) {
    // Pill compacte : dot + nom court + stats compactes + icône status.
    const pill = document.createElement("div");
    pill.className = "opponent-pill";
    if (p.status === "vassal") pill.classList.add("is-vassal");
    if (p.status === "eliminated") pill.classList.add("is-eliminated");
    if (p.status === "expose") pill.classList.add("is-expose");
    if (p.committedAction && p.status !== "eliminated") pill.classList.add("is-committed");
    if (targeting && p.status !== "eliminated") pill.classList.add("targetable");

    const dot = document.createElement("span");
    dot.className = "opponent-pill-dot";
    dot.style.background = seatColor(p.seatIndex);
    pill.appendChild(dot);

    // Nom court (premier mot + initiale du second si présent)
    const tokens = p.name.split(/\s+/);
    let shortName = tokens.slice(0, 2).join(" ");
    if (shortName.length > 18) shortName = shortName.slice(0, 17) + "…";
    const nameEl = document.createElement("span");
    nameEl.className = "opponent-pill-name";
    nameEl.textContent = (p.isBot ? "🤖 " : "") + shortName;
    pill.appendChild(nameEl);

    // Séparateur visuel
    const sep = document.createElement("span");
    sep.className = "opponent-pill-sep";
    sep.textContent = "·";
    pill.appendChild(sep);

    // Stats compactes : hex · D? · cartes
    const intelEntry = state.intel.get(p.id) ?? null;
    const freeIntel = freeIntelTargets.has(p.id);
    let densityStr: string;
    if (intelEntry?.type === "territory" || freeIntel) {
      densityStr = `D${p.maxDensity}`;
    } else if (intelEntry?.type === "density" && intelEntry.data.maxDensity !== undefined) {
      densityStr = `D≤${intelEntry.data.maxDensity}`;
    } else {
      densityStr = "D?";
    }
    const statsEl = document.createElement("span");
    statsEl.className = "opponent-pill-stats";
    statsEl.textContent = `${p.territorySize}h · ${densityStr} · ${p.handSize}c`;
    pill.appendChild(statsEl);

    // Icône statut à droite
    const iconEl = document.createElement("span");
    iconEl.className = "opponent-pill-icon";
    let iconText = "";
    if (p.status === "eliminated") iconText = "✗";
    else if (p.status === "vassal") iconText = "⛓";
    else if (p.status === "expose") iconText = "🔴";
    else if (p.isDisconnected) iconText = "📡";
    else if (p.committedAction) iconText = "✓";
    else iconText = "⏳";
    iconEl.textContent = iconText;
    pill.appendChild(iconEl);

    // Tooltip détaillé au survol (pays, intel ttl, etc.)
    const tooltipParts = [p.name];
    if (p.countryName) tooltipParts.push(`Pays : ${p.countryName}`);
    if (intelEntry) tooltipParts.push(`Intel : ${intelEntry.turnsLeft}t restant(s)`);
    else if (freeIntel) tooltipParts.push("Voisin diplomatique (visible)");
    if (p.status === "expose") tooltipParts.push("Exposé — densité et extension neutralisées");
    pill.title = tooltipParts.join(" · ");

    // Panel revealed-hand au survol : si une carte intel ou une fuite Wikileaks
    // a révélé la main de cet adversaire, on l'affiche en popup absolute sous
    // la pill quand le joueur la survole. Remplace l'ancien rendu dans la
    // colonne verticale Adversaires qui n'existe plus.
    const revealed = state.revealedHands.get(p.id) ?? null;
    if (revealed && revealed.cards.length > 0) {
      pill.classList.add("has-revealed-hand");
      const panel = document.createElement("div");
      panel.className = `opponent-pill-revealed revealed-hand-${revealed.source}`;
      const sourceLabel = revealed.source === "leak" ? "📰 Fuite" : "📡 Intel";
      const head = document.createElement("div");
      head.className = "opponent-pill-revealed-head";
      head.innerHTML = `<span class="opponent-pill-revealed-source">${sourceLabel}</span><span class="opponent-pill-revealed-meta">Main de ${escapeHtml(p.name)} · snapshot round ${revealed.revealedAtRound} · expire dans ${revealed.turnsLeft}t</span>`;
      panel.appendChild(head);
      const ul = document.createElement("ul");
      ul.className = "opponent-pill-revealed-list";
      for (const c of revealed.cards) {
        const li = document.createElement("li");
        li.className = `revealed-card cat-${c.category}`;
        li.innerHTML = `<span class="revealed-card-cat">${escapeHtml(categoryLabel(c.category))}</span><span class="revealed-card-name">${escapeHtml(c.name)}</span>`;
        ul.appendChild(li);
      }
      panel.appendChild(ul);
      pill.appendChild(panel);
    }

    if (targeting && p.status !== "eliminated" && state.pendingTargetCard) {
      const targetCard = state.pendingTargetCard;
      pill.addEventListener("click", () => {
        playCard(targetCard.instanceId, p.id);
        state.pendingTargetCard = null;
        renderPlay();
      });
    }
    grid.appendChild(pill);
  }
}

function renderCharging() {
  const room = state.room;
  if (!room) return;
  const section = $<HTMLElement>("charging-section");
  const list = $("charging-list");
  list.innerHTML = "";

  const charging = room.state.chargingCards;
  if (!charging || charging.length === 0) {
    section.hidden = true;
    return;
  }
  section.hidden = false;

  charging.forEach((c: any) => {
    const owner = room.state.players.get(c.ownerId);
    const block = document.createElement("div");
    block.className = "charging-card";

    const meta = document.createElement("div");
    meta.className = "charging-meta";
    const h4 = document.createElement("h4");
    h4.textContent = c.cardName;
    const p = document.createElement("p");
    p.textContent = `Posée par ${owner?.name ?? "—"} · intention publique`;
    meta.appendChild(h4);
    meta.appendChild(p);

    const cd = document.createElement("div");
    cd.className = "charging-countdown";
    cd.textContent = `${c.turnsRemaining} tour${c.turnsRemaining > 1 ? "s" : ""}`;

    block.appendChild(meta);
    block.appendChild(cd);
    list.appendChild(block);
  });
}

function renderGlobeUpdate() {
  const room = state.room;
  if (!room) return;
  requestAnimationFrame(() => {
    const g = ensureGlobe();
    if (!g) return;
    g.resize();
    const visualPlayers: PlayerVisualData[] = [];
    const hexesByOwner = new Map<string, Array<{ density: number }>>();
    room.state.hexes.forEach((h: any) => {
      const list = hexesByOwner.get(h.ownerId) ?? [];
      list.push({ density: h.density });
      hexesByOwner.set(h.ownerId, list);
    });
    room.state.players.forEach((p: any) => {
      visualPlayers.push({
        id: p.id,
        name: p.name,
        seatIndex: p.seatIndex ?? 0,
        status: p.status,
        territorySize: p.territorySize ?? 0,
        maxDensity: p.maxDensity ?? 0,
        hexes: hexesByOwner.get(p.id) ?? [],
      });
    });

    const visualCharges: ChargingVisualData[] = [];
    room.state.chargingCards?.forEach((c: any) => {
      visualCharges.push({
        id: c.id,
        ownerId: c.ownerId,
        cardName: c.cardName,
        turnsRemaining: c.turnsRemaining,
      });
    });

    g.update(visualPlayers, visualCharges);
  });
}

// ============================================================ //
// Render: GameEnd                                                //
// ============================================================ //

function renderGameEnd() {
  const room = state.room;
  if (!room) return;
  const winner = room.state.winnerId ? room.state.players.get(room.state.winnerId) : null;

  $("winner-title").textContent = winner ? `${winner.name} l'emporte` : "Partie terminée";
  $("winner-subtitle").textContent = room.state.winReason || "—";

  const grid = $("stats-grid");
  grid.innerHTML = "";
  const players = playersSnapshot()
    .filter((p) => p.status !== "eliminated")
    .sort((a, b) => b.territorySize - a.territorySize)
    .slice(0, 3);
  const medals = ["🥇", "🥈", "🥉"];

  players.forEach((p, i) => {
    const card = document.createElement("div");
    card.className = "stat-card";
    const medal = document.createElement("div");
    medal.className = "stat-medal";
    medal.textContent = medals[i] ?? "—";
    const name = document.createElement("div");
    name.className = "stat-name";
    name.textContent = p.name;
    const details = document.createElement("div");
    details.className = "stat-details";
    details.innerHTML = `${p.territorySize} hex<br>D max ${p.maxDensity}<br>${p.status === "vassal" ? "vassal" : "libre"}`;
    card.appendChild(medal);
    card.appendChild(name);
    card.appendChild(details);
    grid.appendChild(card);
  });
}

// ============================================================ //
// Round summary overlay                                          //
// ============================================================ //

interface RoundSummaryStanding {
  id: string;
  name: string;
  seatIndex: number;
  status: string;
  territorySize: number;
  maxDensity: number;
  isBot: boolean;
}

interface RoundSummaryPayload {
  round: number;
  winThreshold: number;
  winMode: "couverture" | "survie";
  eventName?: string;
  eventFlavor?: string;
  standings: RoundSummaryStanding[];
  chargingCardsActive: number;
}

let roundSummaryTimer: number | null = null;

function showRoundSummary(summary: RoundSummaryPayload) {
  const overlay = $opt<HTMLElement>("round-summary");
  if (!overlay) return;

  const room = state.room;
  const selfId = state.sessionId;

  const title = $opt<HTMLElement>("round-summary-title");
  if (title) title.textContent = `Bilan du round ${summary.round}`;

  const subtitle = $opt<HTMLElement>("round-summary-subtitle");
  if (subtitle) {
    const modeLabel = summary.winMode === "couverture"
      ? `Objectif : ${summary.winThreshold} hex`
      : "Mode survie";
    subtitle.textContent = modeLabel;
  }

  const eventBox = $opt<HTMLElement>("round-summary-event");
  if (eventBox) {
    if (summary.eventName) {
      eventBox.hidden = false;
      eventBox.innerHTML = `<span class="evt-label">Événement</span><span class="evt-name">${summary.eventName}</span>${summary.eventFlavor ? `<span class="evt-flavor">${summary.eventFlavor}</span>` : ""}`;
    } else {
      eventBox.hidden = true;
      eventBox.innerHTML = "";
    }
  }

  const list = $opt<HTMLElement>("round-summary-standings");
  if (list) {
    list.innerHTML = "";
    summary.standings.forEach((p, i) => {
      const row = document.createElement("div");
      row.className = `summary-row${p.id === selfId ? " self" : ""}${i === 0 ? " leader" : ""}`;

      const rank = document.createElement("div");
      rank.className = "summary-rank";
      rank.textContent = i === 0 ? "1er" : `${i + 1}e`;
      row.appendChild(rank);

      const name = document.createElement("div");
      name.className = "summary-name";
      name.textContent = `${p.name}${p.isBot ? " 🤖" : ""}`;
      if (p.status === "vassal") name.textContent += " (vassal)";
      if (p.status === "expose") name.textContent += " (exposé)";
      row.appendChild(name);

      const territory = document.createElement("div");
      territory.className = "summary-territory";
      const progress = Math.min(100, Math.round((p.territorySize / Math.max(1, summary.winThreshold)) * 100));
      territory.innerHTML = `
        <div class="summary-bar-wrap">
          <div class="summary-bar" style="width:${progress}%"></div>
        </div>
        <div class="summary-bar-label">${p.territorySize} / ${summary.winThreshold} hex</div>
      `;
      row.appendChild(territory);

      const density = document.createElement("div");
      density.className = "summary-density";
      density.textContent = `D max ${p.maxDensity}`;
      row.appendChild(density);

      list.appendChild(row);
    });
  }

  const footer = $opt<HTMLElement>("round-summary-footer");
  if (footer) {
    const charging = summary.chargingCardsActive;
    footer.textContent = charging > 0
      ? `${charging} carte${charging > 1 ? "s" : ""} en charge — encore actives au prochain round.`
      : "Aucune carte en charge.";
  }

  // Pretty animated dismiss countdown
  const close = $opt<HTMLElement>("round-summary-close");
  if (close) close.textContent = "Prochain round dans 6 s…";
  let remaining = 6;
  if (roundSummaryTimer !== null) {
    window.clearInterval(roundSummaryTimer);
    roundSummaryTimer = null;
  }
  roundSummaryTimer = window.setInterval(() => {
    remaining -= 1;
    if (close) close.textContent = remaining > 0
      ? `Prochain round dans ${remaining} s…`
      : "Reprise…";
    if (remaining <= 0) {
      if (roundSummaryTimer !== null) {
        window.clearInterval(roundSummaryTimer);
        roundSummaryTimer = null;
      }
      hideRoundSummary();
    }
  }, 1000);

  overlay.hidden = false;
  overlay.classList.add("visible");

  // Use `room` to avoid unused warning if it's null
  void room;
}

function hideRoundSummary() {
  const overlay = $opt<HTMLElement>("round-summary");
  if (!overlay) return;
  overlay.classList.remove("visible");
  overlay.hidden = true;
  if (roundSummaryTimer !== null) {
    window.clearInterval(roundSummaryTimer);
    roundSummaryTimer = null;
  }
}

// ============================================================ //
// Card element factory                                           //
// ============================================================ //

/**
 * Convertit un effet atomique en phrase courte et lisible.
 * Renvoie null si l'effet est trop méta pour mériter une ligne.
 */
function describeEffect(eff: Record<string, any>, cardCategory?: string): string | null {
  const t = String(eff.type ?? "");
  const tgt = String(eff.target ?? "");
  const cat = String(eff.category ?? "");
  // Bonus pays — appliqué uniquement si on connaît la catégorie de la carte
  // (donc pour les effets des cartes, pas pour ceux des événements).
  // deltaDensity buffe la densité (ADD_HEX.density / MODIFY_DENSITY.delta>0),
  // deltaSpread buffe l'étendue (ADD_HEX.spread / MODIFY_DENSITY.spread).
  const { deltaDensity, deltaSpread } = cardCategory
    ? getMyCountryDeltasFor(cardCategory)
    : { deltaDensity: 0, deltaSpread: 0 };
  const hasCountryBuff = deltaDensity > 0 || deltaSpread > 0;
  switch (t) {
    case "ADD_HEX": {
      const baseD = Number(eff.density ?? 1);
      const baseS = Number(eff.spread ?? 1);
      const d = Math.max(1, baseD + deltaDensity);
      const s = Math.max(1, baseS + deltaSpread);
      const buffTag = hasCountryBuff ? " ★" : "";
      const delay = eff.delay ? ` (délai ${eff.delay})` : "";
      const persistent = eff.persistent ? ` (persiste ${eff.persistent}t)` : "";
      const range = eff.range ? ` (portée ${eff.range})` : "";
      const fail = eff.failChance ? ` — échec ${Math.round(Number(eff.failChance) * 100)}%` : "";
      return `+${s} hex en D${d}${buffTag}${range}${delay}${persistent}${fail}`;
    }
    case "MODIFY_DENSITY": {
      const baseDelta = Number(eff.delta ?? 0);
      // Bonus densité uniquement sur les delta positifs (renforcement).
      const delta = baseDelta > 0 ? baseDelta + deltaDensity : baseDelta;
      const baseSpread = Number(eff.spread ?? 1);
      const spread = Math.max(1, baseSpread + deltaSpread);
      const buffTag = hasCountryBuff ? " ★" : "";
      const sign = delta >= 0 ? "+" : "";
      const persistent = eff.persistent ? ` (persiste ${eff.persistent}t)` : "";
      const inev = eff.inevitable ? " (inévitable)" : "";
      const where = tgt === "own_hex_adjacent" ? "vos hex adjacents" : "vos hex";
      return `${sign}${delta} densité sur ${spread} ${where}${buffTag}${persistent}${inev}`;
    }
    case "EXPOSE_PLAYER": {
      if (tgt === "self") return "vous passez Exposé";
      if (tgt === "self_clear") return "annule votre statut Exposé";
      if (tgt === "opponent_with_density") {
        const min = eff.minDensity !== undefined ? ` (D≥${eff.minDensity})` : "";
        const back = eff.backfire ? " — retour de flamme possible" : "";
        return `Expose un adversaire${min}${back}`;
      }
      if (tgt === "adjacent_neighbor_or_npc") {
        // Mécanique de déflection (C'est pas moi, c'est le chien) : le marker
        // `from: "self"` signifie "transfère MON statut Exposé". Si non précisé,
        // la carte expose juste un voisin sans toucher au statut de l'acteur.
        if (eff.from === "self") {
          return "transfère votre Exposé à un voisin (ou à un PNJ) — si vous n'êtes pas Exposé, le voisin l'est quand même, à votre place";
        }
        return "Expose un voisin (ou un PNJ)";
      }
      // Variante événement : top_density = le plus gros producteur du moment
      if (tgt === "top_density") {
        if (eff.frequency || eff.trigger) {
          const freq = String(eff.frequency ?? eff.trigger);
          const freqFr = freq === "each_turn" || freq === "start_of_turn" || freq === "end_of_turn"
            ? "à chaque tour"
            : freq;
          return `Le délégué le plus dense passe Exposé ${freqFr}`;
        }
        return `Le délégué le plus dense passe Exposé`;
      }
      return `Expose la cible`;
    }
    case "LOSE_HEX": {
      if (tgt === "self") return "vous perdez 1 hex";
      if (tgt === "last_exposed") return "le dernier exposé perd 1 hex";
      return "perte d'1 hex";
    }
    case "REVEAL_HAND": {
      const c = eff.count ? ` (${eff.count})` : "";
      const expose = eff.exposeIfRiskAtLeast ? ` ; expose si ≥${eff.exposeIfRiskAtLeast} Risque` : "";
      return `Révèle la main de la cible${c}${expose}`;
    }
    case "REVEAL_HEX": {
      const turns = eff.turns ?? 3;
      if (tgt === "all") return `Révèle le territoire de TOUS les adversaires (${turns}t)`;
      return `Révèle le territoire de la cible (${turns}t)`;
    }
    case "REVEAL_DENSITY_MAX": {
      const turns = eff.turns ?? 3;
      return `Imagerie thermique : révèle D max de la cible (${turns}t)`;
    }
    case "MOVE_CLOUDS": {
      const dist = eff.distance ?? 1;
      // Variante événement : déclenchement périodique sur tous les hex
      if (eff.trigger || eff.frequency) {
        const when = String(eff.trigger ?? eff.frequency);
        const whenFr = when === "start_of_turn" ? "début de tour" : when === "end_of_turn" ? "fin de tour" : when;
        return `Les nuages dérivent de ${dist} hex à chaque ${whenFr}`;
      }
      return `Déplace un nuage de la cible (${dist} hex)`;
    }
    case "GRANT_IMMUNITY": {
      const dur = eff.duration ?? 1;
      return `Immunisé contre les attaques pendant ${dur} tour${dur > 1 ? "s" : ""}`;
    }
    case "BUFF_NEXT_CARD": {
      const dd = Number(eff.deltaDensity ?? 0);
      const ds = Number(eff.deltaSpread ?? 0);
      const fail = eff.failChance ? ` — échec ${Math.round(Number(eff.failChance) * 100)}%` : "";
      return `Prochaine carte +${dd}D / +${ds}E${fail}`;
    }
    case "DISABLE_CARD_TYPE": {
      const dur = eff.duration ?? 1;
      if (tgt === "incoming_attack" || tgt === "self") {
        return `Bouclier anti-attaque pendant ${dur} tour${dur > 1 ? "s" : ""}`;
      }
      if (tgt === "opponent") {
        const cats = Array.isArray(eff.categories) ? eff.categories.join(", ") : "certaines catégories";
        return `Confine un adversaire : bloque ${cats} pendant ${dur}t`;
      }
      // Variante événement : désactive toute une catégorie de cartes pour le round
      if (cat) {
        return `Toutes les cartes ${categoryLabel(cat)} sont désactivées ce round`;
      }
      return `Désactive une catégorie de carte`;
    }
    case "REFLECT_LAST_EFFECT":
      return `Reflète la dernière action de la cible contre elle`;
    case "CANCEL_PERSISTENT":
      return `Sabote une carte en chargement chez la cible`;
    case "REDIRECT_TARGET":
      return `Détourne la prochaine attaque vers un autre adversaire`;
    case "CHARGE_CARD": {
      const dur = eff.duration ?? 2;
      const payload = eff.payload as Record<string, any> | undefined;
      let payloadDesc = "puis déclenche un effet massif";
      if (payload?.type === "ADD_HEX") {
        const d = Number(payload.density ?? 1);
        const s = Number(payload.spread ?? 1);
        payloadDesc = `puis pose ${s} hex en D${d}`;
      }
      return `Se charge ${dur}t — ${payloadDesc}`;
    }

    // ===== Effets exclusifs aux événements globaux =====
    case "BUFF_CARD_TYPE": {
      const dd = Number(eff.deltaDensity ?? 0);
      const ds = Number(eff.deltaSpread ?? 0);
      const dDelay = Number(eff.deltaDelay ?? 0);
      const parts: string[] = [];
      if (dd) parts.push(`densité ${dd > 0 ? "+" : ""}${dd}`);
      if (ds) parts.push(`portée ${ds > 0 ? "+" : ""}${ds}`);
      if (dDelay) parts.push(`délai ${dDelay > 0 ? "+" : ""}${dDelay}`);
      const catLabel = cat ? categoryLabel(cat) : "toutes les cartes";
      return `Cartes ${catLabel} : ${parts.join(", ") || "buff"}`;
    }
    case "MODIFY_FAILURE_RATE": {
      const dr = Number(eff.deltaRate ?? 0);
      const pct = Math.round(dr * 100);
      const catLabel = cat ? categoryLabel(cat) : "toutes les cartes";
      return `Cartes ${catLabel} : taux d'échec ${pct > 0 ? "+" : ""}${pct}%`;
    }
    case "MODIFY_DENSITY_CAP": {
      const newCap = Number(eff.cap ?? 5);
      return `Plafond de densité abaissé à D${newCap} (au lieu de D5)`;
    }
    case "GLOBAL_DENSITY": {
      const delta = Number(eff.delta ?? 1);
      const trig = String(eff.trigger ?? eff.frequency ?? "end_of_turn");
      const trigFr = trig === "start_of_turn" ? "début de tour" : trig === "end_of_turn" ? "fin de tour" : trig;
      const sign = delta >= 0 ? "+" : "";
      return `${sign}${delta} densité sur toutes les cases occupées à chaque ${trigFr}`;
    }
    case "GLOBAL_DENSITY_DIVIDE":
      return `Toutes les densités sont divisées par 2`;
    case "DENSITY_TAX": {
      const tax = Number(eff.amount ?? 1);
      return `Chaque délégué perd ${tax} densité (taxe atmosphérique)`;
    }
    case "LOSE_HEX_ALL": {
      const n = Number(eff.count ?? 1);
      return `Chaque délégué perd ${n} hex`;
    }
    case "DISCARD_CARDS": {
      const n = Number(eff.count ?? 1);
      return `Chaque délégué défausse ${n} carte${n > 1 ? "s" : ""} au hasard`;
    }
    case "DOUBLE_DECK_DRAFT":
      return `Prochain draft : pool doublé`;
    case "FORCE_RANDOM_PLAY":
      return `Toutes les cartes sont jouées au hasard ce round`;
    case "FREE_VASSAL":
      return `Un délégué vassal est libéré de sa tutelle`;
    case "INCREMENT_DELAY":
      return `Tous les délais (cartes en chargement) sont rallongés de 1 tour`;
    case "NO_CLEANUP":
      return `Aucun nettoyage atmosphérique possible ce round`;
    case "REVEAL_ALL_FACE_DOWN":
      return `Les cartes engagées sont révélées AVANT résolution (au lieu de simultanément)`;
    case "REVEAL_ALL_HANDS":
      return `Toutes les mains des délégués sont rendues publiques`;

    default:
      return null; // effet trop technique pour être affiché
  }
}

function describeEffects(
  effects: Array<Record<string, any>> | undefined,
  cardCategory?: string
): string[] {
  if (!effects || effects.length === 0) return [];
  const lines: string[] = [];
  for (const eff of effects) {
    const line = describeEffect(eff, cardCategory);
    if (line) lines.push(line);
  }
  return lines;
}

function createCardElement(c: ClientCard, disabled: boolean, selected: boolean, onClick: () => void): HTMLElement {
  const div = document.createElement("div");
  // Détecte si la carte est désactivée par règle (événement ou Confinement)
  const ruleDisabled = isCategoryRuleDisabled(c.category);
  div.className = `card ${c.category}${disabled ? " disabled" : ""}${selected ? " selected-for-target" : ""}${ruleDisabled.disabled ? " rule-disabled" : ""}`;
  div.dataset.instanceId = c.instanceId;
  if (ruleDisabled.disabled) {
    div.dataset.disableReason = ruleDisabled.reason;
    div.dataset.disabledReason = ruleDisabled.label; // tooltip "pourquoi désactivée"
    // Pas de `div.title = ...` ici : le tooltip natif HTML s'affichait
    // EN PLUS du tooltip custom (#disabled-card-tooltip) → doublon visuel.
    // Le custom suffit (il est plus stylé et démarre instantanément).
  } else if (disabled) {
    // Cause générique : action déjà engagée, ou phase autre que play. Le tooltip
    // donne une explication courte au joueur — important pour les nouveaux qui
    // se demandent "pourquoi je peux pas jouer ?".
    const me = state.room?.state.players.get(state.sessionId ?? "") as any;
    const phase = state.room?.state.phase as string | undefined;
    let label: string;
    if (me?.committedAction) {
      label = "Vous avez déjà engagé une carte ce tour. Patientez jusqu'à la résolution.";
    } else if (phase !== "play") {
      label = "Cette carte ne peut s'engager qu'en phase de jeu.";
    } else {
      label = "Cette carte n'est pas jouable actuellement.";
    }
    div.dataset.disabledReason = label;
  }

  // Illustration de la carte (optionnelle). Si l'image n'existe pas dans
  // /public/illustrations/{cardId}.{ext}, le navigateur émet un 404 et le
  // background-image reste vide — pas de bordure cassée. Le CSS masque la
  // zone dans le mode compact où il n'y a pas de place.
  // L'extension est tentée dans l'ordre webp → png → jpg via une <picture>
  // (mais on simplifie en utilisant <img> avec onError pour cascader).
  const illu = document.createElement("div");
  illu.className = "card-illustration";
  const img = document.createElement("img");
  img.alt = "";
  img.loading = "lazy";
  // Marqueur "has-illustration" posé sur la CARTE quand l'image se charge
  // vraiment. Permet au CSS de masquer le bandeau coloré du haut (::before)
  // en présence d'une vraie illustration — la bande de catégorie passe sous
  // l'image à la place, pour un look intégré et non "sticker collé".
  img.onload = () => {
    illu.classList.add("loaded");
    div.classList.add("has-illustration");
  };
  // Cascade d'extensions : si webp échoue, on retombe sur png puis jpg, et
  // si tout échoue on garde le slot mais on marque la carte comme étant
  // sans illustration — le CSS affiche un placeholder (sceau CM en filigrane
  // sur fond pastel de catégorie) pour que toutes les cartes aient la même
  // structure et hauteur. Évite les cartes "tronquées" qui cassaient la grille.
  //
  // IMPORTANT : à l'épuisement de la cascade, on coupe les callbacks onload/
  // onerror pour éviter une race condition où une réponse réseau tardive
  // (cache navigateur, par exemple) déclencherait onload APRÈS qu'on ait
  // déjà déclaré la carte "sans illustration", aboutissant à un cumul des
  // deux classes `no-illustration` + `has-illustration` (placeholder + image
  // superposés visuellement).
  const tryNext = (i: number) => {
    const exts = ["webp", "png", "jpg"];
    if (i >= exts.length) {
      img.onload = null;
      img.onerror = null;
      img.style.display = "none";
      div.classList.add("no-illustration");
      return;
    }
    img.onerror = () => tryNext(i + 1);
    img.src = `/illustrations/${c.id}.${exts[i]}`;
  };
  tryNext(0);
  illu.appendChild(img);
  div.appendChild(illu);

  const cat = document.createElement("div");
  cat.className = "card-category";
  cat.textContent = categoryLabel(c.category);
  div.appendChild(cat);

  const name = document.createElement("h3");
  name.className = "card-name";
  name.textContent = c.name;
  div.appendChild(name);

  if (c.stats && (c.stats.density !== undefined || c.stats.spread !== undefined)) {
    const stats = document.createElement("p");
    stats.className = "card-stats";
    const parts: string[] = [];
    if (c.stats.density !== undefined) parts.push(`D ${c.stats.density}`);
    if (c.stats.spread !== undefined) parts.push(`E ${c.stats.spread}`);
    stats.textContent = parts.join(" · ");
    div.appendChild(stats);
  }

  if (Array.isArray(c.keywords) && c.keywords.length > 0) {
    const kw = document.createElement("p");
    kw.className = "card-keywords";
    kw.textContent = c.keywords
      .map((k) => (typeof k === "string" ? k : (k as any)?.type ? `${(k as any).type} ${(k as any).value ?? ""}` : ""))
      .filter(Boolean)
      .join(" · ");
    div.appendChild(kw);
  }

  // Description lisible des effets (la mécanique réelle) — on passe la catégorie
  // pour que le bonus pays (ex. Vent Continu : +1 étendue Densité) soit visible.
  const effectLines = describeEffects(c.effects, c.category);
  if (effectLines.length > 0) {
    const effectsEl = document.createElement("ul");
    effectsEl.className = "card-effects";
    for (const line of effectLines) {
      const li = document.createElement("li");
      li.textContent = line;
      effectsEl.appendChild(li);
    }
    div.appendChild(effectsEl);
  }

  const flavor = document.createElement("p");
  flavor.className = "card-flavor";
  flavor.textContent = c.flavor;
  div.appendChild(flavor);

  // Indicateur visuel "désactivé par règle"
  if (ruleDisabled.disabled) {
    const banner = document.createElement("div");
    banner.className = "card-disabled-banner";
    banner.textContent = ruleDisabled.label;
    div.appendChild(banner);
  }

  if (!disabled) div.addEventListener("click", onClick);
  return div;
}

/**
 * Inspecte le state Colyseus pour savoir si une catégorie est désactivée
 * pour ce joueur :
 * - par événement global (DISABLE_CARD_TYPE événement)
 * - par Confinement (DISABLE_CARD_TYPE adversaire)
 * - par statut Exposé (uniquement pour Densité et Extension)
 */
function isCategoryRuleDisabled(category: string): { disabled: boolean; reason: string; label: string } {
  const room = state.room;
  if (!room) return { disabled: false, reason: "", label: "" };
  const evtCsv = ((room.state as any).disabledCategoriesByEvent ?? "") as string;
  if (evtCsv && evtCsv.split(",").includes(category)) {
    return { disabled: true, reason: "event", label: `Désactivé par l'événement (${categoryLabel(category)})` };
  }
  if (state.sessionId) {
    const me = room.state.players.get(state.sessionId) as any;
    const meCsv = (me?.disabledCategoriesCsv ?? "") as string;
    if (meCsv && meCsv.split(",").includes(category)) {
      return { disabled: true, reason: "confinement", label: `Vous êtes confiné — ${categoryLabel(category)} bloqué` };
    }
    // Exposé : neutralise les cartes Densité et Extension (uniquement)
    if ((me?.status === "expose") && (category === "density" || category === "extension")) {
      return {
        disabled: true,
        reason: "exposed",
        label: `Vous êtes Exposé — la presse vous a démasqué, vous ne pouvez plus poser de ${categoryLabel(category)}`,
      };
    }
  }
  return { disabled: false, reason: "", label: "" };
}

function categoryLabel(cat: string): string {
  const map: Record<string, string> = {
    extension: "Extension",
    density: "Densité",
    risk: "Risque",
    defense: "Défense",
    sabotage: "Sabotage",
    intel: "Renseignement",
    signature: "Signature",
  };
  return map[cat] ?? cat;
}

// ============================================================ //
// Intel reveals — toasts courts (l'info détaillée vit dans state.intel)
// ============================================================ //

interface IntelToast { id: number; title: string; body: string; expires: number; }
const intelToasts: IntelToast[] = [];
let intelToastIdSeq = 0;
const INTEL_TOAST_LIFETIME_MS = 4500;

function showIntelToast(title: string, body: string) {
  intelToasts.push({
    id: ++intelToastIdSeq,
    title,
    body,
    expires: Date.now() + INTEL_TOAST_LIFETIME_MS,
  });
  renderIntelToasts();
  // Programme le rafraîchissement à l'expiration la plus proche
  window.setTimeout(renderIntelToasts, INTEL_TOAST_LIFETIME_MS + 100);
}

function renderIntelToasts() {
  const section = $opt<HTMLElement>("private-reveals");
  const content = $opt<HTMLElement>("reveal-content");
  if (!section || !content) return;
  const now = Date.now();
  // Purge in place
  for (let i = intelToasts.length - 1; i >= 0; i--) {
    if (intelToasts[i]!.expires <= now) intelToasts.splice(i, 1);
  }
  if (intelToasts.length === 0) {
    section.hidden = true;
    content.innerHTML = "";
    return;
  }
  section.hidden = false;
  content.innerHTML = intelToasts
    .map(
      (t) => `<div class="reveal-block">
        <p class="reveal-block-title">📡 ${escapeHtml(t.title)}</p>
        <p class="reveal-block-content">${escapeHtml(t.body)}</p>
      </div>`
    )
    .join("");
}

function escapeHtml(s: string): string {
  return s.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}

// ============================================================ //
// Recent-action banner — défile les cartes jouées au rythme serveur
// ============================================================ //

const RECENT_ACTION_DURATION_MS = 1200;
let recentActionTimer: number | null = null;

function startRecentActionSequence(reveals: Array<{ playerId: string; card: { id: string; instanceId?: string; name: string; category: string } }>) {
  if (recentActionTimer != null) {
    window.clearTimeout(recentActionTimer);
    recentActionTimer = null;
  }
  if (!reveals || reveals.length === 0) {
    hideRecentAction();
    return;
  }
  const total = reveals.length;
  const showNext = (i: number) => {
    if (i >= total) {
      hideRecentAction();
      state.currentRecentActionInstanceId = null;
      return;
    }
    const r = reveals[i]!;
    showRecentAction(r, i + 1, total);
    // Mémorise l'instanceId courant pour qu'un éventuel countryBonus reçu
    // pendant l'affichage de cette carte puisse être rendu en sync.
    state.currentRecentActionInstanceId = r.card.instanceId ?? null;
    // Si des bonus pays ont déjà été reçus pour cette carte, les afficher MAINTENANT.
    if (r.card.instanceId) {
      const bonuses = state.pendingCountryBonuses.get(r.card.instanceId);
      if (bonuses && bonuses.length > 0) {
        showCountryBonusBanner(bonuses);
        state.pendingCountryBonuses.delete(r.card.instanceId);
      }
    }
    recentActionTimer = window.setTimeout(() => showNext(i + 1), RECENT_ACTION_DURATION_MS);
  };
  showNext(0);
}

function showRecentAction(
  reveal: { playerId: string; card: { id: string; instanceId?: string; name: string; category: string; flavor?: string } },
  pos: number,
  total: number,
  durationMs: number = RECENT_ACTION_DURATION_MS
) {
  const banner = $opt<HTMLElement>("play-recent-action");
  const dot = $opt<HTMLElement>("recent-action-dot");
  const text = $opt<HTMLElement>("recent-action-text");
  const posEl = $opt<HTMLElement>("recent-action-pos");
  const barFill = $opt<HTMLElement>("recent-action-bar-fill");
  if (!banner || !dot || !text || !posEl || !barFill) return;

  const room = state.room;
  const player = room?.state.players.get(reveal.playerId);
  const seat = player?.seatIndex ?? 0;
  const playerName = player?.name ?? "—";

  dot.style.background = seatColor(seat);
  const isMe = reveal.playerId === state.sessionId;
  const headLine = `<strong>${escapeHtml(playerName)}${isMe ? " (vous)" : ""}</strong> joue <span class="recent-action-cat cat-${reveal.card.category}">${escapeHtml(categoryLabel(reveal.card.category))}</span> <em>${escapeHtml(reveal.card.name)}</em>`;
  const flavorLine = reveal.card.flavor
    ? `<div class="recent-action-flavor">« ${escapeHtml(reveal.card.flavor)} »</div>`
    : "";
  text.innerHTML = headLine + flavorLine;
  posEl.textContent = `(${pos}/${total})`;
  banner.hidden = false;
  banner.classList.remove("recent-action-show");
  // Force reflow pour redéclencher l'animation
  void banner.offsetWidth;
  banner.classList.add("recent-action-show");

  barFill.style.transition = "none";
  barFill.style.width = "0%";
  void barFill.offsetWidth;
  barFill.style.transition = `width ${durationMs}ms linear`;
  barFill.style.width = "100%";
}

function hideRecentAction() {
  const banner = $opt<HTMLElement>("play-recent-action");
  if (!banner) return;
  banner.classList.remove("recent-action-show");
  // Petit fade avant de masquer
  window.setTimeout(() => {
    if (!banner.classList.contains("recent-action-show")) banner.hidden = true;
  }, 300);
  recentActionTimer = null;
}

// ============================================================ //
// Country bonus banner — affiché quand un bonus pays s'applique //
// pendant la résolution d'une carte (sync via instanceId).      //
// ============================================================ //
const COUNTRY_BONUS_BANNER_MS = 2500;
let countryBonusBannerTimer: number | null = null;

/**
 * Affiche un bandeau centré "🏛 [Pays] — [bonus appliqué]" pendant ~1.1s.
 * Plusieurs bonus pour la même carte sont concaténés dans un seul bandeau.
 */
function showCountryBonusBanner(
  bonuses: Array<{ playerId: string; countryName: string; label: string; kind: "category_buff" | "play_category_bonus" }>
) {
  if (!bonuses || bonuses.length === 0) return;
  let banner = document.getElementById("country-bonus-banner") as HTMLElement | null;
  if (!banner) {
    banner = document.createElement("div");
    banner.id = "country-bonus-banner";
    banner.className = "country-bonus-banner";
    document.body.appendChild(banner);
  }
  // Regroupe par pays (en général un seul) puis liste les labels
  const room = state.room;
  const lines = bonuses
    .map((b) => {
      const player = room?.state.players.get(b.playerId) as any;
      const playerName = player ? escapeHtml(String(player.name ?? "—")) : "—";
      const kindIcon = b.kind === "play_category_bonus" ? "➕" : "✨";
      return `
        <div class="cbb-row">
          <span class="cbb-flag">🏛</span>
          <div class="cbb-text">
            <div class="cbb-head">${escapeHtml(b.countryName)} <span class="cbb-player">— ${playerName}</span></div>
            <div class="cbb-label">${kindIcon} ${escapeHtml(b.label)}</div>
          </div>
        </div>
      `;
    })
    .join("");
  banner.innerHTML = `
    <div class="cbb-title">BONUS NATIONAL</div>
    ${lines}
  `;
  banner.hidden = false;
  banner.classList.remove("cbb-show");
  void banner.offsetWidth;
  banner.classList.add("cbb-show");
  if (countryBonusBannerTimer != null) window.clearTimeout(countryBonusBannerTimer);
  countryBonusBannerTimer = window.setTimeout(() => {
    if (!banner) return;
    banner.classList.remove("cbb-show");
    window.setTimeout(() => { if (banner && !banner.classList.contains("cbb-show")) banner.hidden = true; }, 320);
    countryBonusBannerTimer = null;
  }, COUNTRY_BONUS_BANNER_MS);
}

/**
 * Affiche la bannière de phase "Veille atmosphérique" — moment narratif quand
 * des cases en égalité dominante drainent les belligérants. Phrase comique +
 * récap des cases impactées. Disparait après ~3s.
 */
const ATTRITION_BANNER_DURATION_MS = 3500;
let attritionBannerTimer: number | null = null;
function showAttritionBanner(payload: { flavor: string; cases: Array<{ caseId: string; affectedPlayerIds: string[] }> }) {
  let banner = document.getElementById("attrition-banner") as HTMLElement | null;
  if (!banner) {
    banner = document.createElement("div");
    banner.id = "attrition-banner";
    banner.className = "attrition-banner";
    document.body.appendChild(banner);
  }
  const room = state.room;
  const casesHtml = payload.cases
    .map((c) => {
      const names = c.affectedPlayerIds
        .map((pid) => {
          const p = room?.state.players.get(pid) as any;
          return p ? escapeHtml(String(p.name ?? "—")) : "—";
        })
        .join(" · ");
      return `<span class="attrition-case">${escapeHtml(c.caseId)} → ${names}</span>`;
    })
    .join("");
  banner.innerHTML = `
    <div class="attrition-head">🌫 Veille atmosphérique</div>
    <div class="attrition-flavor">« ${escapeHtml(payload.flavor)} »</div>
    <div class="attrition-cases">${casesHtml}</div>
  `;
  banner.hidden = false;
  banner.classList.remove("attrition-show");
  void banner.offsetWidth;
  banner.classList.add("attrition-show");

  if (attritionBannerTimer != null) window.clearTimeout(attritionBannerTimer);
  attritionBannerTimer = window.setTimeout(() => {
    if (!banner) return;
    banner.classList.remove("attrition-show");
    window.setTimeout(() => { if (banner && !banner.classList.contains("attrition-show")) banner.hidden = true; }, 350);
    attritionBannerTimer = null;
  }, ATTRITION_BANNER_DURATION_MS);
}

/** Décrémente tous les renseignements actifs d'un tour. */
function tickIntel() {
  let changed = false;
  for (const [pid, entry] of [...state.intel.entries()]) {
    if (entry.turnsLeft <= 1) {
      state.intel.delete(pid);
      const room = state.room;
      const name = room?.state.players.get(pid)?.name ?? pid;
      appendLog(`[renseignement] L'intel sur ${name} expire.`);
      changed = true;
    } else {
      entry.turnsLeft -= 1;
      changed = true;
    }
  }
  for (const [pid, hand] of [...state.revealedHands.entries()]) {
    if (hand.turnsLeft <= 1) {
      state.revealedHands.delete(pid);
      const room = state.room;
      const name = room?.state.players.get(pid)?.name ?? pid;
      appendLog(`[renseignement] La main révélée de ${name} n'est plus à jour.`);
      changed = true;
    } else {
      hand.turnsLeft -= 1;
      changed = true;
    }
  }
  if (changed) {
    // Re-render avec le fog éventuellement re-appliqué
    if (state.room?.state.phase === "play") renderPlay();
  }
}

// ============================================================ //
// Actions                                                        //
// ============================================================ //

async function onCardClick(c: ClientCard) {
  // Carte désactivée par règle : proposer de défausser et passer le tour
  const ruleDisabled = isCategoryRuleDisabled(c.category);
  if (ruleDisabled.disabled) {
    const ok = await confirmDialog({
      eyebrow: "Avis de non-recevabilité",
      title: `« ${c.name} » est désactivée`,
      body: [ruleDisabled.label, "Souhaitez-vous la défausser et passer votre tour ?"],
      stamp: "BLOQUÉE",
      confirmLabel: "Défausser et passer",
      cancelLabel: "Revenir à ma main",
    });
    if (ok) passWithDiscard(c.instanceId);
    return;
  }

  // Mode grille : déduire le budget de densité à répartir depuis les effets de la carte
  const room = state.room;
  const gridRadius = room ? ((room.state as any).gridRadius ?? 0) : 0;
  if (gridRadius > 0) {
    const budget = computeCardCaseBudget(c);
    console.log("[sd] onCardClick mode grille", { cardId: c.id, cardName: c.name, category: c.category, budget, effects: c.effects });
    if (budget > 0) {
      // Aucune case éligible du tout : entrer en mode sélection piégerait le
      // joueur (rien à cliquer, Valider grisé). On l'avertit et on lui laisse
      // le choix de défausser + passer, comme pour une carte désactivée.
      const preview = computeEligibleCasesFor(c);
      if (preview.size === 0) {
        const ok = await confirmDialog({
          eyebrow: "Rapport de reconnaissance",
          title: `« ${c.name} » n'a aucune cible`,
          body: [
            "Aucune case valide sur le plateau actuellement : l'adversaire est hors de portée, ou aucune case n'est disponible pour cette manœuvre.",
            "La jouer maintenant la gaspillerait.",
          ],
          stamp: "SANS EFFET",
          confirmLabel: "Défausser et passer",
          cancelLabel: "Revenir à ma main",
        });
        if (ok) passWithDiscard(c.instanceId);
        return;
      }
      state.pendingHexSelection = { card: c, needed: budget, selected: [] };
      const eligibleSet = preview;
      console.log("[sd] pendingHexSelection activée", {
        needed: budget,
        eligibleCount: eligibleSet.size,
        eligibleSample: [...eligibleSet].slice(0, 10),
      });
      renderPlay();
      return;
    }
    // Sinon : carte sans cible spatiale (Défense, Intel ciblant joueur, etc.)
    if (CARDS_WITH_TARGET.has(c.id)) {
      state.pendingTargetCard = c;
      renderPlay();
    } else {
      playCard(c.instanceId);
    }
    return;
  }

  // === Legacy ===
  const hexNeeded = CARDS_HEX_SELECT[c.id];
  if (hexNeeded && hexNeeded > 0) {
    const myHexCount = countMyHexes();
    if (myHexCount < hexNeeded) {
      playCard(c.instanceId);
      return;
    }
    state.pendingHexSelection = { card: c, needed: hexNeeded, selected: [] };
    renderPlay();
    return;
  }
  if (CARDS_WITH_TARGET.has(c.id)) {
    state.pendingTargetCard = c;
    renderPlay();
  } else {
    playCard(c.instanceId);
  }
}

/**
 * Calcule combien de densité totale doit être répartie sur des cases pour une carte donnée
 * en mode grille. Renvoie 0 si la carte n'a pas d'effet spatial direct (Défense, Intel pure).
 */
/**
 * Calcule la projection appliquée par une carte sur chaque case sélectionnée :
 * - mode "add" : ajoute X de densité (ADD_HEX ou MODIFY_DENSITY)
 * - mode "lose" : retire X de densité (LOSE_HEX / MOVE_CLOUDS)
 * Renvoie { mode, perCase } où perCase est le delta par case ciblée.
 */
function computeCardProjection(c: ClientCard): { mode: "add" | "lose" | "none"; perCase: number } {
  const effects = (c.effects ?? []) as Array<Record<string, any>>;
  for (const eff of effects) {
    if (!isUserSelectableSpatialEffect(eff)) continue;
    const t = String(eff.type ?? "");
    if (t === "ADD_HEX") {
      return { mode: "add", perCase: Math.max(1, Number(eff.density ?? 1)) };
    }
    if (t === "MODIFY_DENSITY") {
      return { mode: "add", perCase: Math.max(1, Number(eff.delta ?? 1)) };
    }
    if (t === "LOSE_HEX") {
      return { mode: "lose", perCase: Math.max(1, Number(eff.count ?? 1)) };
    }
    if (t === "MOVE_CLOUDS") {
      return { mode: "lose", perCase: Math.max(1, Number(eff.density ?? 1)) };
    }
  }
  return { mode: "none", perCase: 0 };
}

/**
 * Renvoie true si un effet de carte nécessite que le joueur sélectionne des cases
 * sur la grille (vs résolu automatiquement par le serveur).
 *
 * Cas auto (false) :
 *   - LOSE_HEX target=last_exposed (la cible est le dernier joueur exposé)
 *   - LOSE_HEX target=self (le joueur perd lui-même, server choisit)
 *   - ADD_HEX target=anywhere via CHARGE_CARD payload (fire timing différent)
 */
function isUserSelectableSpatialEffect(eff: Record<string, any>): boolean {
  const t = String(eff.type ?? "");
  const target = String(eff.target ?? "");
  if (t === "ADD_HEX") {
    // adjacent_to_own est la cible courante user-choice. anywhere n'apparait
    // dans une carte directe nulle part (uniquement via payload CHARGE_CARD).
    return target === "adjacent_to_own" || target === "" || target === "anywhere";
  }
  if (t === "MODIFY_DENSITY") {
    return target === "own_hex" || target === "own_hex_adjacent" || target === "";
  }
  if (t === "LOSE_HEX") {
    // last_exposed et self sont auto (serveur choisit), le reste demande choix
    return target !== "last_exposed" && target !== "self";
  }
  if (t === "MOVE_CLOUDS") {
    return target === "opponent_hex" || target === "";
  }
  return false;
}

function computeCardCaseBudget(c: ClientCard): number {
  const effects = (c.effects ?? []) as Array<Record<string, any>>;
  // Bonus pays "étendue" du joueur courant : si le pays apporte +N spread sur
  // cette catégorie, le budget de cases sélectionnables augmente d'autant.
  // (Le bonus de densité, lui, ne change pas combien de cases on cible.)
  const { deltaSpread } = getMyCountryDeltasFor(c.category);
  let budget = 0;
  for (const eff of effects) {
    if (!isUserSelectableSpatialEffect(eff)) continue;
    const t = String(eff.type ?? "");
    if (t === "ADD_HEX" || t === "MODIFY_DENSITY") {
      const spread = Math.max(1, Number(eff.spread ?? 1) + deltaSpread);
      budget = Math.max(budget, spread);
    } else if (t === "LOSE_HEX") {
      budget = Math.max(budget, Math.max(1, Number(eff.count ?? 1)));
    } else if (t === "MOVE_CLOUDS") {
      // Vent Tactique : 2 sélections séquentielles (source, puis destination).
      // Le filtre des cases sélectionnables change après le 1er clic — cf.
      // computeEligibleCasesFor() qui regarde state.pendingHexSelection.selected.
      budget = Math.max(budget, 2);
    }
  }
  return budget;
}

/**
 * Lit le bonus pays du joueur courant pour une catégorie de carte donnée.
 * Retourne {deltaDensity, deltaSpread} ; 0/0 si pas de pays, pas de buff, ou
 * useCountries désactivé. Le serveur applique le même calcul dans
 * applyBuffsAndVassal / getCountryCategoryBuff.
 */
function getMyCountryDeltasFor(
  category: string
): { deltaDensity: number; deltaSpread: number } {
  const empty = { deltaDensity: 0, deltaSpread: 0 };
  const room = state.room;
  if (!room || !state.sessionId) return empty;
  if (!(room.state as any).useCountries) return empty;
  const me = room.state.players.get(state.sessionId);
  if (!me) return empty;
  const country = findCountry((me as any).countryId);
  const mod = country?.modifier as any;
  if (!mod || mod.category !== category) return empty;
  return {
    deltaDensity: Number(mod.deltaDensity ?? 0),
    deltaSpread: Number(mod.deltaSpread ?? 0),
  };
}

function countMyHexes(): number {
  const room = state.room;
  if (!room || !state.sessionId) return 0;
  let n = 0;
  room.state.hexes.forEach((h: any) => {
    if (h.ownerId === state.sessionId) n++;
  });
  return n;
}

function playCardWithHexes(instanceId: string, hexIds: string[]) {
  if (!state.room) return;
  // En mode grille, on convertit les ids en targetCases (1 densité par id sélectionné).
  // Le serveur détecte si gridRadius > 0 et utilise targetCases ; sinon il prend targetHexIds.
  const gridRadius = (state.room.state as any).gridRadius ?? 0;
  if (gridRadius > 0) {
    // Regroupe les doublons : si un caseId apparaît N fois, amount = N
    const counts = new Map<string, number>();
    for (const id of hexIds) counts.set(id, (counts.get(id) ?? 0) + 1);
    const targetCases = Array.from(counts.entries()).map(([caseId, amount]) => ({ caseId, amount }));
    state.room.send("playCard", { instanceId, targetCases });
    sounds.play("tampon");
  } else {
    state.room.send("playCard", { instanceId, targetHexIds: hexIds });
    sounds.play("tampon");
  }
}

function passWithDiscard(instanceId: string) {
  if (!state.room) return;
  state.room.send("pass", { discardInstanceId: instanceId });
  sounds.play("toux_genee");
}

function playCard(instanceId: string, targetPlayerId?: string) {
  if (!state.room) return;
  state.room.send("playCard", { instanceId, targetPlayerId });
  sounds.play("tampon");
}

function draftPick(instanceId: string) {
  if (!state.room) return;
  state.room.send("draftPick", { instanceId });
  sounds.play("papier");
}

// ============================================================ //
// Connexion                                                      //
// ============================================================ //

/** Verrou : une connexion à la fois (le serveur peut mettre 10-30 s à se
 *  réveiller — sans ce garde, l'utilisateur spamme le bouton et ouvre
 *  plusieurs salles en parallèle). */
let connectInFlight = false;

async function connect(forceCreate: boolean) {
  if (connectInFlight) return;
  const name = ($<HTMLInputElement>("player-name").value || "").trim();
  const rawCode = ($<HTMLInputElement>("room-code").value || "").trim();
  // Normalisation : on uppercase pour matcher la même salle peu importe la casse saisie
  const code = rawCode ? rawCode.toUpperCase() : "";
  const country = selectedCountryId;

  // Récupère les paramètres de partie (utiles uniquement à la création)
  const handSizeEl = $opt<HTMLInputElement>("setting-hand-size");
  const winModeEl = $opt<HTMLSelectElement>("setting-win-mode");
  const winThresholdEl = $opt<HTMLInputElement>("setting-win-threshold");
  const useCountriesEl = $opt<HTMLInputElement>("setting-use-countries");
  const gridRadiusEl = $opt<HTMLSelectElement>("setting-grid-radius");
  const createOptions: any = { name, country };
  if (code) createOptions.code = code;
  // handSize=0 = sentinelle "auto" (le serveur calcule 1 carte/joueur)
  if (handSizeEl) {
    const v = Number(handSizeEl.value);
    createOptions.handSize = Number.isFinite(v) ? v : 10;
  }
  if (winModeEl) createOptions.winMode = (winModeEl.value === "survie") ? "survie" : "couverture";
  if (winThresholdEl) createOptions.winThreshold = Number(winThresholdEl.value) || 20;
  if (useCountriesEl) createOptions.useCountries = Boolean(useCountriesEl.checked);
  if (gridRadiusEl) createOptions.gridRadius = Number(gridRadiusEl.value);
  console.log("[sd] createOptions →", createOptions);

  // Écran d'attente : le premier accès après une période creuse réveille la
  // machine Fly (auto_stop_machines), ce qui prend plusieurs secondes.
  connectInFlight = true;
  const connectTitle = forceCreate
    ? "Ouverture d'une nouvelle séance"
    : code
      ? `Vous rejoignez la séance ${code}`
      : "Recherche d'une séance disponible";
  showConnectingOverlay(connectTitle);
  $<HTMLButtonElement>("connect-btn").disabled = true;
  $<HTMLButtonElement>("create-btn").disabled = true;

  try {
    setStatus("connexion...", false);
    appendLog(`[client] Connexion en cours...`);

    let room: Room | null = null;

    // ============================================================ //
    // Tentative de reconnect via token persistant (sessionStorage). //
    // ============================================================ //
    // Si on a un token sauvegardé (HMR Vite, F5 accidentel, etc.) ET qu'on
    // n'est pas explicitement en "forceCreate", on essaie d'abord de
    // récupérer la session en cours via client.reconnect(token). Si ça
    // échoue (room morte, token expiré, partie déjà commencée et fenêtre
    // de reconnect expirée), on log et on retombe sur le flow normal.
    if (!forceCreate) {
      const saved = readSavedReconnect();
      if (saved) {
        // Reconnect avec retry+backoff : sur un refresh rapide, la 1ère
        // tentative échoue souvent parce que le serveur n'a pas encore
        // détecté la mort de l'ancienne WebSocket (le TCP timeout n'a pas
        // joué, donc onLeave/allowReconnection ne sont pas encore lancés
        // → le matchmaker n'a aucune "pending reconnection" à matcher et
        // route comme un nouveau join → "Partie déjà commencée" en plein
        // milieu de game). On attend que le serveur rattrape son retard.
        const delaysMs = [0, 2000, 3000, 5000]; // 4 tentatives, jusqu'à ~10s total
        let lastErr: any = null;
        appendLog(`[client] Tentative de reconnexion à ${saved.roomId}...`);
        for (let attempt = 0; attempt < delaysMs.length; attempt++) {
          if (delaysMs[attempt]! > 0) {
            await new Promise((r) => setTimeout(r, delaysMs[attempt]!));
          }
          try {
            room = await state.client.reconnect(saved.token);
            appendLog(
              attempt === 0
                ? `[client] Reconnecté à la partie en cours.`
                : `[client] Reconnecté à la partie en cours (essai ${attempt + 1}).`
            );
            break;
          } catch (err: any) {
            lastErr = err;
            // On ne log que les essais intermédiaires en cas de retry pour
            // éviter de polluer le procès-verbal avec des erreurs attendues
            // (race timing serveur).
            if (attempt < delaysMs.length - 1) {
              appendLog(`[client] Reconnexion en attente du serveur (essai ${attempt + 2}/${delaysMs.length})...`);
            }
          }
        }
        if (!room) {
          console.warn(`[sd] reconnect échec après ${delaysMs.length} tentatives, fallback joinOrCreate`, lastErr);
          appendLog(`[client] Reconnexion impossible — création d'une nouvelle séance.`);
          clearSavedReconnect();
        }
      }
    }

    if (!room) {
      if (forceCreate) {
        // "Créer une nouvelle séance" → toujours créer une nouvelle salle. Si un
        // code custom est fourni, il est appliqué à la nouvelle salle (sauf si
        // déjà pris : Colyseus matchmaker filterBy nous routerait vers l'autre).
        // Pour vraiment forcer la création, on utilise client.create() qui ignore
        // les salles existantes.
        room = await state.client.create("game", createOptions);
        appendLog(`[client] Partie créée : ${createOptions.handSize === 0 ? "main auto" : createOptions.handSize + " cartes"} / ${createOptions.winMode === "couverture" ? createOptions.winThreshold + " hex" : "survie"}${createOptions.useCountries ? " · pays activés" : ""}${code ? ` · code ${code}` : ""}`);
      } else if (code) {
        // "Rejoindre" avec un code : on utilise joinOrCreate avec filterBy(["code"]),
        // ce qui rejoint la salle dont metadata.code === code si elle existe,
        // ou en crée une nouvelle avec ce code sinon (= UX "code partagé à l'avance").
        room = await state.client.joinOrCreate("game", createOptions);
      } else {
        // Pas de code et pas forceCreate → comportement legacy : matchmake auto
        // (peut fusionner plusieurs joueurs sans intention dans une seule salle,
        // mais pratique pour tests rapides). À éviter en prod multi-joueur.
        room = await state.client.joinOrCreate("game", createOptions);
      }
    }

    // Sauvegarde le token de reconnect dès qu'on a une room valide. Permet
    // au prochain reload (HMR ou F5) de reprendre la partie.
    saveReconnect(room.roomId, (room as any).reconnectionToken);

    state.room = room;
    state.sessionId = room.sessionId;
    connectInFlight = false;
    hideConnectingOverlay();
    setStatus("connecté", true);
    appendLog(`[client] Connecté — room=${room.roomId}`);

    const codeEl = $<HTMLElement>("room-code-display");
    codeEl.textContent = `Code : ${room.roomId}`;
    codeEl.hidden = false;

    const banner = $<HTMLElement>("room-code-banner");
    $("room-code-banner-value").textContent = room.roomId;
    banner.hidden = false;

    $<HTMLButtonElement>("connect-btn").disabled = true;
    $<HTMLButtonElement>("create-btn").disabled = true;
    $<HTMLInputElement>("player-name").disabled = true;
    $<HTMLInputElement>("room-code").disabled = true;
    $("connect-controls").hidden = true;
    $("ready-controls").hidden = false;

    let lastObservedPhase: string | null = null;
    room.onStateChange(() => {
      const phase = room.state.phase as string;
      showViewport(phase);
      renderPlayersLobby();
      if (phase === "placement") renderPlacement();
      else if (phase === "draft") renderDraft();
      else if (phase === "play" || phase === "end_round") renderPlay();
      else if (phase === "finished") renderGameEnd();

      // Onboarding : déclenche la modale d'introduction quand la phase change.
      // Le système gère lui-même le respect du toggle global + opt-out par
      // localStorage, donc on peut appeler aveuglément.
      if (phase !== lastObservedPhase) {
        showPhaseModal(phase);
        lastShownPhaseModal = phase; // permet le re-show si on quitte/revient
        lastObservedPhase = phase;
      }
      refreshHintBanner();
    });

    room.onMessage("welcome", (msg: any) => {
      appendLog(`[serveur] ${msg.message}`);
      appendLog(`[serveur] Délégué accrédité : "${msg.playerName}" (siège n°${(msg.seatIndex ?? 0) + 1}).`);
      // Met à jour le code de salle affiché si un code custom a été défini par le créateur.
      const displayCode = String(msg.roomCode ?? room.roomId);
      const codeEl = $opt<HTMLElement>("room-code-display");
      if (codeEl) codeEl.textContent = `Code : ${displayCode}`;
      const bannerVal = $opt<HTMLElement>("room-code-banner-value");
      if (bannerVal) bannerVal.textContent = displayCode;
      // Affiche le bouton "Dissoudre" uniquement pour le créateur de la salle.
      const disbandBtn = $opt<HTMLButtonElement>("disband-btn");
      if (disbandBtn) disbandBtn.hidden = !Boolean(msg.isCreator);
    });

    room.onMessage("roomDisbanded", (msg: any) => {
      appendLog(`[serveur] ${msg.reason ?? "Séance dissoute."}`);
      window.alert(msg.reason ?? "Séance dissoute par le créateur.");
      // Séance dissoute = pas de reconnect possible, on nettoie le token.
      clearSavedReconnect();
      resetUI();
    });

    room.onMessage("log", (line: string) => appendLog(`[salle] ${line}`));

    room.onMessage("pong", (msg: any) => {
      appendLog(`[pong] reçu — server time=${msg.time}, echo="${msg.echo}"`);
    });

    room.onMessage("draftedHandUpdate", (msg: any) => {
      // Vue à jour des cartes draftées pendant la phase draft (committed
      // + pick en cours). Source de la shelf "Vos cartes draftées".
      // Distinct de handUpdate qui ne véhicule que la main "de jeu" (post-draft).
      state.draftedCards = (msg.cards ?? []) as ClientCard[];
      renderDraftShelf();
    });

    room.onMessage("handUpdate", (msg: any) => {
      state.hand = (msg.cards ?? []) as ClientCard[];
      if (state.pendingTargetCard) {
        const stillThere = state.hand.find((c) => c.instanceId === state.pendingTargetCard!.instanceId);
        if (!stillThere) state.pendingTargetCard = null;
      }
      if (state.pendingHexSelection) {
        const stillThere = state.hand.find((c) => c.instanceId === state.pendingHexSelection!.card.instanceId);
        if (!stillThere) state.pendingHexSelection = null;
      }
      // Re-render selon la phase :
      // - play → renderPlay() qui appelle renderHand() (carrousel main visible)
      // - draft → renderHand() pour le DOM caché de la barre fixe (la shelf
      //   est alimentée par son propre message draftedHandUpdate, séparé)
      const phase = state.room?.state.phase;
      if (phase === "draft") {
        renderHand();
        updateHandFixedBarVisibility();
      } else {
        renderPlay();
      }
    });

    room.onMessage("draftPool", (msg: any) => {
      const pickNumber = msg.pickNumber ?? 0;
      // Début d'une nouvelle draft : on reset la shelf des cartes draftées.
      // Le serveur vide bien draftedHands à chaque startDraftPhase mais n'envoie
      // un draftedHandUpdate qu'au moment d'un draftPick → sans pick effectué,
      // la shelf client gardait les cartes de la draft précédente.
      if (pickNumber === 0) {
        state.draftedCards = [];
        state.selectedDraftCardId = null;
        lastShelfSig = null;
        lastSelectionSig = null;
      }
      state.draftPool = (msg.cards ?? []) as ClientCard[];
      state.draftPickInfo = {
        pickNumber,
        totalPicks: msg.totalPicks ?? 0,
      };
      renderDraft();
    });

    room.onMessage("turnReveal", (msg: any) => {
      const reveals = msg.reveals ?? [];
      appendLog(`──── Reveal du tour ────`);
      for (const r of reveals) {
        const player = room.state.players.get(r.playerId);
        appendLog(`   ${player?.name ?? r.playerId} : ${r.card.name}`);
      }
      // Le rythme du reveal est désormais piloté par le serveur, carte par
      // carte, via les messages "cardResolved" (rafale pour les banales,
      // cérémonie pour les dramatiques). startRecentActionSequence (défilement
      // client à durée fixe) est conservé uniquement comme utilitaire legacy.
      void startRecentActionSequence;
      // Décrémente les renseignements actifs à chaque tour
      tickIntel();
    });

    room.onMessage("cardResolved", (payload: CardResolvedPayload) => {
      // Bandeau compact (toujours) — durée synchronisée avec le rythme serveur
      const displayMs = payload.dramatic ? 3300 : 850;
      showRecentAction(
        {
          playerId: payload.playerId,
          card: {
            id: payload.cardId,
            instanceId: payload.instanceId,
            name: payload.cardName,
            category: payload.category,
            flavor: payload.flavor,
          },
        },
        payload.idx + 1,
        payload.total,
        displayMs
      );
      // Sync des bonus pays (même mécanique que l'ancien défilement)
      state.currentRecentActionInstanceId = payload.instanceId ?? null;
      if (payload.instanceId) {
        const bonuses = state.pendingCountryBonuses.get(payload.instanceId);
        if (bonuses && bonuses.length > 0) {
          showCountryBonusBanner(bonuses);
          state.pendingCountryBonuses.delete(payload.instanceId);
        }
      }
      // Mise en scène (sons + cérémonie si dramatique) — colorée au joueur
      const caster = room.state.players.get(payload.playerId);
      stageCardResolution(payload, {
        playerColor: seatColor(caster?.seatIndex ?? 0),
        isMe: payload.playerId === state.sessionId,
      });
      // Dernière carte du tour : on replie le bandeau après affichage
      if (payload.idx >= payload.total - 1) {
        window.setTimeout(() => {
          hideRecentAction();
          state.currentRecentActionInstanceId = null;
        }, displayMs);
      }
    });

    room.onMessage("dispatch", (msg: { headline: string; tone: string }) => {
      showDispatch(msg.headline, msg.tone);
      appendLog(`📰 ${msg.headline}`);
    });

    room.onMessage("privateReveal", (msg: any) => {
      if (msg.type === "handReveal") {
        const cards = (msg.cards ?? []).map((c: any) => ({ name: String(c.name), category: String(c.category) }));
        const round = Number(room.state.round ?? 0);
        // Lit la durée envoyée par le serveur (base 3 + bonus pays Vapeurs).
        // Fallback à 3 si non transmis (rétro-compat).
        const turns = Number(msg.turns ?? 3);
        state.revealedHands.set(msg.victim.id, {
          cards,
          turnsLeft: turns,
          revealedAtRound: round,
          source: "intel",
        });
        const summary = cards.map((c: any) => c.name).join(", ");
        showIntelToast(`Main de ${msg.victim.name}`, `${cards.length} carte${cards.length > 1 ? "s" : ""} — visible ${turns} tour${turns > 1 ? "s" : ""}`);
        appendLog(`[renseignement] Main révélée — ${msg.victim.name} : ${summary}`);
        renderPlay();
      } else if (msg.type === "hexReveal") {
        const summary = (msg.hexes ?? []).map((h: any) => `D${h.density}`).join(", ");
        // Stocke le renseignement dans state.intel — le rendu de la carte fera le fog of war
        const turns = Number(msg.turns ?? 3);
        state.intel.set(msg.victim.id, {
          type: "territory",
          turnsLeft: turns,
          data: { hexes: msg.hexes },
        });
        showIntelToast(`Territoire de ${msg.victim.name}`, `${msg.hexes.length} hex (${turns} tours)`);
        appendLog(`[renseignement] Territoire révélé — ${msg.victim.name} : ${msg.hexes.length} hex — visible ${turns} tour${turns > 1 ? "s" : ""}`);
        renderPlay();
        void summary;
      } else if (msg.type === "densityReveal") {
        // Si on a déjà un intel "territory" actif sur ce joueur, on le garde (il est plus complet) ; sinon "density".
        const turns = Number(msg.turns ?? 3);
        const existing = state.intel.get(msg.victim.id);
        if (!existing || existing.type !== "territory") {
          state.intel.set(msg.victim.id, {
            type: "density",
            turnsLeft: turns,
            data: { maxDensity: msg.maxDensity },
          });
        }
        showIntelToast(`Imagerie ${msg.victim.name}`, `D max ${msg.maxDensity} (${turns} tours)`);
        appendLog(`[renseignement] D max — ${msg.victim.name} : ${msg.maxDensity} — visible ${turns} tour${turns > 1 ? "s" : ""}`);
        renderPlay();
      }
    });

    room.onMessage("publicReveal", (msg: any) => {
      if (msg.type === "allHandsRevealed") {
        appendLog(`📰 FUITE WIKILEAKS — toutes les mains révélées :`);
        const summary = (msg.hands ?? []) as Array<{ playerId: string; playerName: string; cards: Array<{ name: string; category: string }> }>;
        const round = Number(room.state.round ?? 0);
        for (const h of summary) {
          const list = h.cards.map((c) => c.name).join(", ");
          appendLog(`   ${h.playerName} : ${list || "(main vide)"}`);
          if (h.playerId && h.playerId !== state.sessionId && h.cards.length > 0) {
            state.revealedHands.set(h.playerId, {
              cards: h.cards.map((c) => ({ name: String(c.name), category: String(c.category) })),
              turnsLeft: 3,
              revealedAtRound: round,
              source: "leak",
            });
          }
        }
        renderPlay();
      }
    });

    room.onMessage("phaseChange", (msg: any) => {
      appendLog(`[phase] → ${msg.phase}`);
      abortCeremony();
      if (msg.phase === "play") sounds.play("clochette_seance");
      if (msg.phase === "draft") sounds.play("papier");
      // Close the round summary overlay if we re-enter draft or play
      if (msg.phase === "draft" || msg.phase === "play") {
        hideRoundSummary();
      }
    });

    room.onMessage("gameEnd", (msg: any) => {
      const winner = room.state.players.get(msg.winnerId);
      abortCeremony();
      sounds.play("fanfare_victoire");
      appendLog(`🏆 Partie terminée — ${winner?.name ?? msg.winnerId} l'emporte (${msg.reason}).`);
      // Partie terminée → plus de raison de garder le token de reconnect.
      clearSavedReconnect();
    });

    room.onMessage("roundSummary", (summary: any) => {
      showRoundSummary(summary);
    });

    room.onMessage("attritionPhase", (payload: { flavor: string; cases: Array<{ caseId: string; affectedPlayerIds: string[] }> }) => {
      showAttritionBanner(payload);
      spawnPhaseDamageNumbers(payload.cases, "attrition");
    });

    room.onMessage("isolationErosionPhase", (payload: { cases: Array<{ caseId: string; affectedPlayerIds: string[] }> }) => {
      spawnPhaseDamageNumbers(payload.cases, "erosion");
    });

    room.onMessage("countryBonus", (msg: {
      playerId: string;
      cardInstanceId: string;
      cardName: string;
      countryId: string;
      countryName: string;
      kind: "category_buff" | "play_category_bonus";
      label: string;
    }) => {
      appendLog(`[bonus pays] ${msg.countryName} — ${msg.label} (sur ${msg.cardName})`);
      const entry = { playerId: msg.playerId, countryName: msg.countryName, label: msg.label, kind: msg.kind };
      // Si l'animation recent-action affiche actuellement cette carte, on
      // déclenche immédiatement le bandeau ; sinon on stocke pour quand le
      // séquençage arrivera dessus.
      if (state.currentRecentActionInstanceId === msg.cardInstanceId) {
        showCountryBonusBanner([entry]);
      } else {
        const bucket = state.pendingCountryBonuses.get(msg.cardInstanceId) ?? [];
        bucket.push(entry);
        state.pendingCountryBonuses.set(msg.cardInstanceId, bucket);
      }
    });

    room.onMessage("error", (msg: any) => appendLog(`[erreur] ${msg.message}`));

    room.onLeave((code) => {
      setStatus("déconnecté", false);
      appendLog("[client] Connexion fermée.");
      // Colyseus close codes:
      //   1000 = normal close (leave volontaire) → on nettoie le token
      //   1001 = going away (reload / tab close) → on GARDE le token pour
      //          un éventuel reconnect au prochain chargement
      //   1006 = abnormal closure (réseau) → on garde le token aussi
      // Tout autre code "propre" (gameEnd notamment) nettoie également.
      if (code === 1000) {
        clearSavedReconnect();
      }
      resetUI();
    });
  } catch (err) {
    const message = err instanceof Error ? err.message : String(err);
    connectInFlight = false;
    setStatus("erreur", false);
    appendLog(`[erreur] ${message}`);
    $<HTMLButtonElement>("connect-btn").disabled = false;
    $<HTMLButtonElement>("create-btn").disabled = false;
    connectingFailed(
      `Le Secrétariat n'a pas répondu : ${message}`,
      () => void connect(forceCreate)
    );
  }
}

function resetUI() {
  $<HTMLButtonElement>("connect-btn").disabled = false;
  $<HTMLButtonElement>("create-btn").disabled = false;
  $<HTMLInputElement>("player-name").disabled = false;
  $<HTMLInputElement>("room-code").disabled = false;
  $("connect-controls").hidden = false;
  $("ready-controls").hidden = true;
  $("room-code-display").hidden = true;
  $("room-code-banner").hidden = true;
  state.room = null;
  state.sessionId = null;
  state.hand = [];
  state.draftPool = [];
  state.draftedCards = [];
  state.selectedDraftCardId = null;
  state.pendingTargetCard = null;
  state.pendingHexSelection = null;
  state.intel.clear();
  state.revealedHands.clear();
  state.pendingCountryBonuses.clear();
  state.currentRecentActionInstanceId = null;
  lastDraftPoolSig = null;
  lastShelfSig = null;
  lastSelectionSig = null;
  lastHandSig = null;
  lastOpponentsSig = null;
  lastPlacementSig = null;
  intelToasts.length = 0;
  prevCaseSnapshot = null;
  prevMyTerritorySize = null;
  gameToasts.length = 0;
  renderIntelToasts();
  hideRoundSummary();
  showViewport("lobby");
  renderPlayersLobby();
}

// ============================================================ //
// Init                                                           //
// ============================================================ //

function init() {
  initLogDrawer();
  initRulesDrawer();
  initDraftDrawers();
  initPlayEventDrawer();
  initHandFixedBar();
  initCarouselKeyboardNav();
  initDraftPoolCarouselWheel();
  initDraftSelectionPanel();
  initPlaySelectionPanel();
  initCountryChips();
  initGridClickDelegation();
  initMapZoomPan();
  initHelpToggle();
  initFocusMode();
  initPhaseModal();
  initHintBanner();
  initDisabledCardTooltip();

  $<HTMLButtonElement>("connect-btn").addEventListener("click", () => connect(false));
  $<HTMLButtonElement>("create-btn").addEventListener("click", () => connect(true));

  // Auto-reconnect au boot si un token de reconnexion existe en sessionStorage
  // (cas HMR Vite, F5, ré-ouverture d'onglet dans la même session). Le
  // connect() détectera le token et tentera reconnect() en premier.
  // Pré-remplit aussi le nom si disponible, pour que le fallback éventuel
  // sur joinOrCreate ne génère pas un "Délégué 042" anonyme.
  try {
    const savedName = sessionStorage.getItem(RECONNECT_NAME_KEY);
    const nameInput = document.getElementById("player-name") as HTMLInputElement | null;
    if (savedName && nameInput && !nameInput.value) nameInput.value = savedName;
    if (readSavedReconnect()) {
      // Petit délai pour laisser le DOM se stabiliser, puis on tente.
      window.setTimeout(() => connect(false), 50);
    }
  } catch {}

  const readyBtn = $opt<HTMLButtonElement>("ready-btn");
  if (readyBtn) {
    readyBtn.addEventListener("click", () => {
      if (!state.room) return;
      const me = state.room.state.players.get(state.sessionId ?? "") as any;
      // Toggle : si déjà prêt, on annule pour pouvoir changer de nation/quitter
      const wantReady = !me?.ready;
      state.room.send("ready", { ready: wantReady });
    });
  }
  const pingBtn = $opt<HTMLButtonElement>("ping-btn");
  if (pingBtn) {
    pingBtn.addEventListener("click", () => {
      if (!state.room) return;
      state.room.send("ping", { echo: "hello-from-client" });
    });
  }

  const addBotBtn = $opt<HTMLButtonElement>("add-bot-btn");
  if (addBotBtn) {
    addBotBtn.addEventListener("click", () => {
      if (!state.room) return;
      state.room.send("addBot", {});
    });
  }

  const disbandBtn = $opt<HTMLButtonElement>("disband-btn");
  if (disbandBtn) {
    disbandBtn.addEventListener("click", async () => {
      if (!state.room) return;
      const ok = await confirmDialog({
        eyebrow: "Décision du Président de séance",
        title: "Dissoudre la séance ?",
        body: [
          "Tous les délégués connectés seront déconnectés immédiatement et la partie en cours sera perdue.",
        ],
        stamp: "IRRÉVERSIBLE",
        tone: "danger",
        confirmLabel: "Dissoudre la séance",
        cancelLabel: "Poursuivre les débats",
      });
      if (ok) state.room?.send("disbandRoom", {});
    });
  }

  // Presets de paramètres
  const presets: Record<string, { handSize: number; winThreshold: number; winMode: string }> = {
    blitz: { handSize: 5, winThreshold: 10, winMode: "couverture" },
    standard: { handSize: 10, winThreshold: 20, winMode: "couverture" },
    long: { handSize: 12, winThreshold: 30, winMode: "couverture" },
  };

  document.querySelectorAll<HTMLButtonElement>(".preset-btn").forEach((btn) => {
    btn.addEventListener("click", () => {
      const presetName = btn.dataset.preset || "standard";
      const p = presets[presetName];
      if (!p) return;
      const handEl = $opt<HTMLInputElement>("setting-hand-size");
      const modeEl = $opt<HTMLInputElement>("setting-win-mode");
      const thresholdEl = $opt<HTMLInputElement>("setting-win-threshold");
      if (handEl) handEl.value = String(p.handSize);
      if (modeEl) modeEl.value = p.winMode;
      if (thresholdEl) thresholdEl.value = String(p.winThreshold);

      document.querySelectorAll<HTMLButtonElement>(".preset-btn").forEach((b) => b.classList.remove("active"));
      btn.classList.add("active");
      toggleThresholdVisibility();
    });
  });

  // Toggle visibilité du seuil selon le mode
  const winModeEl = $opt<HTMLSelectElement>("setting-win-mode");
  function toggleThresholdVisibility() {
    const mode = winModeEl?.value || "couverture";
    const group = $opt<HTMLElement>("setting-threshold-group");
    if (group) group.style.display = mode === "couverture" ? "" : "none";
  }
  if (winModeEl) {
    winModeEl.addEventListener("change", toggleThresholdVisibility);
    toggleThresholdVisibility();
  }

  const replayBtn = $opt<HTMLButtonElement>("replay-btn");
  if (replayBtn) replayBtn.addEventListener("click", () => location.reload());
  const quitBtn = $opt<HTMLButtonElement>("quit-btn");
  if (quitBtn) quitBtn.addEventListener("click", () => location.reload());

  appendLog("[client] Prêt. Saisissez un nom et rejoignez la séance.");
  renderPlayersLobby();
}

init();
